/**
 * spec-sheet-render.js
 *
 * Generates printable spec sheets for The Ramp Basin Factory custom builders.
 * Renders top-down + side cross-section + front elevation + bottom view to SVG
 * for each design, matching the existing CAD-quality spec-sheet style.
 *
 * Public API:
 *   window.SpecSheet.render(design, container)  — mount the spec sheet into container
 *   window.SpecSheet.buildUrl(design, baseUrl)  — encode design as URL params
 *   window.SpecSheet.parseUrl(url)              — decode URL params into design
 *
 * Phase 1 — see SPEC_SHEET_PLAN.md for the full plan and decisions.
 */
(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────
  // Page geometry — A4 landscape in mm
  // ─────────────────────────────────────────────────────────────

  var PAGE_W = 297;
  var PAGE_H = 210;
  var PAGE_MARGIN = 8;
  var HEADER_H = 22;
  var FOOTER_H = 16;
  var INFO_PANEL_W = 65;
  var INFO_PANEL_GAP = 4;

  // Drawing area carved out of the page (the four technical views go here)
  var DRAW_AREA = {
    x: PAGE_MARGIN,
    y: HEADER_H + 2,
    w: PAGE_W - PAGE_MARGIN * 2 - INFO_PANEL_W - INFO_PANEL_GAP,
    h: PAGE_H - HEADER_H - FOOTER_H - 4
  };

  // ─────────────────────────────────────────────────────────────
  // Per-family manufacturing constants
  // Reference: SPEC_SHEET_PLAN.md → Model Family Registry
  // Bracket spacing is deferred — getBracketPositions returns null for v1.
  // ─────────────────────────────────────────────────────────────

  var FAMILY_CONSTANTS = {
    classic: {
      backInset: 110,
      defaultWidth: 460,
      kgPerMetre: 27,
      sectionProfile: 'ramp-classic'
    },
    'classic-double': {
      backInset: 110,
      defaultWidth: 460,
      kgPerMetre: 27,
      sectionProfile: 'ramp-classic'
    },
    trough: {
      backInset: 110,
      defaultWidth: 460,
      kgPerMetre: 34,
      sectionProfile: 'trough',
      lockedG: 40
    },
    dryer: {
      backInset: 145,
      defaultWidth: 530,
      kgPerMetre: 33,
      sectionProfile: 'ramp-dryer'
    },
    moulded: {
      backInset: 110,
      defaultWidth: 480,
      kgPerMetre: 35,
      sectionProfile: 'moulded'
    },
    benchtop: {
      backInset: null,
      defaultWidth: null,
      kgPerMetre: 38, // rough; to be tuned with real benchtop weight data
      sectionProfile: 'slab'
    }
  };

  // Model ID → family + render path + sensible default width.
  //
  // basinDepth — Vertical extent of the BASIN SECTION inside the bench, in mm.
  //   Used by the FRONT ELEVATION to draw the hidden-line basin rectangle.
  //   When null, defaults to design.height (basin section fills the full bench).
  //   Example: Commercial 400 has a 400mm-tall body with the basin section
  //   only occupying the top 150mm → basinDepth = 150.
  //
  // rampDrop — Maximum depth the angled ramp surface reaches at the BACK end
  //   in the SIDE CROSS-SECTION view, in mm. The ramp starts at the bench top
  //   surface at the front and drops by this amount by the time it reaches
  //   the 110mm back inset. When null, falls back to basinDepth (or height).
  //   Example: Classic 100 = 60mm drop, Commercial 200/400 = 80mm drop.
  var MODEL_REGISTRY = {
    classicC100:   { family: 'classic',        render: 'rampClassic',   widthDefault: 460, basinDepth: null, rampDrop: 60 },
    junior100:     { family: 'classic',        render: 'rampClassic',   widthDefault: 380, basinDepth: null, rampDrop: 60 },
    doubleC100:    { family: 'classic-double', render: 'rampDouble',    widthDefault: 460, basinDepth: null, rampDrop: 60 },
    // Merged Commercial (200 + 400 share one cross-section cavity → basinDepth null).
    commercial:    { family: 'classic',        render: 'rampClassic',   widthDefault: 460, basinDepth: null, rampDrop: 80 },
    // Legacy split codes kept so old saved links still render.
    commercial200: { family: 'classic',        render: 'rampClassic',   widthDefault: 460, basinDepth: null, rampDrop: 80 },
    commercial400: { family: 'classic',        render: 'rampClassic',   widthDefault: 460, basinDepth: null, rampDrop: 80 },
    trough180:     { family: 'trough',         render: 'rampTrough',    widthDefault: 460, basinDepth: null, rampDrop: null, rampFrontDepth: 80, rampBackDepth: 120 },
    trough150:     { family: 'trough',         render: 'rampTrough',    widthDefault: 460, basinDepth: null, rampDrop: null, rampFrontDepth: null, rampBackDepth: null }, // flat floor — no ramp (next pass)
    dAirblade400:  { family: 'dryer',          render: 'rampDAirblade', widthDefault: 530, basinDepth: null, rampDrop: null },
    xlerator400:   { family: 'dryer',          render: 'rampDAirblade', widthDefault: 500, basinDepth: null, rampDrop: null },
    // Moulded model IDs (from moulded-basin-builder)
    single:    { family: 'moulded', render: 'mouldedFormline', widthDefault: 480, basinDepth: null, rampDrop: null },
    double:    { family: 'moulded', render: 'mouldedFormline', widthDefault: 480, basinDepth: null, rampDrop: null },
    triple:    { family: 'moulded', render: 'mouldedFormline', widthDefault: 480, basinDepth: null, rampDrop: null },
    quadruple: { family: 'moulded', render: 'mouldedFormline', widthDefault: 480, basinDepth: null, rampDrop: null },
    quintuple: { family: 'moulded', render: 'mouldedFormline', widthDefault: 480, basinDepth: null, rampDrop: null },
    sextuple:  { family: 'moulded', render: 'mouldedFormline', widthDefault: 480, basinDepth: null, rampDrop: null }
  };

  // Ramp models that carry the adjustable waste placement (gap between wastes +
  // centre fine-tune + per-waste overrides). Mirrors WASTE_PLACEMENT_MODELS in
  // `sections/cube-visual-section.liquid` — keep the two in step. Extended from
  // the dryer family to the rest of the ramp range 2026-07-30; `trough150`
  // ("Trough") is deliberately excluded and keeps its centred-rosette layout.
  // Legacy codes are listed too in case an old link reaches the info panel
  // before normalisation.
  var WASTE_PLACEMENT_MODELS = [
    'dAirblade400', 'xlerator400',
    'classicC100', 'junior100', 'commercial', 'commercial200', 'commercial400',
    'doubleC100', 'trough180'
  ];

  function hasWastePlacement(design) {
    return !!design && WASTE_PLACEMENT_MODELS.indexOf(design.model) !== -1;
  }

  // Per-mould spec for the moulded render path. Keyed by the `mould` URL param.
  // Kreno (Formline) is the legacy default for designs without a `mould`.
  //   bowlDepth   — cross-section depth to the top of the waste (Ø top)
  //   wasteDepth  — extra depth from Ø-top down to Ø-bottom (Kreno 45 = 170−125)
  //   gMode       — 'select' (Kreno: G is 20/40) | 'derived' (Smoothe: G = wid−E−mouldWidth)
  //   overflow    — true → draw the bowl + 40×6 overflow slot (front view) and
  //                 the overflow channel (cross-section). Smoothe is implicitly
  //                 overflow via brand; Kreno now carries it explicitly.
  //   cornerRadius— plan-view basin cutout corner radius in mm (Kreno R55).
  //                 Omitted → drawBasinCutout uses its proportional fallback.
  //   wallThickness — cross-section shell thickness in mm (Kreno 12). Omitted →
  //                 falls back to MATERIAL_THICKNESS (20).
  // Kreno (updated 2026-06-18): 530×310 mould, R55 corners, single Ø45 waste,
  // 125mm bowl depth, 170mm total height, 12mm wall, 40×6 overflow.
  // Legacy Smoothe keys → current. The range was renamed ca* → sm* on 2026-08-18
  // to match the SM product codes used on the site and in Cin7; spec-sheet links
  // minted before then still carry `mould=ca15`. Unknown keys fall back to Kreno
  // and draw 530 × 310 without complaint, so a link that isn't normalised produces
  // a plausible-looking but WRONG drawing — hence the map. Mirrors
  // LEGACY_MOULD_KEYS in `templates/page.quote.liquid`; keep the two identical.
  var LEGACY_MOULD_KEYS = {
    ca08: 'sm08', ca12: 'sm12', ca13: 'sm13', ca15: 'sm15',
    ca16: 'sm16', ca17: 'sm17', ca18: 'sm18', ca19: 'sm19'
  };
  function normaliseMouldKey(k) {
    return (k && LEGACY_MOULD_KEYS[k]) || k;
  }

  var MOULD_SPECS = {
    kreno: { brand: 'kreno',   label: 'RBC', gMode: 'select',  mouldLength: 530, mouldWidth: 310, bowlDepth: 125, wasteDepth: 45, wasteTopDia: 45, wasteBotDia: 45, overflow: true, cornerRadius: 55, wallThickness: 12, shallowBowl: true },
    // CA12's front view draws a curved DISH bowl (`bowlProfile: 'dish'`) to match its
    // A-A mould section — client drawing, 2026-08-05. Its side cross-section already
    // drew curved (the corner-control 'deeper Smoothe bowl' path), so only the front
    // view changed. CA15/CA19 still draw straight-down front walls.
    sm12:  { brand: 'smoothe', label: 'SM12',  gMode: 'derived', mouldLength: 500, mouldWidth: 280, bowlDepth: 110, wasteDepth: 40, wasteTopDia: 70, wasteBotDia: 45, bowlProfile: 'dish' },
    // CA15 shares CA16's plan-view corner radius — **R70** (client, 2026-08-05).
    // Without an explicit cornerRadius the top view fell back to the proportional
    // min(w,h)x0.35 = R105 AND printed no R label, so the corner curve was both
    // wrong and undocumented on the sheet. Waste stays CENTRED (no
    // wasteFromBasinEdge) — confirmed, unlike CA08/CA13.
    sm15:  { brand: 'smoothe', label: 'SM15',  gMode: 'derived', mouldLength: 480, mouldWidth: 300, bowlDepth: 95,  wasteDepth: 45, wasteTopDia: 75, wasteBotDia: 45, shape: 'rounded', cornerRadius: 70, bowlProfile: 'dish', rimRoll: 15, dish: { rimAngle: 45, rimRun: 0.55, easeX: 0.24, side: 0.9, rollSide: 0, angleSide: 65 } },
    // CA15 + CA16 share ONE bowl shape (client sections, 2026-08-05): a continuous
    // curved DISH — shallow across the length, deeper across the width — with a soft
    // ROLL at the rim, and the waste CENTRED (no wasteFromBasinEdge). Both therefore
    // take `bowlProfile: 'dish'` + `rimRoll: 15`, which draws the roll in both views.
    // CA16's old `shallowBowl: true` + `rimRadius: 15` are GONE: shallowBowl draws the
    // near-straight V, which is not what these sections show, and rimRadius was only
    // ever read alongside shallowBowl (rimRoll replaces it and works in both views).
    // Both carry a per-mould `dish` override, and the TWO VIEWS DELIBERATELY DIFFER —
    // Q signed the front view off and then asked for a different side section:
    //   FRONT: roll-off lip (rimRoll 15) + wall leaving the rim at `rimAngle: 45`.
    //          Approved 2026-08-06 — do not disturb.
    //   SIDE:  NO roll (`rollSide: 0`), a deeper wall (`angleSide: 65`) and the
    //          wall running deeper before it turns (`side: 0.9`).
    // Measured on the emitted paths: 45.0 front / 65.0 side. This is PER-MOULD on
    // purpose — CA12 (81) and CA13 (79) are signed off at the shared defaults, so never
    // retune DISH_* for one mould, and prefer rimAngle/angleSide over rimX/rimY.
    // Depths are Q-confirmed: 95 bowl + 45 waste = 140 basin top to waste bottom, Ø75
    // waste top on both (so the drawing's Ø90 is NOT the waste top dia — left alone).
    sm16:  { brand: 'smoothe', label: 'SM16',  gMode: 'derived', mouldLength: 480, mouldWidth: 315, bowlDepth: 95, wasteDepth: 45, wasteTopDia: 75, wasteBotDia: 45, cornerRadius: 70, shape: 'rounded', bowlProfile: 'dish', rimRoll: 15, dish: { rimAngle: 45, rimRun: 0.55, easeX: 0.24, side: 0.9, rollSide: 0, angleSide: 65 }, overflowSlot: { width: 55, height: 6, fromTop: 25 } },
    sm17:  { brand: 'smoothe', label: 'SM17',  gMode: 'derived', mouldLength: 630, mouldWidth: 305, bowlDepth: 135, wasteDepth: 40, wasteTopDia: 70, wasteBotDia: 45 },
    sm19:  { brand: 'smoothe', label: 'SM19',  gMode: 'derived', mouldLength: 450, mouldWidth: 300, bowlDepth: 135, wasteDepth: 40, wasteTopDia: 70, wasteBotDia: 45 },

    // Smoothe additions (2026-07-14)
    // CA08 (client dims, 2026-07-29): waste is NOT on the bowl centre — 160 from
    // the basin's back edge (centre would be 172.5); see getWasteFromBasinEdge().
    // Bowl is a near-SQUARE box (`bowlProfile: 'square'` — straight walls with a
    // 20mm radius on the floor corners only), not the deep curved bowl the rest
    // of the Smoothe range uses. It DOES keep the overflow (confirmed 2026-07-29).
    // `flatSlabMax: 15` — SM08's thin slab is 15mm (client, 2026-08-18), so 15 must
    // still draw a plain rear face. Mirrors `flatSlabMax` in the builder registry.
    sm08:  { brand: 'smoothe', label: 'SM08',  gMode: 'derived', mouldLength: 495, mouldWidth: 345, bowlDepth: 125, wasteDepth: 40, wasteTopDia: 75, wasteBotDia: 45, shape: 'rounded', cornerRadius: 60, wasteFromBasinEdge: 160, bowlProfile: 'square', bowlFloorRadius: 20, flatSlabMax: 15 },
    // CA13 (client drawings, 2026-08-05 — two sections):
    //   • ROLL-OFF LIP at the rim — the top face rolls off through a radius before
    //     the wall drops, so both sections show a convex turn at the rim rather
    //     than a hard edge. `rimRoll` (mm) is that radius.
    //   • Curved DISH bowl on the front view (`bowlProfile: 'dish'`), like CA12.
    //   • Depths straight off the drawing: 105 bench top → bowl floor, 140 to the
    //     bottom of the waste, so wasteDepth is 35 (NOT the shared 40). Total 140
    //     is unchanged from the figure the range was signed off with.
    //   • Waste is OFF-CENTRE: Q confirmed 230 from the BENCH back edge, and CA13's
    //     back edge (E) is 90, so it is 140 from the BASIN's back edge — which is
    //     what wasteFromBasinEdge stores (bowl centre would be 165). Verified
    //     against the drawing: predicts the boss at 38.9% across the opening,
    //     measured 39%. The side section derives its asymmetric walls from this.
    //   • NO overflow (`overflow: false`) — confirmed by Q; neither drawing shows a
    //     slot, and CA12's drawing did show its 40×6, so the omission is deliberate.
    sm13:  { brand: 'smoothe', label: 'SM13',  gMode: 'derived', mouldLength: 500, mouldWidth: 330, bowlDepth: 105, wasteDepth: 35, wasteTopDia: 75, wasteBotDia: 45, shape: 'rounded', cornerRadius: 60, bowlProfile: 'dish', rimRoll: 28, wasteFromBasinEdge: 140, overflow: false },
    sm18:  { brand: 'smoothe', label: 'SM18',  gMode: 'derived', mouldLength: 440, mouldWidth: 275, bowlDepth: 103, wasteDepth: 40, wasteTopDia: 63, wasteBotDia: 45, shape: 'stadium', overflow: true },

    // Grandios (ceramic inset) — Sintered/Natural bench.
    // mouldLength/mouldWidth are the OUTER rim footprint (= the bench cutout);
    // bowlLength/bowlWidthInner are the bowl opening. Client drawings 2026-08.
    // `bowlDepth` runs from the TOP FACE down to the cavity floor (the drawings'
    // 20mm top rim is INSIDE that figure, not added to it) and `wasteDepth` is
    // whatever is left to 盆高 — so bowlDepth + wasteDepth = the basin height, and
    // `bowlWasteDepth` repeats that total to keep the front view's waste taper in
    // step with the side section. Waste centred on the bowl (no wasteFromBasinEdge).
    // wallThickness 12 + lipRadius 45 are the signed-off SN023 shell values.
    sn023: { brand: 'grandios', label: 'SN023', gMode: 'derived', mouldLength: 590, mouldWidth: 390, bowlLength: 535, bowlWidthInner: 338, bowlDepth: 160, bowlWasteDepth: 200, wasteDepth: 40, wasteTopDia: 90, wasteBotDia: 45, lipRadius: 45, wallThickness: 12, shape: 'rounded', cornerRadius: 60 },
    // SN025 — 盆高215, bowl depth 160 to the inner floor, so 55 below it.
    sn025: { brand: 'grandios', label: 'SN025', gMode: 'derived', mouldLength: 655, mouldWidth: 415, bowlLength: 595, bowlWidthInner: 355, bowlDepth: 160, bowlWasteDepth: 215, wasteDepth: 55, wasteTopDia: 90, wasteBotDia: 45, lipRadius: 45, wallThickness: 12, shape: 'rounded', cornerRadius: 60 },
    sn026: { brand: 'grandios', label: 'SN026', gMode: 'derived', mouldLength: 605, mouldWidth: 435, bowlDepth: 170, wasteDepth: 40, wasteTopDia: 90, wasteBotDia: 45, shape: 'ellipse' },
    sn027: { brand: 'grandios', label: 'SN027', gMode: 'derived', mouldLength: 703, mouldWidth: 425, bowlDepth: 160, wasteDepth: 40, wasteTopDia: 90, wasteBotDia: 45, shape: 'stadium' },
    sn028: { brand: 'grandios', label: 'SN028', gMode: 'derived', mouldLength: 700, mouldWidth: 425, bowlDepth: 165, wasteDepth: 40, wasteTopDia: 90, wasteBotDia: 45, shape: 'rounded', cornerRadius: 60 },

    // Ferra (Alumiglaze) — TP957 integrated/inset; TP949–953 counter-top.
    // mounts + tapDeck drive the counter-top render (D3); mountHeight = puck height.
    // TP957 = the SAME bowl as TP953 (ellipse 642×422 outer, 598×378 inner, 130
    // bowl / 140 bowl+waste) but INSET into a bench — so no apron, no feet, no deck,
    // and no overflow.
    tp957: { brand: 'ferra', label: 'TP957', tapFromBackEdge: 52, gMode: 'derived', mouldLength: 642, mouldWidth: 422, bowlLength: 598, bowlWidthInner: 378, bowlDepth: 130, bowlWasteDepth: 140, mouldHeight: 140, wasteDepth: 10, wasteTopDia: 68, wasteMidDia: 51, wasteBotDia: 45, lipRadius: 30, shape: 'ellipse', mounts: ['inset'], drainRim: 0.6 },
    tp949: { brand: 'ferra', label: 'TP949', gMode: 'derived', mouldLength: 450, mouldWidth: 450, bowlInner: 434, bowlDepth: 145, mouldHeight: 157, wasteDepth: 12, wasteTopDia: 68, wasteMidDia: 51, wasteBotDia: 45, rimRadius: 61, shape: 'round',   mounts: ['top', 'inset'] },
    tp950: { brand: 'ferra', label: 'TP950', gMode: 'derived', mouldLength: 609, mouldWidth: 389, bowlLength: 598, bowlWidth: 378, bowlDepth: 132, bowlWasteDepth: 143, apron: true, apronHeight: 150, mouldHeight: 150, wasteTopDia: 68, wasteMidDia: 51, wasteBotDia: 45, shape: 'rounded', cornerRadius: 100, mounts: ['top'], feet: { fv: [20, 20], sv: [20, 20] }, drainRim: 2.5 },
    tp951: { brand: 'ferra', label: 'TP951', gMode: 'derived', mouldLength: 641, mouldWidth: 421, bowlLength: 598, bowlWidth: 378, bowlDepth: 129, bowlWasteDepth: 140, apron: true, apronHeight: 150, lipRadius: 30, mouldHeight: 150, wasteTopDia: 68, wasteMidDia: 51, wasteBotDia: 45, shape: 'stadium', mounts: ['top'], feet: { fv: [20, 20], sv: [30, 30] }, drainRim: 2.5 },
    tp952: { brand: 'ferra', label: 'TP952', gMode: 'derived', mouldLength: 610, mouldWidth: 480, bowlLength: 598, bowlWidth: 378, bowlDepth: 132, bowlWasteDepth: 143, apron: true, apronHeight: 150, mouldHeight: 150, wasteTopDia: 68, wasteMidDia: 51, wasteBotDia: 45, shape: 'rounded', cornerRadius: 100, mounts: ['top'], tapDeck: 95, frontLip: 2, feet: { fv: [20, 20], sv: [20, 100] }, drainRim: 0.6 },
    tp953: { brand: 'ferra', label: 'TP953', gMode: 'derived', mouldLength: 642, mouldWidth: 508, bowlLength: 598, bowlOuterLength: 642, bowlWidth: 422, bowlWidthInner: 378, bowlShape: 'ellipse', bowlDepth: 130, bowlWasteDepth: 140, apron: true, apronHeight: 150, lipRadius: 30, mouldHeight: 150, wasteTopDia: 68, wasteMidDia: 51, wasteBotDia: 45, shape: 'stadium', mounts: ['top'], tapDeck: 86, feet: { fv: [20, 20], sv: [20, 100] }, drainRim: 0.6 }
  };

  // Smoothe overflow (front view) + tap-side wall (cross-section) — same across
  // the whole Smoothe range. Overflow: a 40mm wide × 6mm high slot, centred in
  // the basin, 25mm down from the bench top. The tap-side (back) bowl wall is
  // 28mm thick to house the overflow channel.
  var SMOOTHE_OVERFLOW = { width: 40, height: 6, fromTop: 25 };

  // Dish-bowl front-view profile (see isDishBowl). Tuned 2026-08-05 against CA12's
  // A-A section drawing; these are the shape's only dials, so adjust here rather
  // than editing the path. Fractions, not mm — they scale with any mould.
  //   DISH_RIM_X  how far in from the rim the steep drop sits (of the opening width)
  //   DISH_RIM_Y  how far down that first control pulls        (of the bowl depth)
  //   DISH_EASE_X where the wall has fully flattened out       (of the opening width)
  // Smaller RIM_X + larger RIM_Y = crisper rim radius / steeper wall.
  var DISH_RIM_X  = 0.030;
  var DISH_RIM_Y  = 0.90;
  var DISH_EASE_X = 0.17;
  // A mould can flatten (or sharpen) its own bowl with `dish: { rimX, rimY, easeX,
  // side }` without disturbing the moulds that share the defaults — CA12 and CA13
  // are client-approved at the constants above, so per-mould tuning MUST go here
  // rather than into DISH_*. Bigger rimX + smaller rimY + bigger easeX = shallower,
  // flatter wall. `side` (0..1, default 1) is the SIDE section's equivalent: it is
  // the fraction of the bowl depth the wall reaches before it starts turning toward
  // the waste, so a smaller figure reads shallower there too.
  function dishDial(mould, key, dflt) {
    var d = mould && mould.dish;
    var v = d && d[key];
    return (typeof v === 'number' && !isNaN(v)) ? v : dflt;
  }
  // PREFERRED dial: `dish: { rimAngle: <degrees>, rimRun: <0..1> }` states the angle
  // the bowl wall LEAVES THE RIM at, measured from the bench top face — 90 = straight
  // down, 45 = a diagonal. This is how the shape actually gets described in review
  // ("it should start closer to 45 degrees"), and unlike rimX/rimY it means the same
  // thing in both views and on any mould, because it is an angle rather than a pair of
  // fractions of that particular mould's width and depth.
  //   rimRun (default 0.55) = how far along that angle the control sits, i.e. how long
  //   the wall holds the angle before easing into the floor. It is a fraction of the
  //   largest step that keeps the control inside the bowl box, so it can't overshoot.
  // Both views are isotropically scaled (one scale factor for both axes), so the drawn
  // angle IS the real angle. Returns null when the mould doesn't set rimAngle, and
  // every caller then falls back to the original rimX/rimY maths — which is what keeps
  // CA12/CA13 byte-identical. See [[bowl-profiles-front-view]] in project memory.
  function rimAngleRad(mould) {
    var a = dishDial(mould, 'rimAngle', null);
    if (typeof a !== 'number' || isNaN(a) || a <= 0 || a >= 90) return null;
    return a * Math.PI / 180;
  }
  // Control-point offset for a wall leaving the rim at `rad`, given how much room
  // there is along the face (`availAlong`) and down the wall (`availDown`).
  // Returns { along: , down: } in px.
  // Second half of a cubic split at parameter t (de Casteljau) — used to start the
  // dashed overflow water path AT the overflow hole rather than at the rim, while
  // keeping it exactly on the channel centreline.
  function cubicSplitAfter(p, t) {
    function L(a, b) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]; }
    var q0 = L(p[0], p[1]), q1 = L(p[1], p[2]), q2 = L(p[2], p[3]);
    var r0 = L(q0, q1), r1 = L(q1, q2);
    return [L(r0, r1), r1, q2, p[3]];
  }
  // Parameter at which a cubic reaches x = target. x is monotonic along these bowl
  // walls (it is depth), so bisection is exact enough and can't misbehave.
  function cubicTAtX(p, target) {
    function xAt(t) {
      var u = 1 - t;
      return u*u*u*p[0][0] + 3*u*u*t*p[1][0] + 3*u*t*t*p[2][0] + t*t*t*p[3][0];
    }
    var lo = 0, hi = 1;
    if (target <= xAt(0)) return 0;
    if (target >= xAt(1)) return 1;
    for (var i = 0; i < 40; i++) {
      var mid = (lo + hi) / 2;
      if (xAt(mid) < target) lo = mid; else hi = mid;
    }
    return (lo + hi) / 2;
  }
  // Shift every point of a cubic toward the bench BACK edge (−y in the rotated
  // section) by `dy`. Used for the channel's START, which must stay ON the bench top
  // face (x unchanged) exactly `dy` behind the bowl rim — that is the point the 28mm
  // dim measures, so it has to be a pure y-shift, not a normal offset.
  function cubicOffsetY(p, dy) {
    return p.map(function (q) { return [q[0], q[1] - dy]; });
  }
  // Unit normal at a cubic's start, pointing toward the bench BACK edge. The channel's
  // MIDDLE control is offset along this instead of straight down −y: a pure y-shift is
  // only perpendicular to the wall where the wall is steep, so as the wall flattens the
  // gap silently closes up (measured 28mm at the rim but ~16mm lower down before this).
  function cubicStartNormal(p) {
    var tx = p[1][0] - p[0][0], ty = p[1][1] - p[0][1];
    var h = Math.sqrt(tx * tx + ty * ty);
    if (h < 1e-6) return [0, -1];
    return [ty / h, -tx / h];
  }
  // Place a cubic's last control point so the curve ARRIVES along the direction of
  // the straight segment that follows (P3 -> next). Without this the bowl wall and
  // both overflow-channel lines arrived travelling straight down the depth axis and
  // then turned a corner into the waste taper — a visible KINK near the bottom.
  // `len` keeps the previous control distance, so only the direction changes.
  function tangentCtl(p3x, p3y, nextX, nextY, len) {
    var dx = nextX - p3x, dy = nextY - p3y;
    var h = Math.sqrt(dx * dx + dy * dy);
    if (h < 1e-6) return [p3x - len, p3y];
    return [p3x - len * dx / h, p3y - len * dy / h];
  }
  function cubicD(p, moveTo) {
    return (moveTo ? 'M ' + p[0][0] + ' ' + p[0][1] + ' ' : '') +
      'C ' + p[1][0] + ' ' + p[1][1] + ' ' + p[2][0] + ' ' + p[2][1] +
      ' ' + p[3][0] + ' ' + p[3][1];
  }
  function rimAngleStep(rad, run, availAlong, availDown) {
    var ca = Math.cos(rad), sa = Math.sin(rad);
    // Longest step along the angle that stays inside the box, then scale by `run`.
    var t = Math.min(ca > 1e-6 ? availAlong / ca : Infinity,
                     sa > 1e-6 ? availDown  / sa : Infinity) * run;
    return { along: t * ca, down: t * sa };
  }
  var SMOOTHE_TAP_WALL = 28;

  // Resolve basin section depth for a model. Falls back to design.height.
  function getBasinDepth(modelId, height) {
    var entry = MODEL_REGISTRY[modelId];
    if (entry && entry.basinDepth != null) return entry.basinDepth;
    return parseFloat(height) || 100;
  }

  // Resolve ramp drop (cross-section diagonal depth) for a model.
  // Falls back to basinDepth, then to design.height.
  //
  // Classic deep-section rule (2026-06-23): the Ramp Basin Classic deepens to
  // the Commercial 80mm cross-section once its height reaches 200mm (Classic
  // is the only model affected — Junior stays 60mm at every height). The model
  // NAME is unchanged; only the drawn ramp profile deepens.
  function getRampDrop(modelId, height) {
    var entry = MODEL_REGISTRY[modelId];
    if (modelId === 'classicC100' && (parseFloat(height) || 0) >= 200) return 80;
    if (entry && entry.rampDrop != null) return entry.rampDrop;
    if (entry && entry.basinDepth != null) return entry.basinDepth;
    return parseFloat(height) || 100;
  }

  // Resolve the back inset (E) for a model. The customer can now set E for
  // ramp and moulded basins in the 50–145mm range. D-Airblade and Xlerator
  // (family = 'dryer') are locked to the family constant (145mm).
  // Falls back to the family default when design.e is missing or out of range.
  function getBackInset(design, constants) {
    var entry = MODEL_REGISTRY[design.model];
    var family = entry && entry.family;
    if (family === 'dryer' || family === 'benchtop') return constants.backInset;
    var e = parseFloat(design.e);
    if (isNaN(e)) return constants.backInset;
    // Clamp to the allowed range so hand-crafted URLs can't crash the renderer.
    if (e < 50)  e = 50;
    if (e > 145) e = 145;
    return e;
  }

  // Human-readable model names for the sheet title
  var MODEL_LABELS = {
    classicC100:   'Ramp Classic 100',
    junior100:     'Ramp Junior 100',
    doubleC100:    'Ramp Classic 100 Double Basin',
    commercial:    'Ramp Commercial',
    commercial200: 'Ramp Commercial 200',
    commercial400: 'Ramp Commercial 400',
    trough180:     'Ramp Trough 180',
    trough150:     'Ramp Trough 150',
    dAirblade400:  'Ramp D-Airblade 400 Basin',
    xlerator400:   'Ramp Xlerator 400 Basin',
    single:        'Formline 200 Moulded Benchtop Basin (Single)',
    double:        'Formline 200 Moulded Benchtop Basin (Double)',
    triple:        'Formline 200 Moulded Benchtop Basin (Triple)',
    quadruple:    'Formline 200 Moulded Benchtop Basin (Quadruple)',
    quintuple:    'Formline 200 Moulded Benchtop Basin (Quintuple)',
    sextuple:     'Formline 200 Moulded Benchtop Basin (Sextuple)'
  };

  // Static panel content shared by every sheet
  var STATIC_INFO = {
    unit: 'mm (3mm tolerance)',
    composition: 'Stoneresin 12mm/20mm',
    notes: [
      '40mm waste',
      'Brackets and screws included',
      '10 year manufacturers warranty'
    ]
  };

  var CONTACT_BLOCKS = [
    {
      heading: 'INTERNATIONAL',
      lines: [
        'Phone: +61 1800 462 946',
        'Email: info@therampbasinfactory.com',
        'Web: www.therampbasinfactory.com'
      ]
    },
    {
      heading: 'AUSTRALIA',
      lines: [
        'Phone: 1800 462 946',
        'Email: info@stonebaths.com.au',
        'Web: www.stonebaths.com.au'
      ]
    },
    {
      heading: 'NEW ZEALAND',
      lines: [
        'Phone: 0800 001 872',
        'Email: info@stonebaths.co.nz',
        'Web: www.stonebaths.co.nz'
      ]
    }
  ];

  // Bracket positions for a given (family, length).
  // Returns an array of mm offsets from the left edge of the bench, or null
  // when no SOP exists for this family (D-Airblade is still TBD).
  //
  // Rules (all models except D-Airblade):
  //   550mm  → 2 holes 250mm apart, 150mm from each edge
  //   700mm  → 2 holes 300mm apart, 200mm from each edge
  //   900mm  → 2 holes 400mm apart, 250mm from each edge
  //   ≥1000mm → first hole centred, then 300mm spacing each side, stopping
  //              before any hole would fall within 120mm of an edge
  // For non-standard lengths between 550 and 1000, the closest specific rule
  // is applied with its edge offset preserved.
  function getBracketPositions(family, length) {
    if (family === 'dryer') return null;        // D-Airblade: own SOP (TBD)
    if (family === 'moulded') return null;      // Moulded: mounts differently, but
                                                 // the back view reuses the spacing
                                                 // rule directly via
                                                 // bracketPositionsForLength().
    if (family === 'benchtop') return null;     // Benchtops have no brackets
    return bracketPositionsForLength(length);
  }

  // Core bracket/bolt-hole spacing rule (length only — no family gate), so it can
  // be reused by paths whose family is excluded from getBracketPositions (e.g. the
  // moulded back view, which now carries bolt holes + corner cutouts).
  function bracketPositionsForLength(length) {
    var L = parseFloat(length) || 0;
    if (L < 500) return null;

    // Specific short-length rules
    if (L === 550) return [150, 400];
    if (L === 700) return [200, 500];
    if (L === 900) return [250, 650];

    // ≥1000mm: centred + 300mm spacing, 120mm edge buffer
    if (L >= 1000) {
      var center = L / 2;
      var positions = [center];
      for (var n = 1; n < 50; n++) {
        var offset = n * 300;
        var leftPos = center - offset;
        var rightPos = center + offset;
        if (leftPos < 120 || rightPos > L - 120) break;
        positions.unshift(leftPos);
        positions.push(rightPos);
      }
      return positions;
    }

    // 500 ≤ L < 1000: closest-rule fallback using the rule's edge offset
    if (L < 625) return [150, L - 150];   // 550-rule range
    if (L < 800) return [200, L - 200];   // 700-rule range
    return [250, L - 250];                 // 900-rule range
  }

  // Bracket-hole height (vertical position from the top of the bench, in mm).
  //   100mm bench → 45mm from top
  //   >100mm bench → half the bench height
  function getBracketHeight(height) {
    var h = parseFloat(height) || 100;
    if (h === 100) return 45;
    if (h > 100) return h / 2;
    return 45; // fallback for unusual short heights
  }

  // ─────────────────────────────────────────────────────────────
  // SVG element helpers
  // ─────────────────────────────────────────────────────────────

  var SVG_NS = 'http://www.w3.org/2000/svg';

  function svgEl(tag, attrs, children) {
    var el = document.createElementNS(SVG_NS, tag);
    if (attrs) {
      for (var k in attrs) {
        if (attrs[k] !== undefined && attrs[k] !== null) {
          el.setAttribute(k, attrs[k]);
        }
      }
    }
    if (children) {
      for (var i = 0; i < children.length; i++) {
        if (children[i]) el.appendChild(children[i]);
      }
    }
    return el;
  }

  function svgText(content, x, y, attrs) {
    var merged = Object.assign({ x: x, y: y }, attrs || {});
    var el = svgEl('text', merged);
    el.textContent = content;
    return el;
  }

  function svgG(attrs, children) {
    return svgEl('g', attrs, children);
  }

  function svgLine(x1, y1, x2, y2, attrs) {
    return svgEl('line', Object.assign({ x1: x1, y1: y1, x2: x2, y2: y2 }, attrs || {}));
  }

  function svgRect(x, y, w, h, attrs) {
    return svgEl('rect', Object.assign({ x: x, y: y, width: w, height: h }, attrs || {}));
  }

  function svgCircle(cx, cy, r, attrs) {
    return svgEl('circle', Object.assign({ cx: cx, cy: cy, r: r }, attrs || {}));
  }

  function svgPath(d, attrs) {
    return svgEl('path', Object.assign({ d: d }, attrs || {}));
  }

  function createSpecSheetSvg() {
    return svgEl('svg', {
      xmlns: SVG_NS,
      viewBox: '0 0 ' + PAGE_W + ' ' + PAGE_H,
      width: PAGE_W + 'mm',
      height: PAGE_H + 'mm',
      class: 'spec-sheet-svg',
      'font-family': '"Helvetica Neue", Helvetica, Arial, sans-serif'
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Format / math utilities
  // ─────────────────────────────────────────────────────────────

  function formatMm(n) {
    if (n === null || n === undefined || isNaN(n)) return '';
    return Math.round(n).toString();
  }

  function todayString() {
    var d = new Date();
    var mm = String(d.getMonth() + 1);
    var dd = String(d.getDate());
    return d.getFullYear() + '-' + (mm.length < 2 ? '0' + mm : mm) + '-' + (dd.length < 2 ? '0' + dd : dd);
  }

  // Stable-ish 6-char ID derived from project + model + dimensions
  function designId(design) {
    if (design.id) return design.id;
    var seed = (design.project || '') + '|' + (design.model || '') + '|' + design.length + '|' + design.width;
    var h = 0;
    for (var i = 0; i < seed.length; i++) {
      h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
    }
    return 'RB-' + Math.abs(h).toString(36).toUpperCase().slice(0, 6);
  }

  // ─────────────────────────────────────────────────────────────
  // Net weight — surface-area model (provisional, 2026-07-28)
  //
  // Solid-surface / stone-resin weight ≈ shell surface area × areal mass.
  // We approximate the developed shell as the bench footprint plus the four
  // basin cavity walls, and multiply by a single areal constant.
  //
  //   weight = AREAL_KG_PER_M2 × ( footprint + cavityWalls )      [areas in m²]
  //   footprint    = length × width
  //   cavityWalls  = 2 × (totalBasinLength + count × basinWidth) × height
  //
  // AREAL_KG_PER_M2 is calibrated to ONE real weigh-in:
  //   Commercial 4000 × 500 × 150 (D300 E145 G20) = 145 kg.
  // It back-tests sensibly against the old per-family kg/m constants, but is
  // NOT yet validated across the range — retune once real weights come in.
  //
  // Only the ramp-bench families use the area model. Moulded + benchtop stay
  // on the legacy per-metre constant until we have calibration data for them.
  // ─────────────────────────────────────────────────────────────
  var AREAL_KG_PER_M2 = 46.5;
  var AREA_MODEL_FAMILIES = { classic: true, 'classic-double': true, trough: true, dryer: true };

  // Developed shell area (m²) for a ramp bench, from the design's own dims.
  function rampBenchArea(design, entry, constants) {
    var len = parseFloat(design.length) || 1500;
    var wid = parseFloat(design.width)  || (entry && entry.widthDefault) || constants.defaultWidth || 460;
    var hgt = parseFloat(design.height) || 100;

    var backInset  = getBackInset(design, constants);           // E
    var frontInset = parseFloat(design.g) || constants.lockedG || 20; // G
    var basinWidth = Math.max(20, wid - backInset - frontInset);
    var dInset     = parseFloat(design.d) || 100;

    // Basin count (Classic multi-basin; legacy doubleC100 = 2).
    var count = (design.model === 'doubleC100') ? 2 : (parseInt(design.count, 10) || 1);

    // Total basin opening length across all basins.
    var totalBasinLen;
    if (count <= 1 && design.basinLength &&
        (design.basinPosition === 'left' || design.basinPosition === 'right')) {
      totalBasinLen = parseFloat(design.basinLength);           // positioned single
    } else {
      // Equal split: N basins separated by (N+1) D-width end/divider insets.
      totalBasinLen = Math.max(20, len - (count + 1) * dInset);
    }

    var footprint   = (len * wid) / 1e6;
    var cavityWalls = (2 * (totalBasinLen + count * basinWidth) * hgt) / 1e6;
    return footprint + cavityWalls;
  }

  function estimateNetWeight(family, length, design) {
    var c = FAMILY_CONSTANTS[family];
    if (!c) return null;

    var kg = null;
    if (design && AREA_MODEL_FAMILIES[family]) {
      var entry = MODEL_REGISTRY[design.model] || {};
      var area = rampBenchArea(design, entry, c);
      if (area > 0) kg = AREAL_KG_PER_M2 * area;
    }

    // Legacy per-metre fallback (moulded, benchtop, or no design supplied).
    if (kg == null) {
      if (!c.kgPerMetre || !length) return null;
      kg = (c.kgPerMetre * length) / 1000;
    }

    return Math.round(kg / 5) * 5; // round to nearest 5 kg
  }

  // ─────────────────────────────────────────────────────────────
  // Style presets
  // ─────────────────────────────────────────────────────────────

  // Solid surface shell thickness — shown as a 20mm hatched band on the side view
  // (Composition: Matte White Solid Surface 12mm/20mm — using 20mm in cross-section.)
  var MATERIAL_THICKNESS = 20;

  var STYLE = {
    bodyStroke:     { stroke: '#1a1a1a', 'stroke-width': 0.3, fill: 'none' },
    bodyStrokeBold: { stroke: '#1a1a1a', 'stroke-width': 0.5, fill: 'none' },
    dimLine:        { stroke: '#1a1a1a', 'stroke-width': 0.18, fill: 'none' },
    dimText:        { 'font-size': 2.4, fill: '#1a1a1a', 'text-anchor': 'middle' },
    dimTextSm:      { 'font-size': 1.9, fill: '#1a1a1a', 'text-anchor': 'middle' },
    hiddenLine:     { stroke: '#1a1a1a', 'stroke-width': 0.18, fill: 'none', 'stroke-dasharray': '0.8 0.6' },
    label:          { 'font-size': 3.2, fill: '#1a1a1a' },           // increased for readability (was 2.8)
    labelBold:      { 'font-size': 3.4, fill: '#1a1a1a', 'font-weight': 'bold' },
    hatchStroke:    { stroke: '#1a1a1a', 'stroke-width': 0.15 }
  };

  // ─────────────────────────────────────────────────────────────
  // Shared geometry helpers
  // ─────────────────────────────────────────────────────────────

  /**
   * Draw a horizontal or vertical dimension line with tick marks and label.
   * For horizontal: y1 === y2, label sits above by default.
   * For vertical:   x1 === x2, label sits left, rotated -90.
   */
  function drawDimensionLine(parent, x1, y1, x2, y2, label, opts) {
    opts = opts || {};
    var horizontal = Math.abs(y1 - y2) < 0.01;
    var tick = opts.tickSize || 1.4;
    var labelOff = opts.labelOffset || 1.0;
    var line = STYLE.dimLine;

    var g = svgG({ class: 'dim-line' });
    g.appendChild(svgLine(x1, y1, x2, y2, line));

    if (horizontal) {
      g.appendChild(svgLine(x1, y1 - tick / 2, x1, y1 + tick / 2, line));
      g.appendChild(svgLine(x2, y2 - tick / 2, x2, y2 + tick / 2, line));
    } else {
      g.appendChild(svgLine(x1 - tick / 2, y1, x1 + tick / 2, y1, line));
      g.appendChild(svgLine(x2 - tick / 2, y2, x2 + tick / 2, y2, line));
    }

    if (label) {
      var t;
      if (horizontal) {
        var cx = (x1 + x2) / 2;
        // Default: label sits just ABOVE the line. opts.labelBelow flips it so
        // the label sits just BELOW the line — useful when a dim row is placed
        // under a feature (e.g. tap positions below the tap holes).
        var cy = opts.labelBelow
          ? Math.max(y1, y2) + labelOff + 1.7    // +1.7 ≈ font-size to drop text fully below the line
          : Math.min(y1, y2) - labelOff;
        t = svgText(label, cx, cy, Object.assign({}, STYLE.dimText, { 'dominant-baseline': 'baseline' }));
      } else {
        // Default: label on the LEFT of the line. opts.labelRight flips it to
        // the right (same -90 rotation, consistent with all vertical dims).
        var vx = opts.labelRight
          ? Math.max(x1, x2) + labelOff
          : Math.min(x1, x2) - labelOff;
        var vy = (y1 + y2) / 2;
        t = svgText(label, vx, vy, Object.assign({}, STYLE.dimText, {
          transform: 'rotate(-90 ' + vx + ' ' + vy + ')',
          'dominant-baseline': 'baseline'
        }));
      }
      g.appendChild(t);
    }

    parent.appendChild(g);
    return g;
  }

  // Diagonal-hatch fill used to indicate solid material in cross-sections.
  function drawHatching(parent, rect, opts) {
    opts = opts || {};
    var spacing = opts.spacing || 1.0;
    var angle = opts.angle != null ? opts.angle : 45;
    var stroke = STYLE.hatchStroke;

    var clipId = 'hp-' + Math.random().toString(36).slice(2, 10);
    var defs = svgEl('defs', null, [
      svgEl('clipPath', { id: clipId }, [svgRect(rect.x, rect.y, rect.w, rect.h)])
    ]);
    var g = svgG({ class: 'hatch', 'clip-path': 'url(#' + clipId + ')' });

    var rad = (angle * Math.PI) / 180;
    var dx = Math.cos(rad);
    var dy = Math.sin(rad);
    var len = Math.hypot(rect.w, rect.h) * 1.5;
    var stepN = Math.ceil(len / spacing);
    var cx = rect.x + rect.w / 2;
    var cy = rect.y + rect.h / 2;
    for (var i = -stepN; i <= stepN; i++) {
      var ox = cx + i * spacing * -dy;
      var oy = cy + i * spacing * dx;
      g.appendChild(svgLine(ox - dx * len, oy - dy * len, ox + dx * len, oy + dy * len, stroke));
    }

    parent.appendChild(defs);
    parent.appendChild(g);
  }

  // Tap hole with optional Ø35 leader callout.
  function drawTapHole(parent, cx, cy, opts) {
    opts = opts || {};
    var r = opts.r || 1.4;
    parent.appendChild(svgCircle(cx, cy, r, STYLE.bodyStroke));
    if (opts.showCallout) {
      // Label sits BELOW the tap (in the back-inset strip) so it doesn't print
      // over the bench's back edge at small plan-view scales. Leader runs from
      // the bottom-right of the circle down to the label baseline.
      var lx = cx + 5;
      var ly = (opts.calloutY != null) ? opts.calloutY : cy + 5;
      parent.appendChild(svgLine(cx + r * 0.7, cy + r * 0.7, lx, ly, STYLE.dimLine));
      parent.appendChild(svgText('Ø35', lx + 0.5, ly, Object.assign({}, STYLE.dimText, { 'text-anchor': 'start' })));
    }
  }

  // Tap-hole spacing — mirrors the configurator's renderTopFaceTaps algorithm
  // (in cube-visual-section.liquid) so the spec sheet matches the cube preview.
  // Returns an array of tap-centre offsets in mm from the basin's LEFT edge.
  //   basinLength  — basin opening length in mm (e.g. 1500 - 2*100 for Classic)
  //   tapCount     — value from design.h
  //   tapEdgeInset — distance from basin edge to first/last tap (mm). Default 200.
  // Behaviour mirrors the configurator:
  //   - If tap zone (basinLength - 2*edge) <= 0 → one centred tap (short basin).
  //   - If tap zone < 500mm → force 1 tap (centred), EXCEPT the Ramp Junior
  //     (allowSub500 = true), which honours the selected count down to the 250mm
  //     minimum so short benches can still show 2+ taps.
  //   - 2+ taps spread evenly across the zone; if zone > 1399mm, the spacing
  //     is held to a minimum of 500mm (clamped to max possible inside the zone).
  function computeTapXOffsets(basinLength, tapCount, tapEdgeInset, allowSub500) {
    var count = parseInt(tapCount, 10);
    if (!count || count < 1) return [];
    var edge = Math.max(0, parseFloat(tapEdgeInset));
    if (!isFinite(edge)) edge = 200;
    var zone = basinLength - 2 * edge;
    // Basin too short for the edge inset at both ends: a single tap has no
    // spacing to satisfy, so still return ONE centred tap rather than hiding it
    // (matches the cube builder). Callers only invoke this when tapCount > 0.
    if (zone <= 0) return [basinLength / 2];
    var n = (zone < 500 && !allowSub500) ? 1 : count;
    var spacing = n > 1 ? zone / (n - 1) : 0;
    if (zone > 1399) spacing = Math.max(spacing, 500);
    if (n > 1) {
      var maxAllowed = zone / (n - 1);
      spacing = Math.min(spacing, maxAllowed);
    }
    var totalSpan = (n - 1) * spacing;
    var zoneStart = (zone - totalSpan) / 2;
    var out = [];
    for (var i = 0; i < n; i++) {
      out.push(edge + zoneStart + i * spacing);
    }
    return out;
  }

  // Chained tap-position dimension row, placed in the plan view just below
  // the tap holes. Renders edge → tap1 → tap2 → … → tapN → edge, with each
  // segment's length labelled (in mm) below the dim line so it doesn't
  // collide with the tap circles above. Skips drawing if no tap positions.
  //   bx, bw           — bench origin and width in SVG units
  //   scalePlan        — SVG-units-per-mm scale used by the plan view
  //   dimY             — y-coord (SVG units) for the dim row
  //   tapBenchOffsets  — tap centre positions in MM from the bench's left edge
  function drawTapDimChain(parent, bx, bw, scalePlan, dimY, tapBenchOffsets) {
    if (!tapBenchOffsets || !tapBenchOffsets.length) return;
    var sorted = tapBenchOffsets.slice().sort(function (a, b) { return a - b; });
    var prevMm = 0;
    for (var i = 0; i < sorted.length; i++) {
      drawDimensionLine(parent,
        bx + prevMm * scalePlan, dimY,
        bx + sorted[i] * scalePlan, dimY,
        formatMm(sorted[i] - prevMm),
        { labelBelow: true });
      prevMm = sorted[i];
    }
    drawDimensionLine(parent,
      bx + prevMm * scalePlan, dimY,
      bx + bw, dimY,
      formatMm((bw / scalePlan) - prevMm),
      { labelBelow: true });
  }

  // ─────────────────────────────────────────────────────────────
  // Soap dispenser helpers
  //
  // Mirrors the configurator's getSoapDispenserConfig + renderTopFaceSoapDispensers.
  // Ratios:
  //   1-1            — count = H; one soap offset from each tap on the chosen side.
  //   1-2 (even H)   — count = H/2; one soap per pair (gaps 0, 2, 4 …) anchored
  //                    from the left tap of each pair.
  //   2-3 (any H ≥3) — count = H-1; one soap in every gap, anchored from the
  //                    left tap of each gap.
  // distFromBasin places the row in the back-inset zone (same depth as the taps
  // by default — the tap centreline, E / 2; 55mm in the 110mm E strip, 73mm in
  // the 145mm strip the dryer models lock to). See tapLineDist() below.
  // ─────────────────────────────────────────────────────────────

  // Depth of the TAP row inside the E back-inset strip, measured from the
  // basin's back edge.
  //
  // Since 2026-08-11 the ramp builder lets the customer set this (the
  // "Tap distance from Basin Back Edge"
  // field) and passes it as `tapDistFromBasin`. The param is emitted ONLY when
  // it differs from the E/2 default, so an absent param means "auto-centred" —
  // hence the fallback to E / 2 here, which also keeps every pre-existing quote
  // URL rendering exactly as it did before.
  //
  // Mirrors tapLineDistFromBasin() in sections/cube-visual-section.liquid — keep
  // the two in step. Falls back to 55 only if E itself is missing.
  //
  // Also used as the fallback depth for soap / dryer holes when a URL predates
  // the soapDistFromBasin / dryerDistFromBasin params, so those holes stay in
  // line with the taps wherever the taps sit.
  function tapLineDist(design) {
    var t = parseFloat(design.tapDistFromBasin);
    if (!isNaN(t) && t >= 0) return t;
    var e = parseFloat(design.e);
    if (isNaN(e) || e <= 0) return 55;
    // Moulds carrying their own standard (TP957 = 52mm in from the bench back
    // edge) don't centre in the strip — without this a soap/dryer hole falling
    // back to the default would sit 9mm off the tap it's meant to line up with.
    var m = MOULD_SPECS[normaliseMouldKey(design.mould)];
    if (m && m.tapFromBackEdge) return Math.max(10, Math.round(e - m.tapFromBackEdge));
    return Math.round(e / 2);
  }

  // Tap-row depth used by the ramp plan views. Same value as tapLineDist(), but
  // resolved against the backInset the render path actually computed (which can
  // differ from design.e on models that lock E), so the tap never drifts out of
  // the strip that was drawn.
  // The E/2 fallback is ROUNDED, matching tapLineDefaultDist() in the builder
  // and the whole-mm figure the offset dim has always printed. On odd E (the
  // 145mm dryer lock, or a typed 175) this moves the drawn hole by 0.5mm so the
  // drawing finally agrees with its own dimension — invisible in print, and it
  // puts the tap exactly on the soap/dryer default line, which already rounded.
  //
  // CALLERS: measure DOWN-UP from basinY, never from `by` —
  //   tapsY = basinY - tapRowDist(...) * scalePlan
  // exactly as the soap (line ~1113), dryer (~1344) and offset-dim (~1088) rows
  // do. The old `by + backInset/2` form only worked because the midpoint is
  // symmetric about the strip; substituting an arbitrary depth into it mirrors
  // the tap to the wrong side of the strip.
  function tapRowDist(design, backInset) {
    var t = parseFloat(design.tapDistFromBasin);
    if (!isNaN(t) && t >= 0) return t;
    return Math.round(backInset / 2);
  }

  // Moulded variant of tapRowDist(). Same param, but the FALLBACK is the mould's
  // own default: TP957 fixes its tap 52mm in from the bench back edge
  // (`tapFromBackEdge`), everything else centres in the E strip. Mirrors
  // tapLineDefaultDist() in sections/moulded-basin-builder.liquid — keep in step.
  // Tap-deck moulds (TP952/TP953) never reach this: their tap sits on the apron
  // deck and is positioned from the bowl, not the bench edge.
  function mouldedTapRowDist(design, mould, backInset) {
    var t = parseFloat(design.tapDistFromBasin);
    if (!isNaN(t) && t >= 0) return t;
    // Mirrors the builder's guard: a missing/zero E has no strip to measure into.
    if (isNaN(backInset) || backInset <= 0) return 55;
    if (mould && mould.tapFromBackEdge) {
      return Math.max(10, Math.round(backInset - mould.tapFromBackEdge));
    }
    return Math.round(backInset / 2);
  }

  function getSoapConfig(design) {
    var enabled = design.soapDispenser;
    if (enabled !== '1' && enabled !== 1) return null;
    var h = parseInt(design.h, 10) || 0;
    if (h === 0) return null;

    // Ratio defaults to '1-1' for back-compat with URLs that pre-date the field.
    var ratio = design.soapRatio || '1-1';
    // Coerce invalid combinations back to a sensible default.
    if (ratio === '1-2' && h % 2 !== 0) ratio = '1-1';
    if (ratio === '2-3' && h < 3) ratio = '1-1';

    // H is per basin (legacy doubleC100 stored it as a total). The effective
    // tap count for placement is H × basin count so multi-basin gets one soap
    // per tap on every basin, not just the first.
    var bcS = (design.model === 'doubleC100') ? 2 : (parseInt(design.count, 10) || 1);
    var totalTapsS = (design.model === 'doubleC100') ? h : h * bcS;

    var count = 0;
    if (ratio === '1-1')      count = totalTapsS;
    else if (ratio === '1-2') count = Math.floor(totalTapsS / 2);
    else if (ratio === '2-3') count = totalTapsS - 1;

    var multiTap = totalTapsS > 1;
    var distFromTap = parseFloat(design.soapDistFromTap);
    if (isNaN(distFromTap) || distFromTap <= 0) distFromTap = 100;

    return {
      ratio: ratio,
      count: count,
      side: design.soapSide || 'left',
      diameter: parseFloat(design.soapDiameter) || 25,
      distFromTap: distFromTap,
      distFromBasin: parseFloat(design.soapDistFromBasin) || tapLineDist(design),
      multiTap: multiTap
    };
  }

  // Returns soap dispenser centre positions in MM from the bench's left edge,
  // computed from the tap positions (also bench-mm) according to the ratio.
  function computeSoapBenchOffsets(soapConfig, tapBenchOffsets, benchLength) {
    if (!soapConfig || !tapBenchOffsets || !tapBenchOffsets.length) return [];
    var sorted = tapBenchOffsets.slice().sort(function (a, b) { return a - b; });
    var dist   = soapConfig.distFromTap;
    var out    = [];

    if (soapConfig.ratio === '1-1') {
      if (!soapConfig.multiTap) {
        var dir = (soapConfig.side === 'left') ? -1 : 1;
        var pos = sorted[0] + dir * dist;
        if (pos >= 0 && pos <= benchLength) out.push(pos);
      } else {
        var dirM = (soapConfig.side === 'left') ? -1 : 1;
        for (var i = 0; i < sorted.length; i++) {
          var p = sorted[i] + dirM * dist;
          if (p >= 0 && p <= benchLength) out.push(p);
        }
      }
      return out;
    }

    if (soapConfig.ratio === '1-2') {
      // Pair (sorted[0], sorted[1]), (sorted[2], sorted[3]), … — anchor on left tap.
      for (var j = 0; j + 1 < sorted.length; j += 2) {
        var pp = sorted[j] + dist;
        if (pp >= 0 && pp <= benchLength) out.push(pp);
      }
      return out;
    }

    if (soapConfig.ratio === '2-3') {
      // One per gap; anchor on left tap of each gap.
      for (var k = 0; k + 1 < sorted.length; k++) {
        var pk = sorted[k] + dist;
        if (pk >= 0 && pk <= benchLength) out.push(pk);
      }
      return out;
    }

    return out;
  }

  // Draw soap dispenser circles on the plan view. Ø label on the first hole.
  // Each circle has a small "+" cross inside it to visually distinguish soap
  // dispenser holes from plain tap holes.
  function drawSoapHoles(parent, bx, scalePlan, soapY, benchOffsets, diameter) {
    if (!benchOffsets || !benchOffsets.length) return;
    var r = Math.max(0.7, (diameter / 2) * scalePlan);
    // Cross arms extend ~70% of the radius so they stay clearly inside the circle.
    var crossArm = r * 0.7;
    for (var i = 0; i < benchOffsets.length; i++) {
      var cx = bx + benchOffsets[i] * scalePlan;
      parent.appendChild(svgCircle(cx, soapY, r, STYLE.bodyStroke));
      // Cross in the middle (horizontal + vertical arms)
      parent.appendChild(svgLine(cx - crossArm, soapY, cx + crossArm, soapY, STYLE.bodyStroke));
      parent.appendChild(svgLine(cx, soapY - crossArm, cx, soapY + crossArm, STYLE.bodyStroke));
      if (i === 0) {
        // Same placement as the tap Ø callout — label sits BELOW the hole so
        // it doesn't overprint the bench's back edge.
        var lx = cx + 5;
        var ly = soapY + 5;
        parent.appendChild(svgLine(cx + r * 0.7, soapY + r * 0.7, lx, ly, STYLE.dimLine));
        parent.appendChild(svgText('Ø' + Math.round(diameter), lx + 0.5, ly,
          Object.assign({}, STYLE.dimText, { 'text-anchor': 'start' })));
      }
    }
  }

  // Chained soap-position dim row — placed just below the tap dim row.
  // Same shape as drawTapDimChain: edge → soap1 → soap2 → … → edge.
  function drawSoapDimChain(parent, bx, bw, scalePlan, dimY, soapBenchOffsets) {
    if (!soapBenchOffsets || !soapBenchOffsets.length) return;
    var sorted = soapBenchOffsets.slice().sort(function (a, b) { return a - b; });
    var prevMm = 0;
    for (var i = 0; i < sorted.length; i++) {
      drawDimensionLine(parent,
        bx + prevMm * scalePlan, dimY,
        bx + sorted[i] * scalePlan, dimY,
        formatMm(sorted[i] - prevMm),
        { labelBelow: true });
      prevMm = sorted[i];
    }
    drawDimensionLine(parent,
      bx + prevMm * scalePlan, dimY,
      bx + bw, dimY,
      formatMm((bw / scalePlan) - prevMm),
      { labelBelow: true });
  }

  // distFromBasin dims (Option 2) — vertical witness lines stacked at the RIGHT
  // end of the back-inset band (inside the bench), one per DISTINCT offset among
  // the present tap / soap / dryer rows. Replaces the old per-feature dims that
  // stacked off the LEFT edge and ran off the page once two or three were shown.
  // Equal offsets collapse to a single line (e.g. tap = soap = dryer = 55 on a
  // Classic → one dim).
  //   bx, bw           — plan bench left edge + width in SVG units (length axis)
  //   scalePlan        — mm → SVG units
  //   basinY           — basin back-edge Y (datum the rows measure down to)
  //   tapBenchOffsets  — tap centres (bench-mm); used to confirm rows exist
  //   benchLengthMm    — bench length (mm)
  //   tapRowMm         — tap row's offset from the basin back edge (mm), or null
  //                      when this model has no taps in the back-inset band
  function drawBackInsetOffsetDims(parent, design, bx, bw, scalePlan, basinY, tapBenchOffsets, benchLengthMm, tapRowMm) {
    var hasTaps = !!(tapBenchOffsets && tapBenchOffsets.length);
    var raw = [];
    if (hasTaps && tapRowMm != null) raw.push(Math.round(tapRowMm));
    var soapCfg = getSoapConfig(design);
    if (soapCfg && hasTaps && computeSoapBenchOffsets(soapCfg, tapBenchOffsets, benchLengthMm).length) {
      raw.push(Math.round(soapCfg.distFromBasin));
    }
    var dryerCfg = getDryerConfig(design);
    if (dryerCfg && hasTaps && computeSoapBenchOffsets(dryerCfg, tapBenchOffsets, benchLengthMm).length) {
      raw.push(Math.round(dryerCfg.distFromBasin));
    }
    // Dedup equal offsets; drop any that sit on the basin line (~0mm).
    var seen = {};
    var distinct = [];
    for (var i = 0; i < raw.length; i++) {
      var mm = raw[i];
      if (mm <= 0 || seen[mm]) continue;
      seen[mm] = true;
      distinct.push(mm);
    }
    if (!distinct.length) return;
    // Largest offset (tallest witness) nearest the bench edge; step inward for
    // each smaller one so the lines and their labels never coincide.
    distinct.sort(function (a, b) { return b - a; });
    var step = 5.5;        // horizontal gap between stacked witness lines
    var rightInset = 3;    // first line this far inside the bench's right edge
    for (var j = 0; j < distinct.length; j++) {
      var x = bx + bw - rightInset - j * step;
      var rowY = basinY - distinct[j] * scalePlan;
      drawDimensionLine(parent, x, rowY, x, basinY, formatMm(distinct[j]));
    }
  }

  // Convenience: dispatch all the plan-view soap drawing in one call.
  // Returns the soapY (so a render path can place a dim chain just below it
  // if needed). When soapCfg is null the call is a no-op and returns null.
  //   bx, by           — plan-view bench top-left (SVG units)
  //   bw               — bench width in SVG units (length axis)
  //   bh               — bench height in SVG units (width axis)
  //   scalePlan        — mm → SVG units
  //   basinY           — basin's back edge Y in SVG units
  //   tapBenchOffsets  — array of tap positions in MM from bench left
  //   benchLengthMm    — bench length in MM (for bounds)
  //   tapDimY          — Y of the tap dim chain (SVG units) — soap dim sits below
  //   rowPitch         — optional vertical gap between stacked dim rows (default
  //                      4.5). The moulded path shrinks it when a 4-row stack
  //                      (tap/soap/dryer/waste) has to fit a tall bench's tighter
  //                      plan→front gap.
  function drawSoapDispenserPlan(parent, design, bx, by, bw, bh, scalePlan, basinY, tapBenchOffsets, benchLengthMm, tapDimY, rowPitch) {
    var soapCfg = getSoapConfig(design);
    if (!soapCfg) return null;
    var soapBenchOffsets = computeSoapBenchOffsets(soapCfg, tapBenchOffsets, benchLengthMm);
    if (!soapBenchOffsets.length) return null;
    var soapY = basinY - soapCfg.distFromBasin * scalePlan;
    drawSoapHoles(parent, bx, scalePlan, soapY, soapBenchOffsets, soapCfg.diameter);
    // Soap dim chain sits just below the tap dim chain (one rowPitch down so the
    // two rows don't collide).
    var soapDimY = (tapDimY != null ? tapDimY : (by + bh + 3.5)) + (rowPitch || 4.5);
    drawSoapDimChain(parent, bx, bw, scalePlan, soapDimY, soapBenchOffsets);
    // distFromBasin dim is no longer drawn here — it's handled collectively by
    // drawBackInsetOffsetDims (right-side stack, dedup'd with tap + dryer).
    return soapY;
  }

  // ─────────────────────────────────────────────────────────────
  // Dryer holes — mirror the soap dispenser apparatus (same ratio system,
  // side, diameter, distFromTap, distFromBasin) with two differences:
  //   1. A backplate (280mm × 20mm, reusing the D-Airblade plate geometry) is
  //      drawn at the bench's outer back edge behind each dryer hole.
  //   2. The default hole Ø is 40mm.
  // The dAirblade400 model is excluded (its dryer is built into the tap); the
  // xlerator400 dryer model still supports an added dryer hole.
  // ─────────────────────────────────────────────────────────────
  var DRYER_BACKPLATE_W = 280;      // mm — same plate as the D-Airblade backplate
  var DRYER_BACKPLATE_THICK = 20;   // mm — recess depth into the back wall (plan view)
  // Back-elevation dimensions — identical to the D-Airblade backplate.
  var DRYER_BACKPLATE_H = 150;          // mm — plate height in the back view
  var DRYER_BACKPLATE_BOTTOM_OFFSET = 70; // mm — plate bottom edge above the bench bottom
  var DRYER_BACKPLATE_HOLE_HSPACING = 185; // mm — mounting-hole horizontal pitch
  var DRYER_BACKPLATE_HOLE_VEDGE = 42;     // mm — mounting hole inset from plate top/bottom

  function getDryerConfig(design) {
    if (design.model === 'dAirblade400') return null; // built-in dryer; excluded
    var enabled = design.dryerHole;
    if (enabled !== '1' && enabled !== 1) return null;
    var h = parseInt(design.h, 10) || 0;
    if (h === 0) return null;

    var ratio = design.dryerRatio || '1-1';
    if (ratio === '1-2' && h % 2 !== 0) ratio = '1-1';
    if (ratio === '2-3' && h < 3) ratio = '1-1';

    // Effective tap count = H × basin count (legacy doubleC100 stored a total).
    var bcD = (design.model === 'doubleC100') ? 2 : (parseInt(design.count, 10) || 1);
    var totalTapsD = (design.model === 'doubleC100') ? h : h * bcD;

    var count = 0;
    if (ratio === '1-1')      count = totalTapsD;
    else if (ratio === '1-2') count = Math.floor(totalTapsD / 2);
    else if (ratio === '2-3') count = totalTapsD - 1;

    var multiTap = totalTapsD > 1;
    var distFromTap = parseFloat(design.dryerDistFromTap);
    if (isNaN(distFromTap) || distFromTap <= 0) distFromTap = 100;

    var backplateOffset = parseFloat(design.dryerBackplateOffset);
    if (isNaN(backplateOffset)) backplateOffset = 90;

    return {
      ratio: ratio,
      count: count,
      side: design.dryerSide || 'left',
      diameter: parseFloat(design.dryerDiameter) || 40,
      distFromTap: distFromTap,
      distFromBasin: parseFloat(design.dryerDistFromBasin) || tapLineDist(design),
      multiTap: multiTap,
      backplateOffset: backplateOffset,
      backplateSide: design.dryerBackplateSide === 'right' ? 'right' : 'left',
      backplateOverrides: design.dryerBackplateOffsets
    };
  }

  // Backplate constant — must clear the back edge brackets (110mm keepout from
  // each bench end, measured to the 280mm plate's nearest edge).
  var DRYER_BACKPLATE_EDGE_KEEPOUT = 110;

  // The dryer BACKPLATE is only fitted on basins ≥ 300mm tall (a shorter basin
  // wall can't carry it). The dryer HOLE itself is unaffected — it draws at any
  // height. Below this, both plan-view and back-elevation plates are suppressed.
  var DRYER_BACKPLATE_MIN_HEIGHT = 300;
  function dryerHasBackplate(design) {
    return (parseFloat(design.height) || 0) >= DRYER_BACKPLATE_MIN_HEIGHT;
  }

  // Returns backplate centre positions (bench-mm) — one per dryer hole. Each is
  // the hole offset ± backplateOffset on backplateSide, overridable per hole via
  // the dryerBackplateOffsets CSV, then clamped so the 280mm plate's edge never
  // crosses the 110mm bracket keepout at either bench end.
  function computeDryerBackplatePositions(dryerCfg, holeOffsets, benchLengthMm) {
    if (!dryerCfg || !holeOffsets || !holeOffsets.length) return [];
    var dir = dryerCfg.backplateSide === 'right' ? 1 : -1;
    var defSigned = dryerCfg.backplateOffset * dir;
    var overrides = parseOffsetList(dryerCfg.backplateOverrides);
    var half = DRYER_BACKPLATE_W / 2;
    var minC = DRYER_BACKPLATE_EDGE_KEEPOUT + half;
    var maxC = benchLengthMm - DRYER_BACKPLATE_EDGE_KEEPOUT - half;
    return holeOffsets.map(function (h, i) {
      var signed = (overrides[i] !== undefined && overrides[i] !== null) ? overrides[i] : defSigned;
      var c = h + signed;
      if (minC <= maxC) c = Math.max(minC, Math.min(maxC, c));
      return c;
    });
  }

  // Draw the dryer holes on the plan view. A concentric inner ring distinguishes
  // them from plain tap holes and soap (+) holes. Backplates are drawn separately
  // (offset from the hole) by drawDryerBackplates.
  function drawDryerHoles(parent, bx, scalePlan, dryerY, benchOffsets, diameter) {
    if (!benchOffsets || !benchOffsets.length) return;
    var r = Math.max(0.7, (diameter / 2) * scalePlan);
    for (var i = 0; i < benchOffsets.length; i++) {
      var cx = bx + benchOffsets[i] * scalePlan;
      parent.appendChild(svgCircle(cx, dryerY, r, STYLE.bodyStroke));
      parent.appendChild(svgCircle(cx, dryerY, Math.max(0.4, r * 0.5), STYLE.bodyStroke));
      if (i === 0) {
        var lx = cx + 5;
        var ly = dryerY + 5;
        parent.appendChild(svgLine(cx + r * 0.7, dryerY + r * 0.7, lx, ly, STYLE.dimLine));
        parent.appendChild(svgText('Ø' + Math.round(diameter), lx + 0.5, ly,
          Object.assign({}, STYLE.dimText, { 'text-anchor': 'start' })));
      }
    }
  }

  // Draw the 280mm × 20mm backplate rectangles at the bench's outer back edge,
  // one centred on each computed backplate position, plus a light leader line
  // from each dryer hole to its backplate so the offset reads clearly.
  function drawDryerBackplates(parent, bx, by, scalePlan, dryerY, holeOffsets, bpCenters) {
    if (!bpCenters || !bpCenters.length) return;
    var bpW = DRYER_BACKPLATE_W * scalePlan;
    var bpH = DRYER_BACKPLATE_THICK * scalePlan;
    for (var i = 0; i < bpCenters.length; i++) {
      var bpCx = bx + bpCenters[i] * scalePlan;
      parent.appendChild(svgRect(bpCx - bpW / 2, by, bpW, bpH, STYLE.bodyStroke));
      // Leader from the hole to the backplate centre (along the back-edge row).
      if (holeOffsets && holeOffsets[i] !== undefined) {
        var holeCx = bx + holeOffsets[i] * scalePlan;
        parent.appendChild(svgLine(holeCx, dryerY, bpCx, by + bpH, STYLE.dimLine));
      }
    }
  }

  // Draw the dryer backplate(s) in the BACK ELEVATION, using the same plate
  // dimensions as the D-Airblade backplate: 280mm × 150mm, bottom edge 70mm
  // above the bench bottom, with 4 mounting holes (2 rows × 2 cols, 185mm
  // horizontal pitch, 42mm in from the plate top/bottom). `feX` is the back
  // view's left edge (bench-mm 0); `bvY`/`bvH` are its top and height in SVG.
  function drawDryerBackplatesBackView(parent, design, feX, bvY, bvH, scalePlan, tapBenchOffsets, benchLengthMm, cornerEndMm) {
    var dryerCfg = getDryerConfig(design);
    if (!dryerCfg) return;
    // Backplate only on basins ≥ 300mm tall.
    if (!dryerHasBackplate(design)) return;
    var offsets = computeSoapBenchOffsets(dryerCfg, tapBenchOffsets, benchLengthMm);
    if (!offsets.length) return;
    var bpCenters = computeDryerBackplatePositions(dryerCfg, offsets, benchLengthMm);
    // Top dim chain across the bench width. cornerEndMm is the inset where the
    // chain starts/ends: 85 (20mm edge + 65mm corner) for the bracket families,
    // so it fills the MIDDLE of the corner-bracket dim row; 0 for moulded (no
    // corner brackets) so it runs full-width edge→edge (gap · 280 · gap · …).
    // undefined/null → no chain (e.g. the D-Airblade top-dim layout differs).
    if (cornerEndMm !== undefined && cornerEndMm !== null) {
      drawDryerBackplateTopChain(parent, design, feX, bvY, scalePlan, tapBenchOffsets,
        benchLengthMm, bvY - 4, cornerEndMm, benchLengthMm - cornerEndMm);
    }
    var bpW = DRYER_BACKPLATE_W * scalePlan;
    var bpH = DRYER_BACKPLATE_H * scalePlan;
    var botEdge = bvY + bvH - DRYER_BACKPLATE_BOTTOM_OFFSET * scalePlan;
    var topEdge = Math.max(bvY, botEdge - bpH);
    var hSpacing = DRYER_BACKPLATE_HOLE_HSPACING * scalePlan;
    var vEdge = DRYER_BACKPLATE_HOLE_VEDGE * scalePlan;
    for (var i = 0; i < bpCenters.length; i++) {
      var cx = feX + bpCenters[i] * scalePlan;
      var x = cx - bpW / 2;
      parent.appendChild(svgRect(x, topEdge, bpW, bpH, STYLE.bodyStroke));
      // 4 mounting holes
      var hy1 = topEdge + vEdge;
      var hy2 = botEdge - vEdge;
      var hxL = cx - hSpacing / 2;
      var hxR = cx + hSpacing / 2;
      parent.appendChild(svgCircle(hxL, hy1, 0.6, { fill: '#1a1a1a' }));
      parent.appendChild(svgCircle(hxR, hy1, 0.6, { fill: '#1a1a1a' }));
      parent.appendChild(svgCircle(hxL, hy2, 0.6, { fill: '#1a1a1a' }));
      parent.appendChild(svgCircle(hxR, hy2, 0.6, { fill: '#1a1a1a' }));
      // Per-plate dim callouts on every backplate (matches the D-Airblade):
      // 280 width below + 150 height on the left + 70 to bench bottom.
      parent.appendChild(svgLine(x, botEdge, x + bpW, botEdge, STYLE.dimLine));
      drawDimensionLine(parent, x, botEdge + 3, x + bpW, botEdge + 3, formatMm(DRYER_BACKPLATE_W));
      drawDimensionLine(parent, x - 2, topEdge, x - 2, botEdge, formatMm(DRYER_BACKPLATE_H));
      drawDimensionLine(parent, x - 2, botEdge, x - 2, bvY + bvH, formatMm(DRYER_BACKPLATE_BOTTOM_OFFSET));
    }
  }

  // Fills in the MIDDLE of the back-view top dim row, between the left and right
  // corner brackets, so the row reads as one continuous chain:
  //   20 · 65 · gap · 280 · gap · 280 · gap · 65 · 20
  // The corner (20/65) segments are drawn by the render path; this helper adds
  // the gap → backplate(280) → gap → … segments at the same dim Y. `leftEndMm`
  // is the right edge of the left corner (bench-mm), `rightStartMm` the left edge
  // of the right corner.
  function drawDryerBackplateTopChain(parent, design, feX, bvY, scalePlan, tapBenchOffsets, benchLengthMm, dimY, leftEndMm, rightStartMm) {
    var dryerCfg = getDryerConfig(design);
    if (!dryerCfg) return;
    var offsets = computeSoapBenchOffsets(dryerCfg, tapBenchOffsets, benchLengthMm);
    if (!offsets.length) return;
    var bpCenters = computeDryerBackplatePositions(dryerCfg, offsets, benchLengthMm);
    if (!bpCenters.length) return;
    var half = DRYER_BACKPLATE_W / 2;
    var stops = [leftEndMm];
    bpCenters.slice().sort(function (a, b) { return a - b; }).forEach(function (c) {
      stops.push(c - half, c + half);
    });
    stops.push(rightStartMm);
    stops.sort(function (a, b) { return a - b; });
    // Dedupe near-coincident stops so we don't emit zero-length segments.
    var clean = [];
    for (var i = 0; i < stops.length; i++) {
      if (i === 0 || stops[i] - clean[clean.length - 1] > 0.5) clean.push(stops[i]);
    }
    for (var j = 1; j < clean.length; j++) {
      drawDimensionLine(parent,
        feX + clean[j - 1] * scalePlan, dimY,
        feX + clean[j] * scalePlan, dimY,
        formatMm(clean[j] - clean[j - 1]));
    }
  }

  // One-call dispatcher for the plan-view dryer drawing. Reuses the soap
  // offset + dim-chain helpers (the config shape is identical). `dryerDimY`
  // places the chained dim row; pass null to skip it (tight layouts).
  function drawDryerHolePlan(parent, design, bx, by, bw, bh, scalePlan, basinY, tapBenchOffsets, benchLengthMm, dryerDimY) {
    var dryerCfg = getDryerConfig(design);
    if (!dryerCfg) return null;
    var offsets = computeSoapBenchOffsets(dryerCfg, tapBenchOffsets, benchLengthMm);
    if (!offsets.length) return null;
    var dryerY = basinY - dryerCfg.distFromBasin * scalePlan;
    // Backplates first (so the holes + leaders draw on top) — only on basins
    // ≥ 300mm tall. The dryer holes themselves draw at any height.
    if (dryerHasBackplate(design)) {
      var bpCenters = computeDryerBackplatePositions(dryerCfg, offsets, benchLengthMm);
      drawDryerBackplates(parent, bx, by, scalePlan, dryerY, offsets, bpCenters);
    }
    drawDryerHoles(parent, bx, scalePlan, dryerY, offsets, dryerCfg.diameter);
    if (dryerDimY != null) {
      drawSoapDimChain(parent, bx, bw, scalePlan, dryerDimY, offsets);
    }
    // distFromBasin dim is no longer drawn here — it's handled collectively by
    // drawBackInsetOffsetDims (right-side stack, dedup'd with tap + soap).
    return dryerY;
  }

  // Bowl wall profile in the front view + side cross-section.
  //   'square' — straight walls with a small radius on the FLOOR corners only
  //              (CA08). Nearly a box.
  //   default  — the deep curved bowl the rest of the Smoothe range uses, or the
  //              shallow near-straight Kreno wall when `shallowBowl` is set.
  function isSquareBowl(mould) { return !!mould && mould.bowlProfile === 'square'; }
  // 'dish' (CA12) — a continuous curved bowl: the wall turns down through a tight
  // radius just inside the rim, then sweeps out to a broad, gently dished floor.
  // No straight vertical walls (the default Smoothe front view) and no flat taper
  // facets. FRONT VIEW ONLY — these moulds already draw a curved side cross-section,
  // so the side-view branches deliberately don't test for it.
  function isDishBowl(mould) { return !!mould && mould.bowlProfile === 'dish'; }
  // Does this mould have an overflow slot + channel? Smoothe and Kreno do by
  // default; a mould can opt OUT with `overflow: false` (CA13 — its drawings show
  // no slot). Kept separate from `showBowl`, which decides whether the full
  // bowl+waste front view is drawn at all — CA13 still needs that.
  function hasOverflow(mould) {
    if (!mould) return false;
    if (mould.overflow === false) return false;
    return mould.brand === 'smoothe' || mould.overflow === true;
  }
  // Roll-off lip radius (mm) at the bowl rim — the top face turns down through
  // this radius before the wall descends. 0 / absent = a hard rim edge, which is
  // what every mould drew before CA13.
  function rimRollMm(mould) {
    var v = mould && mould.rimRoll;
    return (typeof v === 'number' && v > 0) ? v : 0;
  }
  // The two views can disagree, and on CA15/CA16 they do: Q wants the roll-off lip on
  // the FRONT view but NOT on the side section, and the side bowl a little deeper than
  // the front. So both the roll and the rim angle take an optional side-only override:
  //   dish: { rollSide: 0 }          → no roll in the side section (front keeps rimRoll)
  //   dish: { angleSide: <degrees> } → side wall leaves the rim steeper/shallower than
  //                                    the front's rimAngle; bigger = deeper-reading
  // Both default to the front-view figure, so a mould that sets neither is unchanged.
  function rimRollSideMm(mould) {
    var v = dishDial(mould, 'rollSide', null);
    return (v === null) ? rimRollMm(mould) : (v > 0 ? v : 0);
  }
  function rimAngleSideRad(mould) {
    var a = dishDial(mould, 'angleSide', null);
    if (typeof a !== 'number' || isNaN(a) || a <= 0 || a >= 90) return rimAngleRad(mould);
    return a * Math.PI / 180;
  }

  // Radius on the bowl's floor corners, in mm. Only meaningful for 'square'
  // bowls; the curved profiles derive their own radius proportionally.
  var SQUARE_BOWL_FLOOR_RADIUS = 20;
  function bowlFloorRadiusMm(mould) {
    var r = mould && mould.bowlFloorRadius;
    return (typeof r === 'number' && !isNaN(r) && r >= 0) ? r : SQUARE_BOWL_FLOOR_RADIUS;
  }

  // Waste position across the bench WIDTH, as mm from the BASIN's back edge.
  //
  // Anchored to the basin edge, not the bench back edge, deliberately: the waste
  // is a fixed feature of the mould, so its distance from the basin edge is
  // constant while its distance from the BENCH back edge (E + this) moves
  // whenever E changes. Measuring from the bench edge would drag the waste around
  // inside the bowl every time the bench width changed. The spec sheet still
  // DISPLAYS the bench-edge figure (E + this) — it's just derived, not the datum.
  //
  // Defaults to the bowl centre when a mould doesn't declare its own, which is
  // where every mould sat before CA08 supplied a real dim (160, vs a 172.5
  // centre). `basinWidthMm` is the bowl width across the bench (mouldWidth, or
  // bowlWidth on the aproned counter-tops).
  function getWasteFromBasinEdge(mould, basinWidthMm) {
    var v = mould && mould.wasteFromBasinEdge;
    if (typeof v !== 'number' || isNaN(v) || v <= 0) return basinWidthMm / 2;
    // Never let a bad figure push the waste outside the bowl.
    return Math.min(v, basinWidthMm);
  }

  // Waste rosette. Simple circle for ramp basins; Ø35/Ø70/Ø45 nested for moulded.
  function drawWasteRosette(parent, cx, cy, family, scalePlan, mould) {
    if (family === 'moulded') {
      // TP range (3-ring waste): draw the circles at their TRUE diameters —
      // Ø68 outer / Ø51 middle / Ø45 inner.
      if (scalePlan && mould && mould.wasteMidDia) {
        parent.appendChild(svgCircle(cx, cy, mould.wasteTopDia / 2 * scalePlan, STYLE.bodyStroke));
        parent.appendChild(svgCircle(cx, cy, mould.wasteMidDia / 2 * scalePlan, STYLE.bodyStroke));
        parent.appendChild(svgCircle(cx, cy, mould.wasteBotDia / 2 * scalePlan, STYLE.bodyStroke));
        return;
      }
      parent.appendChild(svgCircle(cx, cy, 2.5, STYLE.bodyStroke));
      parent.appendChild(svgCircle(cx, cy, 1.6, STYLE.bodyStroke));
      parent.appendChild(svgCircle(cx, cy, 1.0, STYLE.bodyStroke));
    } else {
      parent.appendChild(svgCircle(cx, cy, 1.5, STYLE.bodyStroke));
      parent.appendChild(svgCircle(cx, cy, 0.7, STYLE.bodyStroke));
    }
  }

  // Bracket positions along the bottom view.
  // positions: array of mm offsets from left, OR null for deferred-note rendering.
  function drawBracketDots(parent, x0, y0, length, scale, positions) {
    if (positions == null) {
      // Deferred — show note centred where the bracket dots would have been
      var cx = x0 + (length * scale) / 2;
      var t = svgText(
        'Bracket layout to be confirmed by manufacturer at production',
        cx, y0,
        Object.assign({}, STYLE.dimText, { 'font-style': 'italic', fill: '#555' })
      );
      t.setAttribute('dominant-baseline', 'middle');
      parent.appendChild(t);
      return;
    }
    for (var i = 0; i < positions.length; i++) {
      var x = x0 + positions[i] * scale;
      parent.appendChild(svgCircle(x, y0, 0.4, { fill: '#1a1a1a' }));
      parent.appendChild(svgLine(x, y0 - 1.5, x, y0 + 1.5, STYLE.dimLine));
    }
  }

  // Stubs for variants that need them later. Left as no-ops in v1.
  function drawBracketRects(/* parent, positions, w, h */) { /* TODO: D-Airblade */ }
  function drawDryerMountCutouts(/* parent, positions */) { /* TODO: D-Airblade */ }

  // Basin opening — rectangular for ramp/trough, rounded for moulded.
  // radiusPx (optional, scaled px) overrides the proportional corner radius —
  // used for moulds with an explicit cornerRadius (e.g. Kreno R55).
  function drawBasinCutout(parent, x, y, w, h, family, radiusPx, shape) {
    if (family !== 'moulded') {
      parent.appendChild(svgRect(x, y, w, h, STYLE.bodyStroke));
      return;
    }
    // Ellipse basin (e.g. Grandios SN026) — full ellipse opening.
    if (shape === 'ellipse') {
      parent.appendChild(svgEl('ellipse', Object.assign(
        { cx: x + w / 2, cy: y + h / 2, rx: w / 2, ry: h / 2 }, STYLE.bodyStroke)));
      return;
    }
    // round (circle on a square face) + stadium (straight long sides + full
    // semicircular ends) both use radius = half the SHORT side. rounded uses its
    // cornerRadius; anything else the proportional fallback.
    var r;
    if (shape === 'round' || shape === 'stadium') {
      r = Math.min(w, h) / 2;
    } else if (radiusPx != null) {
      r = Math.min(radiusPx, Math.min(w, h) / 2);
    } else {
      r = Math.min(w, h) * 0.35;
    }
    parent.appendChild(svgRect(x, y, w, h, Object.assign({}, STYLE.bodyStroke, { rx: r, ry: r })));
  }

  // Bench top outline for the moulded plan view. `corners`:
  //   'all'   → all four corners rounded
  //   'front' → only the two FRONT-edge corners (bottom of the plan) rounded
  //   else    → square (Kreno / no rounding)
  // The front of the bench is the front-inset (G) side, drawn at the BOTTOM of
  // the plan view, so 'front' rounds the bottom-left + bottom-right corners.
  function drawBenchTopOutline(parent, x, y, w, h, corners) {
    var r = Math.min(Math.min(w, h) * 0.12, 6); // modest hint radius
    if (corners === 'all') {
      parent.appendChild(svgRect(x, y, w, h, Object.assign({}, STYLE.bodyStrokeBold, { rx: r, ry: r })));
      return;
    }
    if (corners === 'front') {
      var p = 'M ' + x + ' ' + y +
              ' L ' + (x + w) + ' ' + y +
              ' L ' + (x + w) + ' ' + (y + h - r) +
              ' Q ' + (x + w) + ' ' + (y + h) + ' ' + (x + w - r) + ' ' + (y + h) +
              ' L ' + (x + r) + ' ' + (y + h) +
              ' Q ' + x + ' ' + (y + h) + ' ' + x + ' ' + (y + h - r) +
              ' Z';
      parent.appendChild(svgPath(p, STYLE.bodyStrokeBold));
      return;
    }
    parent.appendChild(svgRect(x, y, w, h, STYLE.bodyStrokeBold));
  }

  // ─────────────────────────────────────────────────────────────
  // Shared chrome
  // ─────────────────────────────────────────────────────────────

  function renderHeader(parent, design) {
    // Prefer the dynamic, height-aware name from the builder when present;
    // fall back to the static label map for legacy / hand-crafted links.
    var modelLabel = design.modelName ||
      MODEL_LABELS[design.model] ||
      (design.builder === 'benchtop' ? 'Custom Benchtop' : (design.model || 'Custom'));
    var titleStr = modelLabel + (design.length ? ' ' + formatMm(design.length) + 'mm' : '');
    var sizeStr  = 'Size: ' + formatMm(design.length) + ' x ' + formatMm(design.width) +
                   (design.height ? ' x ' + formatMm(design.height) : '') + 'mm';

    var g = svgG({ class: 'header' });

    // Brand logo — Phase 6: use actual PNG asset injected via window.SPEC_SHEET_ASSETS.
    // Placed top-left. Falls back to text wordmark if asset URL not available.
    var _assets = window.SPEC_SHEET_ASSETS || {};
    if (_assets.rampLogo) {
      var _logoImg = svgEl('image');
      _logoImg.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', _assets.rampLogo);
      _logoImg.setAttribute('href', _assets.rampLogo);
      _logoImg.setAttribute('x', String(PAGE_MARGIN));
      _logoImg.setAttribute('y', '5');
      _logoImg.setAttribute('width', '42');
      _logoImg.setAttribute('height', '14');
      _logoImg.setAttribute('preserveAspectRatio', 'xMinYMid meet');
      g.appendChild(_logoImg);
    } else {
      g.appendChild(svgText('The', PAGE_MARGIN, 11, {
        'font-family': 'Times New Roman, serif', 'font-size': 4.5, fill: '#1a1a1a'
      }));
      g.appendChild(svgText('ramp', PAGE_MARGIN + 7.5, 11, {
        'font-family': 'Times New Roman, serif', 'font-size': 6.5, 'font-weight': 'bold', fill: '#1a1a1a'
      }));
      g.appendChild(svgText('Basin', PAGE_MARGIN + 22, 11, {
        'font-family': 'Times New Roman, serif', 'font-size': 4.5, fill: '#1a1a1a'
      }));
      g.appendChild(svgText('Collection', PAGE_MARGIN + 5, 15.5, {
        'font-family': 'Times New Roman, serif', 'font-size': 2.6, fill: '#1a1a1a'
      }));
    }

    // Title block (right-aligned)
    var titleX = PAGE_W - PAGE_MARGIN;
    g.appendChild(svgText(titleStr, titleX, 10, { 'font-size': 5.5, 'text-anchor': 'end', fill: '#1a1a1a' }));
    g.appendChild(svgText(sizeStr, titleX, 14.5, { 'font-size': 3, 'text-anchor': 'end', fill: '#1a1a1a' }));
    if (design.project) {
      g.appendChild(svgText('Project: ' + design.project, titleX, 18,
        { 'font-size': 2.4, 'text-anchor': 'end', fill: '#555' }));
    }
    if (design.id) {
      g.appendChild(svgText('Ref: ' + design.id + (design.date ? '   ·   ' + design.date : ''), titleX, 20.8,
        { 'font-size': 2.1, 'text-anchor': 'end', fill: '#444' }));
    }

    parent.appendChild(g);
  }

  function renderRightInfoPanel(parent, design) {
    var family = (MODEL_REGISTRY[design.model] && MODEL_REGISTRY[design.model].family) ||
                 (design.builder === 'benchtop' ? 'benchtop' : 'classic');
    var netWeight = estimateNetWeight(family, design.length || 1000, design);
    var x = PAGE_W - PAGE_MARGIN - INFO_PANEL_W;
    var y = HEADER_H + 6;    // was +2 — extra 4mm gap below the Ref: header line
    var lineH = 3.6;          // was 3.0 — looser leading to match larger label font
    var sectionGap = 2.5;    // was 2.0

    var g = svgG({ class: 'info-panel' });

    function addLine(text, attrs) {
      g.appendChild(svgText(text, x, y, Object.assign({}, STYLE.label, attrs || {})));
      y += lineH;
    }
    function addHeading(text) {
      addLine(text, { 'font-weight': 'bold' });
    }
    function addSpacer() { y += sectionGap; }

    if (netWeight) {
      addHeading('Net Weight:');
      addLine('Approximately ' + netWeight + 'kg');
      addSpacer();
    }

    addHeading('Unit:');
    addLine(STATIC_INFO.unit);
    addSpacer();

    addHeading('Composition:');
    var finish = design.finish || 'Matte';
    var color = design.color || 'White';
    addLine(finish + ' ' + color + ' (Coatex Layered)');
    // Bench composition — per-design for Grandios/Ferra; legacy → Stoneresin.
    var _compLabels = { sintered: 'Sintered Stone', natural: 'Natural Stone', stoneresin: 'Stoneresin', alumiglaze: 'Alumiglaze', none: 'No bench (counter-top basin)' };
    addLine((design.composition && _compLabels[design.composition]) ? _compLabels[design.composition] : STATIC_INFO.composition);
    addSpacer();

    addHeading('Selected Options:');
    addLine('- Finish: ' + finish + ' (Coatex Layered)');
    addLine('- Colour: ' + color);
    // Multi-basin Ramp Classic stores H (taps) and W (wastes) PER BASIN; the
    // spec sheet reports the TOTAL across all basins with a per-basin note
    // (e.g. "3 (1 per basin)"). Moulded/other builders already store totals in
    // design.h / design.w, so the multiply is scoped to ramp classic models.
    var _specRampCount   = (design.model === 'doubleC100') ? 2 : (parseInt(design.count, 10) || 1);
    var _specIsMultiRamp = (design.model === 'classicC100' || design.model === 'doubleC100') && _specRampCount > 1;
    function _specPerBasinTotal(perValue) {
      var per = parseInt(perValue, 10) || 0;
      return (per * _specRampCount) + ' (' + per + ' per basin)';
    }
    if (design.h !== undefined && design.h !== null && design.h !== '') {
      var _tapsDisplay = _specIsMultiRamp ? _specPerBasinTotal(design.h) : design.h;
      // Wall-mounted taps aren't drilled into the bench — report the count but
      // note there are no holes.
      if (design.wallTaps == 1 || design.wallTaps === '1') {
        addLine('- Taps (wall-mounted): ' + _tapsDisplay + ' — no holes in bench');
      } else {
        addLine('- Tap holes: ' + _tapsDisplay);
      }
    }
    // Soap dispenser holes — count derived from H per the configurator rule
    // (H=1 → 1, H≥2 → H-1).
    var _soapCfg = getSoapConfig(design);
    if (_soapCfg) {
      addLine('- Soap dispenser holes: ' + _soapCfg.count);
    }
    // Dryer holes — count derived from H + ratio; backplate behind each.
    var _dryerCfg = getDryerConfig(design);
    if (_dryerCfg) {
      addLine('- Dryer holes: ' + _dryerCfg.count);
      // Only report the backplate when one is actually drawn — the plate is
      // dropped below DRYER_BACKPLATE_MIN_HEIGHT (300mm), so a shorter basin was
      // advertising a backplate that isn't on the drawing.
      if (dryerHasBackplate(design)) {
        addLine('- Dryer backplate: ' + _dryerCfg.backplateOffset + 'mm ' + _dryerCfg.backplateSide + ' of hole');
      }
    }
    if (design.w !== undefined && design.w !== null && design.w !== '') {
      addLine('- Wastes: ' + (_specIsMultiRamp ? _specPerBasinTotal(design.w) : design.w));
    }
    // Waste placement — every model in WASTE_PLACEMENT_MODELS (was dryer-family
    // only before 2026-07-30).
    var _hasWastePlacement = hasWastePlacement(design);
    if (_hasWastePlacement && parseInt(design.w, 10) > 1 && typeof design.wasteGap === 'number' && !isNaN(design.wasteGap)) {
      addLine('- Waste gap: ' + design.wasteGap + 'mm' + (_specIsMultiRamp ? ', within each basin' : ''));
    }
    if (_hasWastePlacement && typeof design.wasteOffset === 'number' && !isNaN(design.wasteOffset) && design.wasteOffset !== 0) {
      // Direction carried by an R / L prefix on the magnitude — e.g. "R+100mm".
      var _wdir = design.wasteOffset > 0 ? 'R' : 'L';
      addLine('- Centre waste fine-tune: ' + _wdir + '+' + Math.abs(design.wasteOffset) + 'mm');
    }
    // The D-Airblade structural backplate is specific to the D-Airblade model
    // (the Xlerator shares this path but has no such backplate).
    if (design.model === 'dAirblade400') {
      if (design.backplateOffsets) {
        addLine('- Backplate offsets: ' + String(design.backplateOffsets).replace(/,/g, ', ') + 'mm from tap');
      } else if (typeof design.backplateOffset === 'number' && !isNaN(design.backplateOffset)) {
        var _bpSide = design.backplateSide === 'right' ? 'right' : 'left';
        addLine('- Backplate offset: ' + design.backplateOffset + 'mm ' + _bpSide + ' of tap');
      }
    }
    // Gated 2026-07-30 — this line used to print for ANY model, so a hand-built
    // URL could advertise per-waste offsets on a model whose drawing ignores
    // them (trough150, moulded, benchtop).
    if (_hasWastePlacement && design.wasteOffsets) {
      addLine('- Waste offsets: ' + String(design.wasteOffsets).replace(/,/g, ', ') + 'mm');
    }
    if (design.basinPosition && design.basinPosition !== 'center') {
      addLine('- Basin position: ' + design.basinPosition.charAt(0).toUpperCase() + design.basinPosition.slice(1));
    }
    if (design.gap !== undefined && design.gap !== null && design.gap !== '' && parseFloat(design.gap) > 0) {
      addLine('- Gap: ' + design.gap + 'mm');
    }
    addSpacer();

    addHeading('Notes:');
    for (var i = 0; i < STATIC_INFO.notes.length; i++) {
      addLine('- ' + STATIC_INFO.notes[i]);
    }

    parent.appendChild(g);
    return y; // final y after last line — render paths use this to position
              // the side cross-section below the info panel (amendment #1).
  }

  // View title — "Top view", "Front view", "Back view", etc. Placed just to the
  // right of the header logo's right edge so the label sits in the LEFT column
  // without overlapping the brand mark. Same bold style as the existing
  // "Side view (cross-section)" title in the right column.
  // Logo right edge ≈ PAGE_MARGIN (8) + logo width (42) = 50mm. +3mm gap → x=53.
  function drawViewLabel(parent, text, y) {
    var x = PAGE_MARGIN + 42 + 3;
    parent.appendChild(svgText(text, x, y,
      Object.assign({}, STYLE.label, { 'font-weight': 'bold' })));
  }

  // Vertical view label placed to the LEFT of a view, rotated 90° anti-clockwise
  // (reads bottom-to-top) and centred on the view's height. It sits far enough
  // left of the diagram's left edge to leave room for up to TWO stacked dim
  // lines (at xLeftEdge-4 and xLeftEdge-8). Used for the Front + Back views so
  // their titles no longer sit in the tight gap above each diagram — this cuts
  // the clutter between stacked views on the denser spec-sheet renders.
  var VIEW_LABEL_LEFT_OFFSET = 12; // mm to the left of the diagram edge
  function drawViewLabelLeft(parent, text, xLeftEdge, viewTopY, viewH) {
    var x = xLeftEdge - VIEW_LABEL_LEFT_OFFSET;
    var y = viewTopY + viewH / 2;
    parent.appendChild(svgText(text, x, y, Object.assign({}, STYLE.label, {
      'font-weight': 'bold',
      'text-anchor': 'middle',
      transform: 'rotate(-90 ' + x + ' ' + y + ')'
    })));
  }

  function renderFooterContacts(parent) {
    var g = svgG({ class: 'footer' });
    var yTop = PAGE_H - FOOTER_H + 2;
    var colW = 60;
    var startX = PAGE_MARGIN;

    for (var i = 0; i < CONTACT_BLOCKS.length; i++) {
      var blk = CONTACT_BLOCKS[i];
      var cx = startX + i * colW;
      g.appendChild(svgText(blk.heading, cx, yTop, {
        'font-size': 2.3, 'font-weight': 'bold', fill: '#1a1a1a'
      }));
      for (var li = 0; li < blk.lines.length; li++) {
        g.appendChild(svgText(blk.lines[li], cx, yTop + (li + 1) * 2.6, {
          'font-size': 2.0, fill: '#1a1a1a'
        }));
      }
    }

    // Stonebaths logo — Phase 6: use actual PNG asset, bottom-right.
    // Falls back to text wordmark if asset URL not available.
    var _sbAssets = window.SPEC_SHEET_ASSETS || {};
    var _sbX = PAGE_W - PAGE_MARGIN;
    if (_sbAssets.stonebathsLogo) {
      var _sbLogoW = 38;
      var _sbLogoH = 12;
      var _sbImg = svgEl('image');
      _sbImg.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', _sbAssets.stonebathsLogo);
      _sbImg.setAttribute('href', _sbAssets.stonebathsLogo);
      _sbImg.setAttribute('x', String(_sbX - _sbLogoW));
      _sbImg.setAttribute('y', String(yTop + 1.5));
      _sbImg.setAttribute('width', String(_sbLogoW));
      _sbImg.setAttribute('height', String(_sbLogoH));
      _sbImg.setAttribute('preserveAspectRatio', 'xMaxYMid meet');
      g.appendChild(_sbImg);
    } else {
      g.appendChild(svgText('stonebaths', _sbX, yTop + 5, {
        'font-size': 5, 'text-anchor': 'end', 'font-weight': 'bold', fill: '#1a1a1a'
      }));
      g.appendChild(svgText('Industry leaders since 2004', _sbX, yTop + 8.5, {
        'font-size': 2.0, 'text-anchor': 'end', fill: '#555'
      }));
    }

    parent.appendChild(g);
  }

  // The static install-guide page sits below the SVG drawing in the DOM.
  // Phase 6: embed the actual Installation Guide.pdf from assets.
  function renderInstallGuidePage(container) {
    var wrap = document.createElement('div');
    wrap.className = 'spec-sheet-install-guide';
    // Page-break so print/PDF puts install guide on page 2
    wrap.style.pageBreakBefore = 'always';
    wrap.style.breakBefore     = 'page';
    wrap.style.margin          = '0';
    wrap.style.padding         = '0';
    wrap.style.background      = '#fff';

    var guideUrl = (window.SPEC_SHEET_ASSETS || {}).installGuide;
    if (guideUrl) {
      // Display the installation guide PNG at A4 landscape size
      wrap.innerHTML =
        '<div style="width:297mm; height:210mm; overflow:hidden; margin:0 auto; background:#fff;">' +
          '<img src="' + guideUrl + '" ' +
            'style="width:297mm; height:210mm; display:block; object-fit:contain;" ' +
            'alt="Wall Hung Basin Installation Guide">' +
        '</div>';
    } else {
      // Fallback placeholder (asset URL not injected)
      wrap.style.marginTop  = '20mm';
      wrap.style.fontFamily = 'Arial, Helvetica, sans-serif';
      wrap.innerHTML =
        '<div style="text-align:center; padding:20mm 10mm; border:1px dashed #ccc; max-width:277mm; margin:0 auto;">' +
          '<h2 style="font-size:18pt; margin:0 0 8mm;">Wall Hung Basin Installation Guide</h2>' +
          '<p style="font-size:10pt; color:#666; margin:0;">Installation guide asset not available.</p>' +
        '</div>';
    }
    container.appendChild(wrap);
  }

  // ─────────────────────────────────────────────────────────────
  // Render path: Benchtop (simplest — validates the scaffold)
  // ─────────────────────────────────────────────────────────────

  function renderBenchtop(design) {
    var svg = createSpecSheetSvg();
    renderHeader(svg, design);
    renderRightInfoPanel(svg, design);
    renderFooterContacts(svg);

    var len = parseFloat(design.length) || 1000;
    var wid = parseFloat(design.width)  || 460;
    var hgt = parseFloat(design.height) || 170;

    // Top plan view
    var planArea = { x: DRAW_AREA.x + 14, y: DRAW_AREA.y + 12, w: DRAW_AREA.w - 30, h: 55 };
    var scalePlan = Math.min(planArea.w / len, planArea.h / wid);

    var bx = planArea.x;
    var by = planArea.y;
    var bw = len * scalePlan;
    var bh = wid * scalePlan;

    // Bench outline (plan)
    drawViewLabel(svg, 'Top view', by - 12);
    svg.appendChild(svgRect(bx, by, bw, bh, STYLE.bodyStrokeBold));
    drawDimensionLine(svg, bx, by - 6, bx + bw, by - 6, formatMm(len));
    drawDimensionLine(svg, bx - 4, by, bx - 4, by + bh, formatMm(wid));

    // Side elevation (length × height) — gap widened for thin slabs to use the
    // empty page space; falls back to the original tight gap on tall benchtops.
    var benchtopGap = hgt > 200 ? 14 : 22;
    var sideY = by + bh + benchtopGap;
    var sideH = hgt * scalePlan;
    drawViewLabel(svg, 'Side view', sideY - 8);
    svg.appendChild(svgRect(bx, sideY, bw, sideH, STYLE.bodyStrokeBold));
    drawDimensionLine(svg, bx, sideY - 4, bx + bw, sideY - 4, formatMm(len));
    drawDimensionLine(svg, bx + bw + 4, sideY, bx + bw + 4, sideY + sideH, formatMm(hgt));

    // Front elevation (width × height), centred below
    var feY = sideY + sideH + benchtopGap;
    var fX = bx + (bw - bh) / 2;
    drawViewLabelLeft(svg, 'Front view', fX, feY, sideH);
    svg.appendChild(svgRect(fX, feY, bh, sideH, STYLE.bodyStrokeBold));
    drawDimensionLine(svg, fX, feY - 4, fX + bh, feY - 4, formatMm(wid));
    drawDimensionLine(svg, fX + bh + 4, feY, fX + bh + 4, feY + sideH, formatMm(hgt));

    return svg;
  }

  // Shared Ramp Classic-style BACK ELEVATION ("bottom view"): corner bracket-
  // pocket notches (65mm wide × full height, 20mm from side + top, open to the
  // base) and bracket bolt holes at their proper vertical height, plus the
  // top / left / bottom dimension callouts. Used by the Classic/Junior/
  // Commercial path and by the Xlerator (which mounts the same way). Pass the
  // bracket positions (mm from the left edge) — or null/empty to render the
  // deferred-bracket fallback. Caller draws the "Back view" label + any dryer
  // backplate; this only draws the notches, holes, and dims.
  function drawRampClassicBackView(parent, feX, feW, bvY, bvH, scalePlan, len, hgt, bracketPositions) {
    if (bracketPositions && bracketPositions.length > 0) {
      // ── Geometry ──
      var cornerW       = 65 * scalePlan;
      var cornerOffX_mm = 20;             // 20mm from each side edge
      var cornerOffY_mm = 20;             // 20mm from the top edge
      var cornerOffX    = cornerOffX_mm * scalePlan;
      var cornerOffY    = cornerOffY_mm * scalePlan;
      var lcX = feX + cornerOffX;
      var rcX = feX + feW - cornerOffX - cornerW;
      var cornerTopY = bvY + cornerOffY;
      var baseY = bvY + bvH;
      var bracketYmm = getBracketHeight(hgt);
      var bracketY = bvY + bracketYmm * scalePlan;

      // ── Outer border ──
      parent.appendChild(svgLine(feX, bvY, feX + feW, bvY, STYLE.bodyStrokeBold));
      parent.appendChild(svgLine(feX, bvY, feX, baseY, STYLE.bodyStrokeBold));
      parent.appendChild(svgLine(feX + feW, bvY, feX + feW, baseY, STYLE.bodyStrokeBold));
      // Base drawn in three segments with 65mm gaps where the corner notches sit.
      parent.appendChild(svgLine(feX,           baseY, lcX,            baseY, STYLE.bodyStrokeBold));
      parent.appendChild(svgLine(lcX + cornerW, baseY, rcX,            baseY, STYLE.bodyStrokeBold));
      parent.appendChild(svgLine(rcX + cornerW, baseY, feX + feW,      baseY, STYLE.bodyStrokeBold));

      // ── Corner notches (3 sides, open to base) ──
      parent.appendChild(svgLine(lcX, cornerTopY, lcX + cornerW, cornerTopY, STYLE.bodyStroke));
      parent.appendChild(svgLine(lcX, cornerTopY, lcX, baseY,                STYLE.bodyStroke));
      parent.appendChild(svgLine(lcX + cornerW, cornerTopY, lcX + cornerW, baseY, STYLE.bodyStroke));
      parent.appendChild(svgLine(rcX, cornerTopY, rcX + cornerW, cornerTopY, STYLE.bodyStroke));
      parent.appendChild(svgLine(rcX, cornerTopY, rcX, baseY,                STYLE.bodyStroke));
      parent.appendChild(svgLine(rcX + cornerW, cornerTopY, rcX + cornerW, baseY, STYLE.bodyStroke));

      // ── Bracket holes ──
      for (var bi = 0; bi < bracketPositions.length; bi++) {
        var bx_pos = feX + bracketPositions[bi] * scalePlan;
        parent.appendChild(svgCircle(bx_pos, bracketY, 0.7, { fill: '#1a1a1a' }));
      }

      // ── Dimensions: above the back view ── 20 (edge→corner) and 65 (corner)
      var topDimY = bvY - 4;
      drawDimensionLine(parent, feX, topDimY, lcX, topDimY, formatMm(cornerOffX_mm));
      drawDimensionLine(parent, lcX, topDimY, lcX + cornerW, topDimY, '65');
      drawDimensionLine(parent, rcX, topDimY, rcX + cornerW, topDimY, '65');
      drawDimensionLine(parent, rcX + cornerW, topDimY, feX + feW, topDimY, formatMm(cornerOffX_mm));

      // ── Dimensions: left of the back view ── 20mm top offset + bracket height
      drawDimensionLine(parent, feX - 4, bvY, feX - 4, cornerTopY, formatMm(cornerOffY_mm));
      drawDimensionLine(parent, feX - 9, bvY, feX - 9, bracketY, formatMm(bracketYmm));

      // ── Dimensions: below the back view ── edge → hole → … → edge + overall
      var spDimY = baseY + 4;
      var prevPos = 0;
      var spacings = [];
      for (var sp = 0; sp < bracketPositions.length; sp++) {
        spacings.push(bracketPositions[sp] - prevPos);
        prevPos = bracketPositions[sp];
      }
      spacings.push(len - prevPos);
      var runningX = feX;
      for (var spi = 0; spi < spacings.length; spi++) {
        var nextX = runningX + spacings[spi] * scalePlan;
        if (spacings[spi] >= 20) {
          drawDimensionLine(parent, runningX, spDimY, nextX, spDimY, formatMm(spacings[spi]));
        }
        runningX = nextX;
      }
      drawDimensionLine(parent, feX, spDimY + 4, feX + feW, spDimY + 4, formatMm(len));
    } else {
      // Fallback for families with no bracket SOP yet — simple outlined back
      // view with the deferred-bracket note.
      parent.appendChild(svgRect(feX, bvY, feW, Math.max(bvH, 10), STYLE.bodyStrokeBold));
      drawBracketDots(parent, feX, bvY + Math.max(bvH, 10) / 2, len, scalePlan, null);
      drawDimensionLine(parent, feX, bvY + Math.max(bvH, 10) + 4, feX + feW, bvY + Math.max(bvH, 10) + 4, formatMm(len));
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Render path: Ramp Classic (single basin, ramp profile)
  // Handles classicC100, junior100, commercial200, commercial400
  // ─────────────────────────────────────────────────────────────

  function renderRampClassic(design) {
    var svg = createSpecSheetSvg();
    renderHeader(svg, design);
    var infoPanelEndY = renderRightInfoPanel(svg, design);
    renderFooterContacts(svg);

    var entry = MODEL_REGISTRY[design.model] || MODEL_REGISTRY.classicC100;
    var family = entry.family;
    var constants = FAMILY_CONSTANTS[family];

    var len = parseFloat(design.length) || 1500;
    var wid = parseFloat(design.width)  || entry.widthDefault || constants.defaultWidth;
    var hgt = parseFloat(design.height) || 100;
    var basinDepth = getBasinDepth(design.model, hgt);  // section depth (front elevation)
    var rampDrop   = getRampDrop(design.model, hgt);    // ramp diagonal depth (side cross-section)
    var backInset = getBackInset(design, constants); // customer-set E (50–145), default 110 for classic
    var frontInset = parseFloat(design.g) || 20;     // G inset, 20 or 40
    var basinWidth = Math.max(20, wid - backInset - frontInset);
    var dInset = parseFloat(design.d) || 100;
    var basinLengthDefault = Math.max(20, len - dInset * 2);

    // Centre / Left / Right
    var pos = design.basinPosition || 'center';
    var basinLeftOffsetMm;
    var actualBasinLength;
    if (pos === 'left' && design.basinEdge && design.basinLength) {
      var be = parseFloat(design.basinEdge);
      var bl = parseFloat(design.basinLength);
      basinLeftOffsetMm = Math.max(0, be - bl);
      actualBasinLength = bl;
    } else if (pos === 'right' && design.basinEdge && design.basinLength) {
      var be2 = parseFloat(design.basinEdge);
      basinLeftOffsetMm = Math.max(0, len - be2);
      actualBasinLength = parseFloat(design.basinLength);
    } else {
      basinLeftOffsetMm = dInset;
      actualBasinLength = basinLengthDefault;
    }

    // Layout zones — plan / front / bottom views expand into the full drawing
    // area now that the side section view has moved to the right column
    // (amendment #1).
    // Top view lifted ~20px (5 units) so the tap / soap / dryer / waste dim
    // chains hanging below it clear the "Front view" title. planToFrontGap is
    // bumped by the same 5 below, so the front view block holds its position.
    var planArea = { x: DRAW_AREA.x + 14, y: DRAW_AREA.y + 7, w: DRAW_AREA.w - 18, h: 50 };
    var scalePlan = Math.min(planArea.w / len, planArea.h / wid);

    // ===== TOP PLAN VIEW =====
    var bx = planArea.x;
    var by = planArea.y;
    var bw = len * scalePlan;
    var bh = wid * scalePlan;

    drawViewLabel(svg, 'Top view', by - 12);
    svg.appendChild(svgRect(bx, by, bw, bh, STYLE.bodyStrokeBold));

    var basinX = bx + basinLeftOffsetMm * scalePlan;
    var basinY = by + backInset * scalePlan;
    var basinW = actualBasinLength * scalePlan;
    var basinH = basinWidth * scalePlan;
    drawBasinCutout(svg, basinX, basinY, basinW, basinH, family);

    // Tap holes — distributed along the back-inset centerline, over the basin.
    // Uses the same algorithm as the configurator's renderTopFaceTaps so the
    // spec sheet matches the cube preview exactly.
    var tapCount = Math.max(0, parseInt(design.h, 10) || 0);
    var tapBenchOffsets = [];
    var tapDimY = by + bh + 3.5;
    if (tapCount > 0) {
      var tapsY = basinY - tapRowDist(design, backInset) * scalePlan;
      var tapXOffsets = computeTapXOffsets(actualBasinLength, tapCount, design.tapEdgeInset, design.model === 'junior100');
      for (var ti = 0; ti < tapXOffsets.length; ti++) {
        var tapX = basinX + tapXOffsets[ti] * scalePlan;
        drawTapHole(svg, tapX, tapsY, { showCallout: ti === 0 });
      }
      // Chained tap-position dim row — placed just below the plan-view bench
      // (above the front elevation) so the text has room without crowding the
      // basin opening. Reads as "below the tap holes" visually.
      tapBenchOffsets = tapXOffsets.map(function (off) {
        return basinLeftOffsetMm + off;
      });
      drawTapDimChain(svg, bx, bw, scalePlan, tapDimY, tapBenchOffsets);
    }

    // Soap dispenser holes — drawn at distFromBasin into the back-inset zone.
    // Position along the length is derived from the tap row per the
    // configurator rule (H=1 single offset, H≥2 midpoints). Adds its own
    // chained dim row just below the tap dim row.
    drawSoapDispenserPlan(svg, design, bx, by, bw, bh, scalePlan, basinY, tapBenchOffsets, len, tapDimY);

    // Dryer holes — backplate behind each, drawn after the soap row.
    drawDryerHolePlan(svg, design, bx, by, bw, bh, scalePlan, basinY, tapBenchOffsets, len,
      tapDimY + (getSoapConfig(design) ? 9 : 4.5));

    // distFromBasin dims for tap / soap / dryer rows — stacked at the right end
    // of the back-inset band, dedup'd (tap row offset = backInset/2).
    drawBackInsetOffsetDims(svg, design, bx, bw, scalePlan, basinY, tapBenchOffsets, len, tapRowDist(design, backInset));

    // Waste at the BACK of the basin (back edge of waste sits on the boundary
    // between back inset and basin opening, extending forward into the basin).
    // Double circle: Ø80 outer (waste cutout), Ø40 inner (drain pipe).
    var WASTE_OUTER_DIA = 80;
    var WASTE_INNER_DIA = 40;
    // Placement honours the shared waste rule (2026-07-30): even spacing about
    // the basin centre by `wasteGap`, centre fine-tune via `wasteOffset`, and
    // per-waste `wasteOffsets` nudges. With no params supplied the helper's
    // fallback gap (bowl / (count + 1)) reproduces the previous even spread
    // exactly, so legacy links are unchanged.
    var wasteCount = Math.max(0, parseInt(design.w, 10) || 1);
    var wasteCentresMm = [];
    if (wasteCount > 0) {
      var rOuter = (WASTE_OUTER_DIA / 2) * scalePlan;
      var rInner = (WASTE_INNER_DIA / 2) * scalePlan;
      wasteCentresMm = computeWasteCentres(
        design, tapBenchOffsets, basinLeftOffsetMm, actualBasinLength,
        { count: wasteCount, wasteDia: WASTE_OUTER_DIA });
      for (var wi = 0; wi < wasteCount; wi++) {
        var wCentreMm = wasteCentresMm[wi];
        var wcx = basinX + (wCentreMm - basinLeftOffsetMm) * scalePlan;
        var wcy = basinY + rOuter;     // back edge of outer circle on the boundary
        svg.appendChild(svgCircle(wcx, wcy, rOuter, STYLE.bodyStroke));
        svg.appendChild(svgCircle(wcx, wcy, rInner, STYLE.bodyStroke));
      }
    }

    // Waste-position dim chain — a chained row (edge → waste → … → edge) below
    // the tap / soap / dryer dim rows, so each waste's distance along the bench
    // reads. Rows stack at 4.5-unit intervals below the tap row (tapDimY):
    // tap → soap → dryer, then the waste row after whichever of those exist.
    if (wasteCentresMm.length) {
      var soapRows = getSoapConfig(design) ? 1 : 0;
      var dryerRows = getDryerConfig(design) ? 1 : 0;
      var wasteDimY = tapDimY + 4.5 * (1 + soapRows + dryerRows);
      drawTapDimChain(svg, bx, bw, scalePlan, wasteDimY, wasteCentresMm);
    }

    // Length dimensions (above bench)
    drawDimensionLine(svg, bx, by - 8, bx + bw, by - 8, formatMm(len));
    var leftPad = basinLeftOffsetMm;
    var rightPad = Math.max(0, len - basinLeftOffsetMm - actualBasinLength);
    drawDimensionLine(svg, bx, by - 3.5, basinX, by - 3.5, formatMm(leftPad));
    drawDimensionLine(svg, basinX, by - 3.5, basinX + basinW, by - 3.5, formatMm(actualBasinLength));
    drawDimensionLine(svg, basinX + basinW, by - 3.5, bx + bw, by - 3.5, formatMm(rightPad));

    // Width dimensions (left of bench)
    drawDimensionLine(svg, bx - 4, by, bx - 4, by + backInset * scalePlan, formatMm(backInset));
    drawDimensionLine(svg, bx - 4, by + backInset * scalePlan, bx - 4, by + (backInset + basinWidth) * scalePlan, formatMm(basinWidth));
    drawDimensionLine(svg, bx - 4, by + (backInset + basinWidth) * scalePlan, bx - 4, by + bh, formatMm(frontInset));
    drawDimensionLine(svg, bx - 9, by, bx - 9, by + bh, formatMm(wid));

    // ===== FRONT ELEVATION =====
    // Plan→front gap is widened for standard-height benches to use the
    // empty space lower on the page. Tall variants (Commercial 400, etc.)
    // keep the original tight gap because their front + back elevations
    // already consume the available vertical space.
    var planToFrontGap = hgt > 200 ? 21 : 31;
    var feY = planArea.y + planArea.h + planToFrontGap;
    var feX = bx;
    var feW = bw;
    var feH = hgt * scalePlan;
    // Length dim removed from the front view (already shown on the top + back
    // views, all left-aligned). With that row gone, lift the diagram ~15px
    // (4 units) into the freed space. The view title now sits vertically to the
    // LEFT of the diagram (drawViewLabelLeft) instead of above it.
    feY -= 4;
    drawViewLabelLeft(svg, 'Front view', feX, feY, feH);
    svg.appendChild(svgRect(feX, feY, feW, feH, STYLE.bodyStrokeBold));
    // Basin cutout (dashed/hidden line) — sits at the TOP of the bench in the
    // front elevation because that's where the basin opens. Drawn as deep as
    // the basin cavity (basinDepth from the bench top, leaving body material
    // below for taller variants like Commercial 400).
    var feBasinTop = feY + feH * 0.08;
    var feBasinH   = Math.min(basinDepth * scalePlan, feH * 0.85);
    svg.appendChild(svgRect(basinX, feBasinTop, basinW, feBasinH, STYLE.hiddenLine));
    drawDimensionLine(svg, feX - 4, feY, feX - 4, feY + feH, formatMm(hgt));

    // ===== BACK ELEVATION ("bottom view") =====
    // Looking at the rear face of the bench. Bench length runs horizontal,
    // bench height runs vertical (top of bench at top of view). Shows the
    // bracket-hole row at its proper vertical height plus corner notches
    // at each end (which extend full-height down to the bottom of the bench).
    var frontToBackGap = hgt > 200 ? 16 : 20;
    var bvY = feY + feH + frontToBackGap;
    var bvH = hgt * scalePlan;            // height-proportional (matches front elevation)
    drawViewLabelLeft(svg, 'Back view', feX, bvY, bvH);
    var bracketPositions = getBracketPositions(family, len);
    drawRampClassicBackView(svg, feX, feW, bvY, bvH, scalePlan, len, hgt, bracketPositions);

    // Dryer backplate(s) in the back view — same plate as the D-Airblade.
    // cornerEndMm = 85 (20mm edge + 65mm corner) so the top dim chain fills the
    // middle of the corner-bracket dim row.
    drawDryerBackplatesBackView(svg, design, feX, bvY, bvH, scalePlan, tapBenchOffsets, len, 85);

    // ===== SIDE CROSS-SECTION — right column, below info panel =====
    // Oriented like the reference PDFs: bench WIDTH runs vertically (with the
    // 110 | basinWidth | frontInset breakdown), bench HEIGHT runs horizontally,
    // bench TOP is on the LEFT (where the basin opens), bench BOTTOM on the RIGHT.
    // The ramp surface slopes diagonally inside the basin cavity — shallow at
    // the back (top of image), deeper at the front (bottom of image) where the
    // drain sits. The 20mm material shell traces the cavity boundary.

    var SECTION_PAD = 12;
    var sectionTitleY = infoPanelEndY + SECTION_PAD;
    var sx_origin = PAGE_W - PAGE_MARGIN - INFO_PANEL_W;

    svg.appendChild(svgText('Side view (cross-section)', sx_origin, sectionTitleY,
      Object.assign({}, STYLE.label, { 'font-weight': 'bold' })));

    // Available space in the right column. Cross-section is tall and narrow.
    var sAvailW = INFO_PANEL_W - 16;
    var sAvailH = PAGE_H - FOOTER_H - 8 - (sectionTitleY + 8);
    var sScale = Math.min(sAvailW / hgt, sAvailH / wid);

    var sBodyW = hgt * sScale;        // horizontal in image = bench height
    var sBodyH = wid * sScale;        // vertical in image = bench width
    var sx = sx_origin + 12;          // leave room on left for dimension labels
    var sy = sectionTitleY + 8;
    var t = MATERIAL_THICKNESS * sScale;

    // Outer body outline (bench profile, oriented "lying on its side")
    svg.appendChild(svgRect(sx, sy, sBodyW, sBodyH, STYLE.bodyStrokeBold));

    // Vertical regions of the cross-section (top→bottom of image):
    //   y=0          → back of bench (mounts to wall)
    //   y=backInset  → top of basin cavity (back wall ends)
    //   y=backInset + basinWidth → bottom of basin cavity (front wall starts)
    //   y=wid        → front of bench
    var cavityTopY = sy + backInset * sScale;
    var cavityBotY = sy + (backInset + basinWidth) * sScale;

    // ── Ramp surface ──
    // Starts at the basin FRONT (where the 20mm front wall is), at the bench-top
    // surface (x=sx, no depth into the bench), and drops diagonally to a depth
    // of rampDrop (60mm for Classic 100, 80mm for Commercial 200/400) at the
    // back, where the 110mm back wall begins.
    var rampFrontX = sx;
    var rampFrontY = cavityBotY;
    var rampBackX  = sx + rampDrop * sScale;
    var rampBackY  = cavityTopY;

    // Cavity outline (right triangle): bench-top opening on the left, back wall
    // lip on top, diagonal ramp surface on the hypotenuse.
    var cavityPath =
      'M ' + sx + ' ' + cavityTopY +              // back-top corner (basin opening at back)
      ' L ' + rampBackX + ' ' + rampBackY +       // along back wall lip to back of ramp
      ' L ' + rampFrontX + ' ' + rampFrontY +     // diagonal ramp down to front
      ' Z';                                       // close back to start (along bench-top opening)
    svg.appendChild(svgPath(cavityPath, STYLE.bodyStroke));

    // ── 20mm material shell ──
    // Back wall lip: hatched strip above the back ramp endpoint.
    drawHatching(svg, {
      x: sx,
      y: cavityTopY - t,
      w: rampDrop * sScale,
      h: t
    }, { spacing: 0.55 });
    // No hatching on the angled ramp itself (per amendment).
    // No front wall lip — the ramp meets the bench-top surface directly at the front.

    // ── Ramp drop callout ──
    // Horizontal dimension line just below the back wall lip showing the
    // ramp drop value (e.g. 60 for Classic 100, 80 for Commercial 200/400).
    drawDimensionLine(svg, sx, cavityTopY + 3, rampBackX, cavityTopY + 3, formatMm(rampDrop));

    // ── Waste indicator in cross-section ──
    // Two-step shape — 75mm wide × 40mm long from the ramp back end, then
    // narrows to 45mm wide × 20mm long. Applies to ALL classic-family
    // models (classicC100, junior100, commercial200, commercial400).
    // The 60mm total waste length is fixed — for taller benches (Commercial
    // 200/400) it does NOT extend the full bench height; bench material
    // continues below the waste.
    var WASTE_W1 = 75;
    var WASTE_L1 = 40;
    var WASTE_W2 = 45;
    var WASTE_L2 = 20;
    var wx1 = rampBackX;
    var wy1 = cavityTopY;
    var ww1 = WASTE_L1 * sScale;
    var wh1 = WASTE_W1 * sScale;
    svg.appendChild(svgRect(wx1, wy1, ww1, wh1, STYLE.bodyStroke));
    var wx2 = wx1 + ww1;
    var wy2 = wy1 + (WASTE_W1 - WASTE_W2) / 2 * sScale;
    var ww2 = WASTE_L2 * sScale;
    var wh2 = WASTE_W2 * sScale;
    svg.appendChild(svgRect(wx2, wy2, ww2, wh2, STYLE.bodyStroke));
    // Vertical width-dim lines on the RIGHT of the waste — same orientation
    // as the 460 total-width dim on the left, opposite side.
    var wasteRightEdge = wx2 + ww2;
    var dim45X = wasteRightEdge + 4;
    var dim75X = wasteRightEdge + 9;
    drawDimensionLine(svg, dim45X, wy2, dim45X, wy2 + wh2, formatMm(WASTE_W2));
    drawDimensionLine(svg, dim75X, wy1, dim75X, wy1 + wh1, formatMm(WASTE_W1));

    // ── Dimensions on the cross-section frame ──
    // Vertical breakdown on left side: backInset / basinWidth / frontInset / total
    var dimX = sx - 5;
    drawDimensionLine(svg, dimX, sy, dimX, sy + backInset * sScale, formatMm(backInset));
    drawDimensionLine(svg, dimX, sy + backInset * sScale, dimX, sy + (backInset + basinWidth) * sScale, formatMm(basinWidth));
    drawDimensionLine(svg, dimX, sy + (backInset + basinWidth) * sScale, dimX, sy + sBodyH, formatMm(frontInset));
    drawDimensionLine(svg, dimX - 5, sy, dimX - 5, sy + sBodyH, formatMm(wid));

    // Horizontal dimension on top — bench height
    drawDimensionLine(svg, sx, sy - 4, sx + sBodyW, sy - 4, formatMm(hgt));

    return svg;
  }

  // ─────────────────────────────────────────────────────────────
  // Render path stubs — implemented next session
  // ─────────────────────────────────────────────────────────────

  // Multi-basin Ramp Classic (Double / Triple / Quadruple) + legacy doubleC100.
  // Equal-split, uniform-width basins: D | basin | gap | … | basin | D. This is
  // the generalised former renderRampDouble — `count` drives the basin loop.
  function renderRampMulti(design) {
    var svg = createSpecSheetSvg();
    renderHeader(svg, design);
    var infoPanelEndY = renderRightInfoPanel(svg, design);
    renderFooterContacts(svg);

    var isLegacyDouble = design.model === 'doubleC100';
    // Deep cross-section + ramp drop follow the Classic model id for new designs.
    var modelForDepth = isLegacyDouble ? 'classicC100' : (design.model || 'classicC100');
    var entry = MODEL_REGISTRY.doubleC100; // shared 'classic-double' family chrome
    var family = entry.family;                       // 'classic-double'
    var constants = FAMILY_CONSTANTS[family];

    var len = parseFloat(design.length) || 1500;
    var wid = parseFloat(design.width)  || entry.widthDefault || constants.defaultWidth;
    var hgt = parseFloat(design.height) || 100;
    var basinDepth = getBasinDepth(modelForDepth, hgt);
    var rampDrop   = getRampDrop(modelForDepth, hgt);
    var backInset  = getBackInset(design, constants); // customer-set E (50–145), default 110
    var frontInset = parseFloat(design.g) || 20;
    var basinWidth = Math.max(20, wid - backInset - frontInset);
    var dInset = parseFloat(design.d) || 125;

    // Basin count: classicC100 carries `count` (2–4); legacy doubleC100 → 2.
    var count = isLegacyDouble ? 2 : Math.max(2, parseInt(design.count, 10) || 2);
    var gap = parseFloat(design.gap) || 200;

    // Equal-split, uniform-width basins (matches the builder's geometry).
    var basinLen = Math.max(20, (len - 2 * dInset - (count - 1) * gap) / count);
    var basinStarts = [];
    for (var bIdx = 0; bIdx < count; bIdx++) {
      basinStarts.push(dInset + bIdx * (basinLen + gap));
    }
    var rightD = Math.max(0, len - (basinStarts[count - 1] + basinLen));

    // ===== TOP PLAN VIEW =====
    // Top view lifted ~20px (5 units) so the tap / soap / dryer / waste dim
    // chains hanging below it clear the "Front view" title. planToFrontGap is
    // bumped by the same 5 below, so the front view block holds its position.
    var planArea = { x: DRAW_AREA.x + 14, y: DRAW_AREA.y + 7, w: DRAW_AREA.w - 18, h: 50 };
    var scalePlan = Math.min(planArea.w / len, planArea.h / wid);

    var bx = planArea.x;
    var by = planArea.y;
    var bw = len * scalePlan;
    var bh = wid * scalePlan;

    drawViewLabel(svg, 'Top view', by - 12);
    svg.appendChild(svgRect(bx, by, bw, bh, STYLE.bodyStrokeBold));

    var basinY = by + backInset * scalePlan;
    var basinH = basinWidth * scalePlan;
    var basinW = basinLen * scalePlan;
    // Per-basin plan-view X (px) for reuse by cutouts, taps, wastes, elevation.
    var basinXs = basinStarts.map(function (s) { return bx + s * scalePlan; });

    basinXs.forEach(function (bxi) {
      drawBasinCutout(svg, bxi, basinY, basinW, basinH, family);
    });

    // Tap holes — H is taps PER BASIN (uniform). Legacy doubleC100 stored h as a
    // total (2 = 1 per basin), so divide by count for that case only.
    var rawH = Math.max(0, parseInt(design.h, 10) || 0);
    var tapsPerBasin = isLegacyDouble ? Math.round(rawH / count) : rawH;
    var allTapBenchOffsets = [];
    var dblTapDimY = by + bh + 3.5;
    if (tapsPerBasin > 0) {
      var tapsY = basinY - tapRowDist(design, backInset) * scalePlan;
      var perBasinOffsets = computeTapXOffsets(basinLen, tapsPerBasin, design.tapEdgeInset);
      // Small triple/quad basins can be shorter than 2× the tap edge inset, so
      // computeTapXOffsets returns []. Fall back to one centred tap per basin
      // (matches the builder's computeBasinTapLayout fallback).
      if (perBasinOffsets.length === 0 && basinLen > 0) perBasinOffsets = [basinLen / 2];
      for (var bi2 = 0; bi2 < count; bi2++) {
        for (var ti = 0; ti < perBasinOffsets.length; ti++) {
          drawTapHole(svg, basinXs[bi2] + perBasinOffsets[ti] * scalePlan, tapsY,
            { showCallout: bi2 === 0 && ti === 0 });
          allTapBenchOffsets.push(basinStarts[bi2] + perBasinOffsets[ti]);
        }
      }
      // Chained tap-position dim row covering all basins.
      drawTapDimChain(svg, bx, bw, scalePlan, dblTapDimY, allTapBenchOffsets);
    }

    // Soap dispenser holes — for the double basin the configurator clamps H=2
    // (1 tap per basin) so the default soap layout is 1 dispenser at the
    // midpoint between the two taps (i.e., in the gap zone).
    drawSoapDispenserPlan(svg, design, bx, by, bw, bh, scalePlan, basinY, allTapBenchOffsets, len, dblTapDimY);

    // Dryer holes — backplate behind each.
    drawDryerHolePlan(svg, design, bx, by, bw, bh, scalePlan, basinY, allTapBenchOffsets, len,
      dblTapDimY + (getSoapConfig(design) ? 9 : 4.5));

    // distFromBasin dims for tap / soap / dryer rows — right-side stack, dedup'd.
    drawBackInsetOffsetDims(svg, design, bx, bw, scalePlan, basinY, allTapBenchOffsets, len, tapRowDist(design, backInset));

    // Wastes — W is wastes PER BASIN (default 1). Legacy doubleC100 stored w as
    // a total (2 = 1 per basin), so divide by count for that case only. Each
    // waste sits at the BACK of its basin (Ø80 outer back edge on the boundary).
    var WASTE_OUTER_DIA_D = 80;
    var WASTE_INNER_DIA_D = 40;
    var rawW = parseInt(design.w, 10);
    if (isNaN(rawW)) rawW = isLegacyDouble ? 2 : 1;
    var wastesPerBasin = isLegacyDouble ? Math.max(1, Math.round(rawW / count)) : Math.max(0, rawW);
    var rOuter_d = (WASTE_OUTER_DIA_D / 2) * scalePlan;
    var rInner_d = (WASTE_INNER_DIA_D / 2) * scalePlan;
    var wcy_d = basinY + rOuter_d;     // back edge on the boundary
    // Placement (2026-07-30): the shared waste rule is applied WITHIN each basin,
    // so `wasteGap` / `wasteOffset` / `wasteOffsets` from the builder repeat per
    // bowl (W is per basin, so the override list is per basin too). With no
    // placement params the helper's fallback gap reproduces the previous even
    // spread exactly, so legacy links are unchanged.
    var allWasteBenchOffsets = [];
    for (var bw3 = 0; bw3 < count; bw3++) {
      if (wastesPerBasin <= 0) break;
      var basinCentresMm = computeWasteCentres(
        design, allTapBenchOffsets, basinStarts[bw3], basinLen,
        { count: wastesPerBasin, wasteDia: WASTE_OUTER_DIA_D });
      for (var wj = 0; wj < basinCentresMm.length; wj++) {
        var wCentreMm_d = basinCentresMm[wj];
        allWasteBenchOffsets.push(wCentreMm_d);
        var wcx = bx + wCentreMm_d * scalePlan;
        svg.appendChild(svgCircle(wcx, wcy_d, rOuter_d, STYLE.bodyStroke));
        svg.appendChild(svgCircle(wcx, wcy_d, rInner_d, STYLE.bodyStroke));
      }
    }

    // Waste-position dim chain — chained row (edge → waste → … → edge) below the
    // tap / soap / dryer rows, same 4.5-unit stacking as the Classic path. Added
    // with the adjustable-waste work so a nudged waste can actually be read off
    // the multi-basin sheet.
    if (allWasteBenchOffsets.length) {
      var soapRowsD = getSoapConfig(design) ? 1 : 0;
      var dryerRowsD = getDryerConfig(design) ? 1 : 0;
      var wasteDimYD = dblTapDimY + 4.5 * (1 + soapRowsD + dryerRowsD);
      drawTapDimChain(svg, bx, bw, scalePlan, wasteDimYD, allWasteBenchOffsets);
    }

    // Length dimensions (above bench): overall + breakdown
    //   D | basin | gap | basin | … | basin | D
    drawDimensionLine(svg, bx, by - 8, bx + bw, by - 8, formatMm(len));
    drawDimensionLine(svg, bx, by - 3.5, basinXs[0], by - 3.5, formatMm(dInset));
    for (var di = 0; di < count; di++) {
      var bStartX = basinXs[di];
      var bEndX   = bStartX + basinW;
      drawDimensionLine(svg, bStartX, by - 3.5, bEndX, by - 3.5, formatMm(basinLen));
      if (di < count - 1) {
        drawDimensionLine(svg, bEndX, by - 3.5, basinXs[di + 1], by - 3.5, formatMm(gap));
      }
    }
    drawDimensionLine(svg, basinXs[count - 1] + basinW, by - 3.5, bx + bw, by - 3.5, formatMm(rightD));

    // Width dimensions (left of bench)
    drawDimensionLine(svg, bx - 4, by, bx - 4, by + backInset * scalePlan, formatMm(backInset));
    drawDimensionLine(svg, bx - 4, by + backInset * scalePlan, bx - 4, by + (backInset + basinWidth) * scalePlan, formatMm(basinWidth));
    drawDimensionLine(svg, bx - 4, by + (backInset + basinWidth) * scalePlan, bx - 4, by + bh, formatMm(frontInset));
    drawDimensionLine(svg, bx - 9, by, bx - 9, by + bh, formatMm(wid));

    // ===== FRONT ELEVATION =====
    // Wider plan→front gap on standard-height benches (see renderRampClassic).
    var planToFrontGap = hgt > 200 ? 21 : 31;
    var feY = planArea.y + planArea.h + planToFrontGap;
    var feX = bx;
    var feW = bw;
    var feH = hgt * scalePlan;
    // Length dim removed (already on the top + back views); lift the diagram
    // ~15px (4 units) into the freed space. The view title now sits vertically
    // to the LEFT of the diagram (drawViewLabelLeft) instead of above it.
    feY -= 4;
    drawViewLabelLeft(svg, 'Front view', feX, feY, feH);
    svg.appendChild(svgRect(feX, feY, feW, feH, STYLE.bodyStrokeBold));
    var feBasinTop = feY + feH * 0.08;
    var feBasinH   = Math.min(basinDepth * scalePlan, feH * 0.85);
    basinXs.forEach(function (bxi) {
      svg.appendChild(svgRect(bxi, feBasinTop, basinW, feBasinH, STYLE.hiddenLine));
    });
    drawDimensionLine(svg, feX - 4, feY, feX - 4, feY + feH, formatMm(hgt));

    // ===== BACK ELEVATION ("bottom view") =====
    // Same bracket rules + corner notches as the single-basin classic.
    var frontToBackGap = hgt > 200 ? 16 : 20;
    var bvY = feY + feH + frontToBackGap;
    var bvH = hgt * scalePlan;
    drawViewLabelLeft(svg, 'Back view', feX, bvY, bvH);
    // Dryer backplate(s) in the back view — same plate as the D-Airblade.
    // cornerEndMm = 85 (20mm edge + 65mm corner) → top dim chain fills the middle.
    drawDryerBackplatesBackView(svg, design, feX, bvY, bvH, scalePlan, allTapBenchOffsets, len, 85);
    var bracketPositions = getBracketPositions(family, len);

    if (bracketPositions && bracketPositions.length > 0) {
      var cornerW       = 65 * scalePlan;
      var cornerOffX_mm = 20;
      var cornerOffY_mm = 20;
      var cornerOffX    = cornerOffX_mm * scalePlan;
      var cornerOffY    = cornerOffY_mm * scalePlan;
      var lcX = feX + cornerOffX;
      var rcX = feX + feW - cornerOffX - cornerW;
      var cornerTopY = bvY + cornerOffY;
      var baseY = bvY + bvH;
      var bracketYmm = getBracketHeight(hgt);
      var bracketY = bvY + bracketYmm * scalePlan;

      svg.appendChild(svgLine(feX, bvY, feX + feW, bvY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(feX, bvY, feX, baseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(feX + feW, bvY, feX + feW, baseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(feX,           baseY, lcX,            baseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(lcX + cornerW, baseY, rcX,            baseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(rcX + cornerW, baseY, feX + feW,      baseY, STYLE.bodyStrokeBold));

      svg.appendChild(svgLine(lcX, cornerTopY, lcX + cornerW, cornerTopY, STYLE.bodyStroke));
      svg.appendChild(svgLine(lcX, cornerTopY, lcX, baseY, STYLE.bodyStroke));
      svg.appendChild(svgLine(lcX + cornerW, cornerTopY, lcX + cornerW, baseY, STYLE.bodyStroke));
      svg.appendChild(svgLine(rcX, cornerTopY, rcX + cornerW, cornerTopY, STYLE.bodyStroke));
      svg.appendChild(svgLine(rcX, cornerTopY, rcX, baseY, STYLE.bodyStroke));
      svg.appendChild(svgLine(rcX + cornerW, cornerTopY, rcX + cornerW, baseY, STYLE.bodyStroke));

      for (var bi = 0; bi < bracketPositions.length; bi++) {
        svg.appendChild(svgCircle(feX + bracketPositions[bi] * scalePlan, bracketY, 0.7, { fill: '#1a1a1a' }));
      }

      var topDimY = bvY - 4;
      drawDimensionLine(svg, feX, topDimY, lcX, topDimY, formatMm(cornerOffX_mm));
      drawDimensionLine(svg, lcX, topDimY, lcX + cornerW, topDimY, '65');
      drawDimensionLine(svg, rcX, topDimY, rcX + cornerW, topDimY, '65');
      drawDimensionLine(svg, rcX + cornerW, topDimY, feX + feW, topDimY, formatMm(cornerOffX_mm));
      drawDimensionLine(svg, feX - 4, bvY, feX - 4, cornerTopY, formatMm(cornerOffY_mm));
      drawDimensionLine(svg, feX - 9, bvY, feX - 9, bracketY, formatMm(bracketYmm));

      var spDimY = baseY + 4;
      var prevPos = 0;
      var spacings = [];
      for (var sp = 0; sp < bracketPositions.length; sp++) {
        spacings.push(bracketPositions[sp] - prevPos);
        prevPos = bracketPositions[sp];
      }
      spacings.push(len - prevPos);
      var runningX = feX;
      for (var spi = 0; spi < spacings.length; spi++) {
        var nextX = runningX + spacings[spi] * scalePlan;
        if (spacings[spi] >= 20) {
          drawDimensionLine(svg, runningX, spDimY, nextX, spDimY, formatMm(spacings[spi]));
        }
        runningX = nextX;
      }
      drawDimensionLine(svg, feX, spDimY + 4, feX + feW, spDimY + 4, formatMm(len));
    }

    // ===== SIDE CROSS-SECTION (right column, below info panel) =====
    var SECTION_PAD = 12;
    var sectionTitleY = infoPanelEndY + SECTION_PAD;
    var sx_origin = PAGE_W - PAGE_MARGIN - INFO_PANEL_W;

    svg.appendChild(svgText('Side view (cross-section)', sx_origin, sectionTitleY,
      Object.assign({}, STYLE.label, { 'font-weight': 'bold' })));

    var sAvailW = INFO_PANEL_W - 16;
    var sAvailH = PAGE_H - FOOTER_H - 8 - (sectionTitleY + 8);
    var sScale = Math.min(sAvailW / hgt, sAvailH / wid);

    var sBodyW = hgt * sScale;
    var sBodyH = wid * sScale;
    var sx = sx_origin + 12;
    var sy = sectionTitleY + 8;
    var t = MATERIAL_THICKNESS * sScale;

    svg.appendChild(svgRect(sx, sy, sBodyW, sBodyH, STYLE.bodyStrokeBold));

    var cavityTopY = sy + backInset * sScale;
    var cavityBotY = sy + (backInset + basinWidth) * sScale;
    var rampFrontX = sx;
    var rampFrontY = cavityBotY;
    var rampBackX  = sx + rampDrop * sScale;
    var rampBackY  = cavityTopY;

    svg.appendChild(svgPath(
      'M ' + sx + ' ' + cavityTopY +
      ' L ' + rampBackX + ' ' + rampBackY +
      ' L ' + rampFrontX + ' ' + rampFrontY +
      ' Z',
      STYLE.bodyStroke
    ));

    drawHatching(svg, {
      x: sx, y: cavityTopY - t, w: rampDrop * sScale, h: t
    }, { spacing: 0.55 });

    drawDimensionLine(svg, sx, cavityTopY + 3, rampBackX, cavityTopY + 3, formatMm(rampDrop));

    var dimX = sx - 5;
    drawDimensionLine(svg, dimX, sy, dimX, sy + backInset * sScale, formatMm(backInset));
    drawDimensionLine(svg, dimX, sy + backInset * sScale, dimX, sy + (backInset + basinWidth) * sScale, formatMm(basinWidth));
    drawDimensionLine(svg, dimX, sy + (backInset + basinWidth) * sScale, dimX, sy + sBodyH, formatMm(frontInset));
    drawDimensionLine(svg, dimX - 5, sy, dimX - 5, sy + sBodyH, formatMm(wid));
    drawDimensionLine(svg, sx, sy - 4, sx + sBodyW, sy - 4, formatMm(hgt));

    // Waste indicator — two-step shape: 75mm wide × 40mm long from the ramp
    // back end (fills the bench-bottom material), then narrows to 45mm × 20mm
    // (drain pipe extending below the bench).
    var WW1 = 75, WL1 = 40, WW2 = 45, WL2 = 20;
    var wdx1 = rampBackX;
    var wdy1 = cavityTopY;
    var wdw1 = WL1 * sScale;
    var wdh1 = WW1 * sScale;
    svg.appendChild(svgRect(wdx1, wdy1, wdw1, wdh1, STYLE.bodyStroke));
    var wdx2 = wdx1 + wdw1;
    var wdy2 = wdy1 + (WW1 - WW2) / 2 * sScale;
    var wdw2 = WL2 * sScale;
    var wdh2 = WW2 * sScale;
    svg.appendChild(svgRect(wdx2, wdy2, wdw2, wdh2, STYLE.bodyStroke));
    // Vertical 75 and 45 width dim lines on the RIGHT of the waste — same
    // orientation as the 460 total-width dim on the left, opposite side.
    var wdWasteRight = wdx2 + wdw2;
    var dDim45X = wdWasteRight + 4;
    var dDim75X = wdWasteRight + 9;
    drawDimensionLine(svg, dDim45X, wdy2, dDim45X, wdy2 + wdh2, formatMm(WW2));
    drawDimensionLine(svg, dDim75X, wdy1, dDim75X, wdy1 + wdh1, formatMm(WW1));

    return svg;
  }
  function renderRampTrough(design) {
    var svg = createSpecSheetSvg();
    renderHeader(svg, design);
    var infoPanelEndY = renderRightInfoPanel(svg, design);
    renderFooterContacts(svg);

    var entry = MODEL_REGISTRY[design.model] || MODEL_REGISTRY.trough180;
    var family = entry.family;                       // 'trough'
    var constants = FAMILY_CONSTANTS[family];

    var len = parseFloat(design.length) || 1500;
    var wid = parseFloat(design.width)  || entry.widthDefault || constants.defaultWidth;
    var defaultHeight = (design.model === 'trough150') ? 150 : 180;
    var hgt = parseFloat(design.height) || defaultHeight;
    var basinDepth = getBasinDepth(design.model, hgt);  // defaults to full height for trough

    // Front inset (G): Trough 180 is fixed at 40mm; Trough 150 offers both
    // 20mm and 40mm, so read the customer's actual selection and fall back to
    // the model default (20mm for Trough 150, 40mm otherwise).
    var backInset = getBackInset(design, constants); // customer-set E (50–145), default 110
    var gDefault = (design.model === 'trough150') ? 20 : 40;
    var frontInset = parseFloat(design.g) || gDefault;
    var basinWidth = Math.max(20, wid - backInset - frontInset);
    var dInset = parseFloat(design.d) || 150;        // trough default = 150
    var basinLength = Math.max(20, len - dInset * 2);

    // Trough is centre-only — no Left/Right positioning.
    var basinLeftOffsetMm = dInset;
    var actualBasinLength = basinLength;

    // ===== TOP PLAN VIEW =====
    // Top view lifted ~20px (5 units) so the tap / soap / dryer / waste dim
    // chains hanging below it clear the "Front view" title. planToFrontGap is
    // bumped by the same 5 below, so the front view block holds its position.
    var planArea = { x: DRAW_AREA.x + 14, y: DRAW_AREA.y + 7, w: DRAW_AREA.w - 18, h: 50 };
    var scalePlan = Math.min(planArea.w / len, planArea.h / wid);

    var bx = planArea.x;
    var by = planArea.y;
    var bw = len * scalePlan;
    var bh = wid * scalePlan;

    drawViewLabel(svg, 'Top view', by - 12);
    svg.appendChild(svgRect(bx, by, bw, bh, STYLE.bodyStrokeBold));

    var basinX = bx + basinLeftOffsetMm * scalePlan;
    var basinY = by + backInset * scalePlan;
    var basinW = actualBasinLength * scalePlan;
    var basinH = basinWidth * scalePlan;
    drawBasinCutout(svg, basinX, basinY, basinW, basinH, family);

    // Tap holes — distributed along the back-inset centerline, over the basin.
    // Same algorithm as the configurator (shared with Classic/Double/D-Airblade).
    var tapCount = Math.max(0, parseInt(design.h, 10) || 0);
    var tapBenchOffsets = [];
    var tapDimY = by + bh + 3.5;
    if (tapCount > 0) {
      var tapsY = basinY - tapRowDist(design, backInset) * scalePlan;
      var tapXOffsets = computeTapXOffsets(actualBasinLength, tapCount, design.tapEdgeInset, design.model === 'junior100');
      for (var ti = 0; ti < tapXOffsets.length; ti++) {
        var tapX = basinX + tapXOffsets[ti] * scalePlan;
        drawTapHole(svg, tapX, tapsY, { showCallout: ti === 0 });
      }
      // Chained tap-position dim row below the taps.
      tapBenchOffsets = tapXOffsets.map(function (off) {
        return basinLeftOffsetMm + off;
      });
      drawTapDimChain(svg, bx, bw, scalePlan, tapDimY, tapBenchOffsets);
    }

    // Soap dispenser holes — same back-inset row as the taps.
    drawSoapDispenserPlan(svg, design, bx, by, bw, bh, scalePlan, basinY, tapBenchOffsets, len, tapDimY);

    // Dryer holes — backplate behind each, drawn after the soap row.
    drawDryerHolePlan(svg, design, bx, by, bw, bh, scalePlan, basinY, tapBenchOffsets, len,
      tapDimY + (getSoapConfig(design) ? 9 : 4.5));

    // distFromBasin dims for tap / soap / dryer rows — right-side stack, dedup'd.
    drawBackInsetOffsetDims(svg, design, bx, bw, scalePlan, basinY, tapBenchOffsets, len, tapRowDist(design, backInset));

    // Wastes — trough 180 puts its waste(s) at the BACK of the basin (over the
    // deepest point of the ramp). Each is an 80mm feature with its BACK edge on
    // the boundary between the 110mm back inset and the 310mm basin opening, so
    // it extends 80mm forward into the basin.
    // Placement (2026-07-30): trough 180 now honours the W count and the shared
    // waste rule — even spacing about the basin centre by `wasteGap`, centre
    // fine-tune via `wasteOffset`, per-waste `wasteOffsets` nudges. Previously it
    // always drew exactly ONE waste on the basin centre regardless of W; with a
    // single waste and no placement params the result is identical.
    // trough 150 ("Trough") is deliberately left on its centred-rosette layout.
    var isTrough180 = (design.model === 'trough180');
    var wasteCentresMm = [];
    if (isTrough180) {
      // Double-circle waste: outer Ø80 (the cutout), inner Ø40 (the drain hole)
      var WASTE_OUTER_MM = 80;
      var WASTE_INNER_MM = 40;
      var rOuter = (WASTE_OUTER_MM / 2) * scalePlan;
      var rInner = (WASTE_INNER_MM / 2) * scalePlan;
      var troughWasteCount = Math.max(1, parseInt(design.w, 10) || 1);
      wasteCentresMm = computeWasteCentres(
        design, tapBenchOffsets, basinLeftOffsetMm, actualBasinLength,
        { count: troughWasteCount, wasteDia: WASTE_OUTER_MM });
      var wasteCy = basinY + rOuter;             // back edge of outer circle sits on the 110/310 boundary
      for (var wt = 0; wt < wasteCentresMm.length; wt++) {
        var wasteCx = basinX + (wasteCentresMm[wt] - basinLeftOffsetMm) * scalePlan;
        svg.appendChild(svgCircle(wasteCx, wasteCy, rOuter, STYLE.bodyStroke));
        svg.appendChild(svgCircle(wasteCx, wasteCy, rInner, STYLE.bodyStroke));
        // Ø80 callout on the FIRST waste only — vertical dimension line on the
        // right of the waste with the label sitting to the RIGHT of the line,
        // rotated -90° so it reads upward (standard drafting convention for
        // right-side vertical dims).
        if (wt !== 0) continue;
        var wasteDimX = wasteCx + rOuter + 2;
        var wasteDimY1 = basinY;
        var wasteDimY2 = basinY + rOuter * 2;
        var wasteTick = 1.4;
        svg.appendChild(svgLine(wasteDimX, wasteDimY1, wasteDimX, wasteDimY2, STYLE.dimLine));
        svg.appendChild(svgLine(wasteDimX - wasteTick / 2, wasteDimY1, wasteDimX + wasteTick / 2, wasteDimY1, STYLE.dimLine));
        svg.appendChild(svgLine(wasteDimX - wasteTick / 2, wasteDimY2, wasteDimX + wasteTick / 2, wasteDimY2, STYLE.dimLine));
        var wasteLabelX = wasteDimX + 1.5;
        var wasteLabelY = (wasteDimY1 + wasteDimY2) / 2;
        svg.appendChild(svgText(
          'Ø' + WASTE_OUTER_MM, wasteLabelX, wasteLabelY,
          Object.assign({}, STYLE.dimText, {
            'text-anchor': 'middle',
            'dominant-baseline': 'middle',
            transform: 'rotate(-90 ' + wasteLabelX + ' ' + wasteLabelY + ')'
          })
        ));
      }
    } else {
      var wasteCount = Math.max(0, parseInt(design.w, 10) || 1);
      for (var wi = 0; wi < wasteCount; wi++) {
        var wCentreMm = basinLeftOffsetMm + actualBasinLength / 2 +
          (wi - (wasteCount - 1) / 2) * (actualBasinLength / (wasteCount + 1));
        wasteCentresMm.push(wCentreMm);
        drawWasteRosette(svg, basinX + (wCentreMm - basinLeftOffsetMm) * scalePlan, basinY + basinH / 2, family);
      }
    }

    // Waste-position dim chain — chained row (edge → waste → … → edge) below the
    // tap / soap / dryer dim rows (4.5-unit stacking, same as the Classic path).
    if (wasteCentresMm.length) {
      var soapRowsT = getSoapConfig(design) ? 1 : 0;
      var dryerRowsT = getDryerConfig(design) ? 1 : 0;
      var wasteDimYT = tapDimY + 4.5 * (1 + soapRowsT + dryerRowsT);
      drawTapDimChain(svg, bx, bw, scalePlan, wasteDimYT, wasteCentresMm);
    }

    // Length dimensions (above)
    drawDimensionLine(svg, bx, by - 8, bx + bw, by - 8, formatMm(len));
    drawDimensionLine(svg, bx, by - 3.5, basinX, by - 3.5, formatMm(dInset));
    drawDimensionLine(svg, basinX, by - 3.5, basinX + basinW, by - 3.5, formatMm(actualBasinLength));
    drawDimensionLine(svg, basinX + basinW, by - 3.5, bx + bw, by - 3.5, formatMm(dInset));

    // Width dimensions (left of bench)
    drawDimensionLine(svg, bx - 4, by, bx - 4, by + backInset * scalePlan, formatMm(backInset));
    drawDimensionLine(svg, bx - 4, by + backInset * scalePlan, bx - 4, by + (backInset + basinWidth) * scalePlan, formatMm(basinWidth));
    drawDimensionLine(svg, bx - 4, by + (backInset + basinWidth) * scalePlan, bx - 4, by + bh, formatMm(frontInset));
    drawDimensionLine(svg, bx - 9, by, bx - 9, by + bh, formatMm(wid));

    // ===== FRONT ELEVATION =====
    // Wider plan→front gap on standard-height benches (see renderRampClassic).
    var planToFrontGap = hgt > 200 ? 21 : 31;
    var feY = planArea.y + planArea.h + planToFrontGap;
    var feX = bx;
    var feW = bw;
    var feH = hgt * scalePlan;
    // Length dim removed (already on the top + back views); lift the diagram
    // ~15px (4 units) into the freed space. The view title now sits vertically
    // to the LEFT of the diagram (drawViewLabelLeft) instead of above it.
    feY -= 4;
    drawViewLabelLeft(svg, 'Front view', feX, feY, feH);
    svg.appendChild(svgRect(feX, feY, feW, feH, STYLE.bodyStrokeBold));
    var feBasinTop = feY + feH * 0.08;
    var feBasinH   = Math.min(basinDepth * scalePlan, feH * 0.85);
    svg.appendChild(svgRect(basinX, feBasinTop, basinW, feBasinH, STYLE.hiddenLine));
    drawDimensionLine(svg, feX - 4, feY, feX - 4, feY + feH, formatMm(hgt));

    // ===== BACK ELEVATION ("bottom view") — same bracket rules as Classic =====
    var frontToBackGap = hgt > 200 ? 16 : 20;
    var bvY = feY + feH + frontToBackGap;
    var bvH = hgt * scalePlan;
    drawViewLabelLeft(svg, 'Back view', feX, bvY, bvH);
    // Dryer backplate(s) in the back view — same plate as the D-Airblade.
    // cornerEndMm = 85 (20mm edge + 65mm corner) → top dim chain fills the middle.
    drawDryerBackplatesBackView(svg, design, feX, bvY, bvH, scalePlan, tapBenchOffsets, len, 85);
    var bracketPositions = getBracketPositions(family, len);

    if (bracketPositions && bracketPositions.length > 0) {
      var cornerW       = 65 * scalePlan;
      var cornerOffX_mm = 20;
      var cornerOffY_mm = 20;
      var cornerOffX    = cornerOffX_mm * scalePlan;
      var cornerOffY    = cornerOffY_mm * scalePlan;
      var lcX = feX + cornerOffX;
      var rcX = feX + feW - cornerOffX - cornerW;
      var cornerTopY = bvY + cornerOffY;
      var baseY = bvY + bvH;
      var bracketYmm = getBracketHeight(hgt);
      var bracketY = bvY + bracketYmm * scalePlan;

      svg.appendChild(svgLine(feX, bvY, feX + feW, bvY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(feX, bvY, feX, baseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(feX + feW, bvY, feX + feW, baseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(feX,           baseY, lcX,            baseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(lcX + cornerW, baseY, rcX,            baseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(rcX + cornerW, baseY, feX + feW,      baseY, STYLE.bodyStrokeBold));

      svg.appendChild(svgLine(lcX, cornerTopY, lcX + cornerW, cornerTopY, STYLE.bodyStroke));
      svg.appendChild(svgLine(lcX, cornerTopY, lcX, baseY, STYLE.bodyStroke));
      svg.appendChild(svgLine(lcX + cornerW, cornerTopY, lcX + cornerW, baseY, STYLE.bodyStroke));
      svg.appendChild(svgLine(rcX, cornerTopY, rcX + cornerW, cornerTopY, STYLE.bodyStroke));
      svg.appendChild(svgLine(rcX, cornerTopY, rcX, baseY, STYLE.bodyStroke));
      svg.appendChild(svgLine(rcX + cornerW, cornerTopY, rcX + cornerW, baseY, STYLE.bodyStroke));

      for (var bi = 0; bi < bracketPositions.length; bi++) {
        svg.appendChild(svgCircle(feX + bracketPositions[bi] * scalePlan, bracketY, 0.7, { fill: '#1a1a1a' }));
      }

      var topDimY = bvY - 4;
      drawDimensionLine(svg, feX, topDimY, lcX, topDimY, formatMm(cornerOffX_mm));
      drawDimensionLine(svg, lcX, topDimY, lcX + cornerW, topDimY, '65');
      drawDimensionLine(svg, rcX, topDimY, rcX + cornerW, topDimY, '65');
      drawDimensionLine(svg, rcX + cornerW, topDimY, feX + feW, topDimY, formatMm(cornerOffX_mm));
      drawDimensionLine(svg, feX - 4, bvY, feX - 4, cornerTopY, formatMm(cornerOffY_mm));
      drawDimensionLine(svg, feX - 9, bvY, feX - 9, bracketY, formatMm(bracketYmm));

      var spDimY = baseY + 4;
      var prevPos = 0;
      var spacings = [];
      for (var sp = 0; sp < bracketPositions.length; sp++) {
        spacings.push(bracketPositions[sp] - prevPos);
        prevPos = bracketPositions[sp];
      }
      spacings.push(len - prevPos);
      var runningX = feX;
      for (var spi = 0; spi < spacings.length; spi++) {
        var nextX = runningX + spacings[spi] * scalePlan;
        if (spacings[spi] >= 20) {
          drawDimensionLine(svg, runningX, spDimY, nextX, spDimY, formatMm(spacings[spi]));
        }
        runningX = nextX;
      }
      drawDimensionLine(svg, feX, spDimY + 4, feX + feW, spDimY + 4, formatMm(len));
    }

    // ===== SIDE CROSS-SECTION =====
    var SECTION_PAD = 12;
    var sectionTitleY = infoPanelEndY + SECTION_PAD;
    var sx_origin = PAGE_W - PAGE_MARGIN - INFO_PANEL_W;

    svg.appendChild(svgText('Side view (cross-section)', sx_origin, sectionTitleY,
      Object.assign({}, STYLE.label, { 'font-weight': 'bold' })));

    if (design.model === 'trough150') {
      // ── Trough 150 cross-section ─────────────────────────────────────
      // Rotated orientation: bench WIDTH runs vertical (with 110 | 330 | 20
      // breakdown), bench HEIGHT runs horizontal. The basin opens on the LEFT
      // (bench top). Floor is ANGLED from 90mm depth at the front and back
      // edges down to 110mm in the centre. At the deepest point sits a tapered
      // waste opening that goes through to the bench bottom: 80mm wide at the
      // cavity floor (top), narrowing to 40mm wide at the bench bottom, 40mm
      // tall (so for a 150mm bench: 110mm cavity depth + 40mm waste height =
      // exact bench bottom).
      var sAvailW150 = INFO_PANEL_W - 16;
      var sAvailH150 = PAGE_H - FOOTER_H - 12 - (sectionTitleY + 8);
      var sScale150 = Math.min(sAvailW150 / hgt, sAvailH150 / wid);
      var sBodyW150 = hgt * sScale150;
      var sBodyH150 = wid * sScale150;
      var sx150 = sx_origin + 12;
      var sy150 = sectionTitleY + 8;
      var t150 = MATERIAL_THICKNESS * sScale150;

      // Outer body rectangle
      svg.appendChild(svgRect(sx150, sy150, sBodyW150, sBodyH150, STYLE.bodyStrokeBold));

      // Vertical breakdown reference y-coordinates
      var cavityTopY150 = sy150 + backInset * sScale150;
      var cavityBotY150 = sy150 + (backInset + basinWidth) * sScale150;

      // Depths and waste geometry
      var SHALLOW_DEPTH = 90;
      var DEEP_DEPTH = 110;
      var shallowX = sx150 + SHALLOW_DEPTH * sScale150;
      var deepX = sx150 + DEEP_DEPTH * sScale150;
      var benchBotX = sx150 + sBodyW150; // bench bottom (= sx + hgt*scale, = sx + 150*scale)
      var basinMidY150 = (cavityTopY150 + cavityBotY150) / 2;

      // Two-step waste (same pattern as Classic / Trough 180):
      //   First segment: 80mm wide × 40mm deep (depths 110 → 150 = bench bottom).
      //   Second segment: 45mm × 20mm (depths 150 → 170, extending 20mm below bench).
      // Square sides (no tapered transition).
      var WASTE_W1_150 = 80;
      var WASTE_L1_150 = 40;
      var WASTE_W2_150 = 45;
      var WASTE_L2_150 = 20;
      var w1BackY  = basinMidY150 - (WASTE_W1_150 / 2) * sScale150;
      var w1FrontY = basinMidY150 + (WASTE_W1_150 / 2) * sScale150;
      var w2BackY  = basinMidY150 - (WASTE_W2_150 / 2) * sScale150;
      var w2FrontY = basinMidY150 + (WASTE_W2_150 / 2) * sScale150;
      var w1EndX = deepX + WASTE_L1_150 * sScale150;     // = benchBotX
      var w2EndX = w1EndX + WASTE_L2_150 * sScale150;    // 20mm past bench bottom

      // Cavity outline: angled floor (90→110) + stepped two-segment waste
      var trough150Path =
        'M ' + sx150 + ' ' + cavityTopY150 +                       // top-left of opening
        ' L ' + shallowX + ' ' + cavityTopY150 +                   // back floor edge at 90mm
        ' L ' + deepX + ' ' + w1BackY +                            // slope down to back-top of waste 1
        ' L ' + w1EndX + ' ' + w1BackY +                           // straight along back wall of waste 1
        ' L ' + w1EndX + ' ' + w2BackY +                           // step inward to back-top of waste 2
        ' L ' + w2EndX + ' ' + w2BackY +                           // straight along back wall of waste 2
        ' L ' + w2EndX + ' ' + w2FrontY +                          // across bottom of waste 2 (45mm)
        ' L ' + w1EndX + ' ' + w2FrontY +                          // up front wall of waste 2
        ' L ' + w1EndX + ' ' + w1FrontY +                          // step outward to front-bottom of waste 1
        ' L ' + deepX + ' ' + w1FrontY +                           // straight along front wall of waste 1
        ' L ' + shallowX + ' ' + cavityBotY150 +                   // slope up to front floor edge at 90mm
        ' L ' + sx150 + ' ' + cavityBotY150;                       // bottom-left of opening
      svg.appendChild(svgPath(trough150Path, STYLE.bodyStroke));

      // 20mm material shell — horizontal strips above/below the back and front floor edges
      drawHatching(svg, { x: sx150, y: cavityTopY150 - t150, w: shallowX - sx150, h: t150 }, { spacing: 0.55 });
      drawHatching(svg, { x: sx150, y: cavityBotY150, w: shallowX - sx150, h: t150 }, { spacing: 0.55 });
      drawHatching(svg, { x: sx150, y: sy150, w: t150, h: cavityTopY150 - sy150 }, { spacing: 0.55 });
      drawHatching(svg, { x: sx150, y: cavityBotY150, w: t150, h: sy150 + sBodyH150 - cavityBotY150 }, { spacing: 0.55 });

      // ── Dimensions ──
      // Vertical breakdown on the LEFT side: 110 | 330 | 20 plus 460 overall.
      // Moved here so the waste-width dim lines on the right have room to breathe.
      var dimX150 = sx150 - 5;
      drawDimensionLine(svg, dimX150, sy150, dimX150, cavityTopY150, formatMm(backInset));
      drawDimensionLine(svg, dimX150, cavityTopY150, dimX150, cavityBotY150, formatMm(basinWidth));
      drawDimensionLine(svg, dimX150, cavityBotY150, dimX150, sy150 + sBodyH150, formatMm(frontInset));
      drawDimensionLine(svg, dimX150 - 5, sy150, dimX150 - 5, sy150 + sBodyH150, formatMm(wid));
      // Bench height on top
      drawDimensionLine(svg, sx150, sy150 - 4, sx150 + sBodyW150, sy150 - 4, formatMm(hgt));
      // 110 — deepest depth callout (basin floor at centre)
      drawDimensionLine(svg, sx150, basinMidY150, deepX, basinMidY150, formatMm(DEEP_DEPTH));
      // 90 — shallowest depth callout (basin floor at edges)
      drawDimensionLine(svg, sx150, sy150 + sBodyH150 + 4, shallowX, sy150 + sBodyH150 + 4, formatMm(SHALLOW_DEPTH));

      // 80 and 45 width dim lines on the right of the waste (past the second
      // segment). Same convention as Classic — 45 closer, 80 further out.
      var dim45X_150 = w2EndX + 4;
      var dim80X_150 = w2EndX + 9;
      drawDimensionLine(svg, dim45X_150, w2BackY, dim45X_150, w2FrontY, formatMm(WASTE_W2_150));
      drawDimensionLine(svg, dim80X_150, w1BackY, dim80X_150, w1FrontY, formatMm(WASTE_W1_150));

      return svg;
    }

    // ── Trough 180 cross-section ─────────────────────────────────────
    // Standard orientation per the trough reference image: bench width runs
    // horizontal (with 110 | basin | 40 breakdown across the top), bench height
    // runs vertical (180 on the left). Sloped ramp inside — 80mm deep at the
    // FRONT (right side of image), dropping to 120mm at the BACK (left side)
    // where the waste is.
    var sAvailW = INFO_PANEL_W - 8;
    var sAvailH = PAGE_H - FOOTER_H - 12 - (sectionTitleY + 12);
    var sScale = Math.min(sAvailW / wid, sAvailH / hgt);

    var sBodyW = wid * sScale;          // horizontal = bench width
    var sBodyH = hgt * sScale;          // vertical = bench height
    var sx = sx_origin + 4;
    var sy = sectionTitleY + 12;
    var t = MATERIAL_THICKNESS * sScale;

    // Outer rectangle (bench profile)
    svg.appendChild(svgRect(sx, sy, sBodyW, sBodyH, STYLE.bodyStrokeBold));

    // Width axis reference x-coordinates
    var backWallX2  = sx + backInset * sScale;                          // back wall → basin
    var frontWallX1 = sx + (backInset + basinWidth) * sScale;           // basin → front wall

    // Ramp depths (front shallower, back deeper — waste at back)
    var rampFront = parseFloat(entry.rampFrontDepth) || 80;
    var rampBack  = parseFloat(entry.rampBackDepth)  || 120;
    var rampFrontY = sy + rampFront * sScale;
    var rampBackY  = sy + rampBack  * sScale;

    // Basin cavity outline: opens at top, sloped floor at bottom
    //   top-left   (backWallX2,  sy)        — back wall meets bench top
    //   top-right  (frontWallX1, sy)        — front wall meets bench top
    //   right-bot  (frontWallX1, rampFrontY) — front of ramp (80mm deep)
    //   left-bot   (backWallX2,  rampBackY)  — back of ramp (120mm deep)
    svg.appendChild(svgPath(
      'M ' + backWallX2  + ' ' + sy +
      ' L ' + frontWallX1 + ' ' + sy +
      ' L ' + frontWallX1 + ' ' + rampFrontY +
      ' L ' + backWallX2  + ' ' + rampBackY +
      ' Z',
      STYLE.bodyStroke
    ));

    // 20mm material shell — no hatching on the angled ramp line itself.
    //   Back wall inner skin (vertical strip immediately LEFT of cavity opening)
    drawHatching(svg, {
      x: backWallX2 - t, y: sy, w: t, h: rampBackY - sy
    }, { spacing: 0.55 });
    //   Front wall inner skin (vertical strip immediately RIGHT of cavity opening)
    drawHatching(svg, {
      x: frontWallX1, y: sy, w: t, h: rampFrontY - sy
    }, { spacing: 0.55 });
    //   Bench-top edge (20mm top strip on back wall area)
    drawHatching(svg, {
      x: sx, y: sy, w: backInset * sScale, h: t
    }, { spacing: 0.55 });
    //   Bench-top edge (20mm top strip on front wall area)
    drawHatching(svg, {
      x: frontWallX1, y: sy, w: frontInset * sScale, h: t
    }, { spacing: 0.55 });

    // Waste indicator — two-step rectangle (same pattern as Classic 100, but
    // first segment is 80mm wide instead of 75mm).
    // First segment: 80mm wide × 40mm long, starts at the back wall boundary
    // at the deepest point of the ramp.
    // Second segment: 45mm × 20mm, centred horizontally on the first.
    // For Trough 180 (height 180, rampBack 120): 120 + 40 + 20 = 180 = bench
    // bottom, so the waste reaches exactly to the bench bottom.
    var WASTE_W1_T = 80;
    var WASTE_L1_T = 40;
    var WASTE_W2_T = 45;
    var WASTE_L2_T = 20;
    var wtx1 = backWallX2;
    var wty1 = rampBackY;
    var wtw1 = WASTE_W1_T * sScale;
    var wth1 = WASTE_L1_T * sScale;
    svg.appendChild(svgRect(wtx1, wty1, wtw1, wth1, STYLE.bodyStroke));
    var wtx2 = wtx1 + (WASTE_W1_T - WASTE_W2_T) / 2 * sScale;
    var wty2 = wty1 + wth1;
    var wtw2 = WASTE_W2_T * sScale;
    var wth2 = WASTE_L2_T * sScale;
    svg.appendChild(svgRect(wtx2, wty2, wtw2, wth2, STYLE.bodyStroke));

    // Horizontal width-dim lines below the bench bottom — 45 closer to feature,
    // 80 further out (standard nested dimension convention).
    var wDimY45 = sy + sBodyH + 3;
    var wDimY80 = sy + sBodyH + 7;
    drawDimensionLine(svg, wtx2, wDimY45, wtx2 + wtw2, wDimY45, formatMm(WASTE_W2_T));
    drawDimensionLine(svg, wtx1, wDimY80, wtx1 + wtw1, wDimY80, formatMm(WASTE_W1_T));

    // ── Dimensions ──
    // Width breakdown at the top: 110 | basin | 40, plus overall width above
    var topDimY1 = sy - 4;
    drawDimensionLine(svg, sx, topDimY1, backWallX2, topDimY1, formatMm(backInset));
    drawDimensionLine(svg, backWallX2, topDimY1, frontWallX1, topDimY1, formatMm(basinWidth));
    drawDimensionLine(svg, frontWallX1, topDimY1, sx + sBodyW, topDimY1, formatMm(frontInset));
    drawDimensionLine(svg, sx, topDimY1 - 4, sx + sBodyW, topDimY1 - 4, formatMm(wid));

    // Bench height on the left
    drawDimensionLine(svg, sx - 4, sy, sx - 4, sy + sBodyH, formatMm(hgt));

    // Back ramp depth (120) — inside the cavity at the back, vertical from top to ramp
    drawDimensionLine(svg, backWallX2 + 3, sy, backWallX2 + 3, rampBackY, formatMm(rampBack));
    // Front ramp depth (80) — inside the cavity at the front, vertical from top to ramp
    drawDimensionLine(svg, frontWallX1 - 3, sy, frontWallX1 - 3, rampFrontY, formatMm(rampFront));

    return svg;
  }
  // Compute waste-centre positions (in bench-mm) for a D-Airblade design.
  // Implements the placement rules described at the call site.
  // Parse a comma-separated override list ("100,,120") into an array where each
  // slot is a finite Number or null (blank / non-numeric → null → use default).
  function parseOffsetList(raw) {
    if (raw === null || raw === undefined || raw === '') return [];
    return String(raw).split(',').map(function (s) {
      var t = s.trim();
      if (t === '') return null;
      var n = parseFloat(t);
      return isNaN(n) ? null : n;
    });
  }

  // Waste centres for a single basin bowl. Placement rule (reworked with the
  // customer 2026-07-02, extended from the dryer family to the whole ramp range
  // 2026-07-30) — wastes are spaced EVENLY about the BASIN CENTRE and are
  // independent of the tap positions:
  //   • `wasteGap` (default 60) is the gap between adjacent wastes (centre to
  //     centre). Waste i sits at basinCentre + (i − (count−1)/2) · wasteGap, so
  //     the whole set is symmetric about the basin centre.
  //   • ODD count → one waste lands exactly on the basin centre. `wasteOffset`
  //     is a fine-tune that nudges ONLY that centre waste (+right / −left); the
  //     other wastes stay spaced from the basin centre and are NOT affected.
  //   • EVEN count → the two central wastes straddle the centre at ±wasteGap/2;
  //     there is no centre waste, so the fine-tune has no effect.
  //   • Per-waste signed overrides (`wasteOffsets` csv, advanced panel) nudge a
  //     waste individually and take precedence over the centre fine-tune for
  //     that index.
  // `taps` are accepted for signature compatibility but no longer used.
  //
  // `opts` (all optional, added 2026-07-30 so the classic / trough / multi paths
  // can share this):
  //   • `count`   — waste count for THIS bowl, overriding `design.w` (multi-basin
  //                 passes the per-basin count; legacy doubleC100 stored w as a
  //                 bench total so it divides down).
  //   • `wasteDia`— when supplied, every centre is clamped so a circle of that
  //                 diameter stays fully inside the basin opening. Omitted by the
  //                 D-Airblade path, which clamps itself (unchanged behaviour).
  function computeWasteCentres(design, taps, basinLeftMm, basinLengthMm, opts) {
    opts = opts || {};
    var wasteCount = (typeof opts.count === 'number' && opts.count > 0)
      ? Math.max(1, Math.round(opts.count))
      : Math.max(1, parseInt(design.w, 10) || 1);
    // Default gap = basin bowl length / (count + 1) → wastes spread evenly with
    // equal margins inside the basin. Only used when no explicit gap is supplied
    // (the builder normally sends one).
    var wasteGap = (typeof design.wasteGap === 'number' && !isNaN(design.wasteGap))
      ? design.wasteGap
      : (wasteCount > 1 ? (basinLengthMm / (wasteCount + 1)) : 60);
    var centreNudge = (typeof design.wasteOffset === 'number' && !isNaN(design.wasteOffset))
      ? design.wasteOffset : 0;
    var overrides = parseOffsetList(design.wasteOffsets);
    var basinCentre = basinLeftMm + basinLengthMm / 2;
    var mid = (wasteCount - 1) / 2;      // integer index for odd, half for even
    var isOdd = (wasteCount % 2) === 1;
    var centres = [];
    var i;

    // Base positions: even spacing centred on the basin.
    for (i = 0; i < wasteCount; i++) {
      centres.push(basinCentre + (i - mid) * wasteGap);
    }

    // Nudges: a per-waste override wins for its index; otherwise the fine-tune
    // applies to the centre waste only (odd counts).
    for (i = 0; i < centres.length; i++) {
      if (overrides[i] !== undefined && overrides[i] !== null) {
        centres[i] += overrides[i];
      } else if (isOdd && i === mid) {
        centres[i] += centreNudge;
      }
    }

    // Optional clamp — keeps a waste of the given diameter inside the bowl.
    if (typeof opts.wasteDia === 'number' && opts.wasteDia > 0 && basinLengthMm > 0) {
      var lo = basinLeftMm + opts.wasteDia / 2;
      var hi = basinLeftMm + basinLengthMm - opts.wasteDia / 2;
      if (hi < lo) { lo = hi = basinLeftMm + basinLengthMm / 2; }
      for (i = 0; i < centres.length; i++) {
        centres[i] = Math.max(lo, Math.min(hi, centres[i]));
      }
    }
    return centres;
  }

  // Legacy name kept so older notes / call sites keep resolving.
  var computeDAirbladeWasteCentres = computeWasteCentres;

  // Backplate centres for the dryer family. Each backplate is offset from its
  // tap by `backplateOffset` mm (default 90) on `backplateSide` (default left).
  // A per-plate signed override (`backplateOffsets` csv, mm from the tap, +right
  // / −left) replaces the default for that index; a blank slot uses the default.
  function computeDAirbladeBackplatePositions(design, taps) {
    var mag = (typeof design.backplateOffset === 'number' && !isNaN(design.backplateOffset))
      ? design.backplateOffset : 90;
    var dir = design.backplateSide === 'right' ? 1 : -1;
    var defSigned = mag * dir;
    var overrides = parseOffsetList(design.backplateOffsets);
    return taps.map(function (t, i) {
      var signed = (overrides[i] !== undefined && overrides[i] !== null)
        ? overrides[i] : defSigned;
      return t + signed;
    });
  }

  function renderRampDAirblade(design) {
    var svg = createSpecSheetSvg();
    renderHeader(svg, design);
    var infoPanelEndY = renderRightInfoPanel(svg, design);
    renderFooterContacts(svg);

    var entry = MODEL_REGISTRY[design.model] || MODEL_REGISTRY.dAirblade400;
    var family = entry.family;                       // 'dryer'
    var constants = FAMILY_CONSTANTS[family];

    // Wall-mounted taps: the taps mount on the wall instead of being drilled
    // into the bench, so the tap holes (and their dim chain) are NOT drawn, and
    // the back-edge inset (E) is variable rather than locked at 145mm. The tap
    // POSITIONS are still computed below so the backplates stay anchored to them,
    // and the tap count still shows in the info panel.
    var wallTaps = (design.wallTaps == 1 || design.wallTaps === '1');

    var len = parseFloat(design.length) || 900;
    var wid = parseFloat(design.width)  || entry.widthDefault || 530;
    var hgt = parseFloat(design.height) || 400;
    // Deck-mounted dryer taps lock E to 145mm (constants.backInset). Wall-mounted
    // taps unlock E to the customer-entered value (50–145mm).
    var backInset  = wallTaps ? (parseFloat(design.e) || constants.backInset) : constants.backInset;
    var frontInset = parseFloat(design.g) || 40;
    var basinWidth = Math.max(20, wid - backInset - frontInset);
    var dInset = parseFloat(design.d) || 120;
    var basinLengthDefault = Math.max(20, len - 2 * dInset);

    // Centre / Left / Right — dAirblade400 is a POSITIONABLE model. When the
    // configurator sends basinPosition='left'/'right' it also sends basinEdge
    // (distance from chosen side to basin's far edge) and basinLength.
    var pos = design.basinPosition || 'center';
    var basinLeftOffsetMm;
    var basinLength;
    if (pos === 'left' && design.basinEdge && design.basinLength) {
      var be = parseFloat(design.basinEdge);
      var bl = parseFloat(design.basinLength);
      basinLeftOffsetMm = Math.max(0, be - bl);
      basinLength = bl;
    } else if (pos === 'right' && design.basinEdge && design.basinLength) {
      var be2 = parseFloat(design.basinEdge);
      basinLeftOffsetMm = Math.max(0, len - be2);
      basinLength = parseFloat(design.basinLength);
    } else {
      basinLeftOffsetMm = dInset;
      basinLength = basinLengthDefault;
    }
    var basinCentreMm = basinLeftOffsetMm + basinLength / 2;

    // D-Airblade constants
    var BACKPLATE_W = 280;
    var BACKPLATE_H = 150;
    var BACKPLATE_THICK = 20;
    var BACKPLATE_BOTTOM_OFFSET = 70;
    var BRACKET_W = 65;
    var BRACKET_H = 296;
    var BRACKET_TOP_OFFSET = 24;
    var BRACKET_SIDE_OFFSET = 20;
    var FRONT_LIP_DEPTH = 25;
    var WASTE_TOP_DEPTH = 100;   // mm — depth where slope meets the waste
    var WASTE_BOT_DEPTH = 150;   // mm — depth at the waste floor (back wall side)
    var WASTE_WIDTH = 94;        // mm — waste opening width (in basin width direction)
    var WASTE_DIA = 42;          // mm — Ø42 waste callout in plan view
    var HOLE_HORIZ_SPACING = 185;
    var HOLE_EDGE_OFFSET = 42;

    // Tap, backplate & waste are three INDEPENDENT elements that share a tap as
    // their anchor (rule changed 2026-06-03 — previously backplate sat behind
    // the tap on the same centreline). Tap positions derive from design.h via
    // the shared computeTapXOffsets algorithm. The backplate is offset from its
    // tap by backplateOffset (default 90mm) on backplateSide; the waste offsets
    // the other way (see computeDAirbladeWasteCentres). Each is individually
    // overridable via the comma-separated override lists.
    var tapCount = Math.max(0, parseInt(design.h, 10) || 0);
    var tapXOffsetsInBasin = computeTapXOffsets(basinLength, tapCount, design.tapEdgeInset);
    // Tap centre positions, bench-relative (mm from bench left edge).
    var tapPositions = [];
    for (var tpi = 0; tpi < tapXOffsetsInBasin.length; tpi++) {
      tapPositions.push(basinLeftOffsetMm + tapXOffsetsInBasin[tpi]);
    }
    // Backplate centres = tap ± backplateOffset, clamped so the 280mm-wide plate
    // never intrudes into the 110mm bracket-hole zone at each bench end (the
    // backplate edge must stay ≥ 110mm from the bench edge).
    var bpHalf = BACKPLATE_W / 2;
    var BACKPLATE_EDGE_KEEPOUT = 110;
    var bpMin = BACKPLATE_EDGE_KEEPOUT + bpHalf;
    var bpMax = len - BACKPLATE_EDGE_KEEPOUT - bpHalf;
    // The D-Airblade structural backplate (dryer mount behind each tap) is
    // specific to the D-Airblade model. The Xlerator shares this render path but
    // has no such backplate — it uses the generic dryer hole instead — so its
    // backplate list is empty and every backplate draw site below no-ops.
    var backplatePositions = (design.model === 'dAirblade400')
      ? computeDAirbladeBackplatePositions(design, tapPositions)
          .map(function (p) { return Math.max(bpMin, Math.min(bpMax, p)); })
      : [];

    // ===== TOP PLAN VIEW =====
    // Plan height is a touch shorter than the other ramp paths (46 vs 50) to
    // free vertical room for the extra waste-position dim row below the tap
    // dim chain (see the wider planToFrontGap below).
    // Top view lifted ~20px (5 units) vs the other paths so the tap / soap /
    // dryer / waste dim chains below it clear the front-view dim lines on the
    // dense D-Airblade + dryer configs. planToFrontGap is bumped by the same
    // amount below so the front view stays put (only the gap grows).
    var planArea = { x: DRAW_AREA.x + 14, y: DRAW_AREA.y + 7, w: DRAW_AREA.w - 18, h: 46 };
    var scalePlan = Math.min(planArea.w / len, planArea.h / wid);

    var bx = planArea.x;
    var by = planArea.y;
    var bw = len * scalePlan;
    var bh = wid * scalePlan;

    drawViewLabel(svg, 'Top view', by - 12);
    svg.appendChild(svgRect(bx, by, bw, bh, STYLE.bodyStrokeBold));

    // Basin opening
    var basinX = bx + basinLeftOffsetMm * scalePlan;
    var basinY = by + backInset * scalePlan;
    var basinW = basinLength * scalePlan;
    var basinH = basinWidth * scalePlan;
    drawBasinCutout(svg, basinX, basinY, basinW, basinH, family);

    // Backplate(s) shown as thin rectangles at the OUTER back edge of the bench
    // (they attach to the wall — sit recessed into the back wall material
    // 20mm thick × 280mm wide, with the outer face flush at y = by).
    for (var bpi = 0; bpi < backplatePositions.length; bpi++) {
      var bpCx = bx + backplatePositions[bpi] * scalePlan;
      var bpX = bpCx - (BACKPLATE_W / 2) * scalePlan;
      var bpY = by;                                          // outer back edge
      var bpW = BACKPLATE_W * scalePlan;
      var bpH = BACKPLATE_THICK * scalePlan;
      svg.appendChild(svgRect(bpX, bpY, bpW, bpH, STYLE.bodyStroke));
    }

    // Tap holes — drawn at the TAP positions (no longer the backplate
    // positions, which are now offset away from the taps). Suppressed for
    // wall-mounted taps (see the wallTaps note at the top of this function).
    var dAirTapDimY = by + bh + 3.5;
    // Dim-row stacking (4.5-unit intervals below the tap row): tap → soap → dryer
    // → waste. Computed up front so the dryer holes (Xlerator) can place their
    // chain ABOVE the waste row (waste sits on the bottom). When no dryer chain is
    // drawn (dAirblade400, or dryer not enabled) the waste keeps the upper slot so
    // no empty row is left behind.
    var soapPresentDA = !!getSoapConfig(design);
    var dryerPresentDA = !!getDryerConfig(design);
    var dimRowY = dAirTapDimY + (soapPresentDA ? 9 : 4.5); // first free slot below tap/soap
    var dryerDimYDA = dimRowY;
    var wasteDimY = dryerPresentDA ? dimRowY + 4.5 : dimRowY;
    if (tapPositions.length > 0 && !wallTaps) {
      var thY = basinY - tapRowDist(design, backInset) * scalePlan;
      for (var thi = 0; thi < tapPositions.length; thi++) {
        var thCx = bx + tapPositions[thi] * scalePlan;
        drawTapHole(svg, thCx, thY, { showCallout: thi === 0 });
      }
      // Chained tap-position dim row below the taps. tapPositions are already in
      // bench-mm coordinates, which is exactly what the helper wants.
      drawTapDimChain(svg, bx, bw, scalePlan, dAirTapDimY, tapPositions);
    }

    // Soap dispenser holes — anchored off the TAP positions, same back-inset row
    // (the dryer models still use the 145mm back-inset E strip).
    drawSoapDispenserPlan(svg, design, bx, by, bw, bh, scalePlan, basinY, tapPositions, len, dAirTapDimY);

    // Dryer holes — only the Xlerator dryer model (dAirblade400 is excluded in
    // getDryerConfig, so this no-ops for the D-Airblade). The dryer-hole position
    // dim chain sits one row below the waste row (dryerDimYDA); getDryerConfig
    // returns null for dAirblade400 so no chain is drawn there.
    drawDryerHolePlan(svg, design, bx, by, bw, bh, scalePlan, basinY, tapPositions, len, dryerDimYDA);

    // distFromBasin dims for tap / soap / dryer rows — right-side stack, dedup'd.
    // Wall-mounted taps have no drilled tap row, so its offset dim is suppressed
    // (null); soap/dryer rows still draw off the (undrawn) tap anchor positions.
    drawBackInsetOffsetDims(svg, design, bx, bw, scalePlan, basinY, tapPositions, len, wallTaps ? null : tapRowDist(design, backInset));

    // Waste(s) at the back of the basin (back edge sits on the 145/345
    // boundary). Placement rules (see PROJECT.md):
    //   • Single waste → auto-centred in the central gap (even taps) or the
    //     chosen-side gap next to the centre tap (odd taps), then nudged by the
    //     signed wasteOffset (+right / −left). No taps → basin centre.
    //   • Multiple wastes → fill the gaps nearest the basin centre first; if
    //     there aren't enough gaps, spread evenly across the basin.
    var wasteCentresMm = computeWasteCentres(
      design, tapPositions, basinLeftOffsetMm, basinLength);

    var wasteRadius = (WASTE_DIA / 2) * scalePlan;
    var wasteCyPlan = basinY + wasteRadius;                  // back edge on the boundary
    // Clamp each waste centre so the circle stays inside the basin opening.
    var wasteMinMm = basinLeftOffsetMm + WASTE_DIA / 2;
    var wasteMaxMm = basinLeftOffsetMm + basinLength - WASTE_DIA / 2;
    var clampedWasteMm = [];
    for (var wi3 = 0; wi3 < wasteCentresMm.length; wi3++) {
      var cMm = Math.max(wasteMinMm, Math.min(wasteMaxMm, wasteCentresMm[wi3]));
      clampedWasteMm.push(cMm);
      var wasteCxPlan = bx + cMm * scalePlan;
      svg.appendChild(svgCircle(wasteCxPlan, wasteCyPlan, wasteRadius, STYLE.bodyStroke));
      svg.appendChild(svgCircle(wasteCxPlan, wasteCyPlan, wasteRadius * 0.4, STYLE.bodyStroke));
      // Ø42 callout on the first waste only.
      if (wi3 === 0) {
        var calloutEnd = wasteCxPlan + 5;
        svg.appendChild(svgLine(wasteCxPlan + wasteRadius * 0.7, wasteCyPlan + wasteRadius * 0.7,
          calloutEnd, wasteCyPlan + 3, STYLE.dimLine));
        svg.appendChild(svgText('Ø' + WASTE_DIA, calloutEnd + 0.5, wasteCyPlan + 3,
          Object.assign({}, STYLE.dimTextSm, { 'text-anchor': 'start' })));
      }
    }

    // Waste-position dim chain — a chained row (edge → waste → … → edge) below
    // the tap dim chain (and below the soap dim chain when soap is present), so
    // the customer can read each waste's distance along the bench. Reuses the
    // tap dim-chain helper since the layout is identical. wasteDimY / soapPresentDA
    // are computed up front (near dAirTapDimY) so the dryer row can stack below.
    drawTapDimChain(svg, bx, bw, scalePlan, wasteDimY, clampedWasteMm);

    // Length dimensions (above bench): overall + leftPad | basin | rightPad
    // For Centre, leftPad === rightPad === dInset; Left/Right give asymmetric
    // values straight from the basin offset.
    var leftPad = basinLeftOffsetMm;
    var rightPad = Math.max(0, len - basinLeftOffsetMm - basinLength);
    drawDimensionLine(svg, bx, by - 8, bx + bw, by - 8, formatMm(len));
    drawDimensionLine(svg, bx, by - 3.5, basinX, by - 3.5, formatMm(leftPad));
    drawDimensionLine(svg, basinX, by - 3.5, basinX + basinW, by - 3.5, formatMm(basinLength));
    drawDimensionLine(svg, basinX + basinW, by - 3.5, bx + bw, by - 3.5, formatMm(rightPad));

    // Width dimensions (left of bench)
    drawDimensionLine(svg, bx - 4, by, bx - 4, by + backInset * scalePlan, formatMm(backInset));
    drawDimensionLine(svg, bx - 4, by + backInset * scalePlan, bx - 4, by + (backInset + basinWidth) * scalePlan, formatMm(basinWidth));
    drawDimensionLine(svg, bx - 4, by + (backInset + basinWidth) * scalePlan, bx - 4, by + bh, formatMm(frontInset));
    drawDimensionLine(svg, bx - 9, by, bx - 9, by + bh, formatMm(wid));

    // ===== FRONT ELEVATION =====
    // Wider plan→front gap to fit the tap + soap + waste dim rows plus the
    // "Front view" label. D-Airblade is normally 400mm tall (hgt > 200).
    // +5 over the previous values to absorb the 5-unit top-view lift above, so
    // the front view stays in its original position while the dim chains gain
    // clearance above it.
    var planToFrontGap = hgt > 200 ? 28 : 31;
    var feY = planArea.y + planArea.h + planToFrontGap;
    var feX = bx;
    var feW = bw;
    var feH = hgt * scalePlan;
    // Length dim removed (already on the top + back views); lift the diagram
    // ~15px (4 units) into the freed space. The view title now sits vertically
    // to the LEFT of the diagram (drawViewLabelLeft) instead of above it.
    feY -= 4;
    drawViewLabelLeft(svg, 'Front view', feX, feY, feH);
    svg.appendChild(svgRect(feX, feY, feW, feH, STYLE.bodyStrokeBold));
    var feBasinTop = feY + feH * 0.06;
    var feBasinH   = Math.min(WASTE_BOT_DEPTH * scalePlan, feH * 0.5);
    svg.appendChild(svgRect(basinX, feBasinTop, basinW, feBasinH, STYLE.hiddenLine));
    // Length dim intentionally omitted on the front view — it's already shown on
    // the top view (above the bench) and on the back view (top dim row), and all
    // three views are left-aligned, so repeating it here only crowds the gap
    // against the top view's tap/soap/dryer/waste dim chains.
    drawDimensionLine(svg, feX - 4, feY, feX - 4, feY + feH, formatMm(hgt));

    // ===== BACK ELEVATION =====
    // Xlerator mounts like a Classic bench, so it uses the shared Classic back
    // view (corner bracket-pocket notches + bracket bolt holes at their proper
    // height, height handled the same way). The D-Airblade keeps its own back
    // view (large 65×296 corner cutouts + dryer backplates with mounting holes).
    var isXlerator = (design.model === 'xlerator400');
    var frontToBackGap = isXlerator ? (hgt > 200 ? 16 : 20) : (hgt > 200 ? 12 : 20);
    var bvY = feY + feH + frontToBackGap;
    var bvH = hgt * scalePlan;
    drawViewLabelLeft(svg, 'Back view', feX, bvY, bvH);
    if (isXlerator) {
      // Classic-style back view + bolt holes. bracketPositionsForLength bypasses
      // the dryer-family gate in getBracketPositions so the Xlerator gets the
      // same bracket layout as a Classic bench of the same length.
      drawRampClassicBackView(svg, feX, feW, bvY, bvH, scalePlan, len, hgt, bracketPositionsForLength(len));
      // Dryer backplate(s) — cornerEndMm 85 matches the Classic path so the top
      // dim chain fills the middle of the corner-bracket dim row.
      drawDryerBackplatesBackView(svg, design, feX, bvY, bvH, scalePlan, tapPositions, len, 85);
    } else {
    svg.appendChild(svgRect(feX, bvY, feW, bvH, STYLE.bodyStrokeBold));

    // Bracket cutouts at corners — 65 × 296, 24 from top, 20 from side
    var brackW = BRACKET_W * scalePlan;
    var brackH = BRACKET_H * scalePlan;
    var brackTop = bvY + BRACKET_TOP_OFFSET * scalePlan;
    var brackLeftX = feX + BRACKET_SIDE_OFFSET * scalePlan;
    var brackRightX = feX + feW - BRACKET_SIDE_OFFSET * scalePlan - brackW;
    svg.appendChild(svgRect(brackLeftX, brackTop, brackW, brackH, STYLE.bodyStroke));
    svg.appendChild(svgRect(brackRightX, brackTop, brackW, brackH, STYLE.bodyStroke));

    // Backplates with mounting holes — 280 × 150, 70mm from bottom of bench
    var bpRectW = BACKPLATE_W * scalePlan;
    var bpRectH = BACKPLATE_H * scalePlan;
    var bpBotEdge = bvY + bvH - BACKPLATE_BOTTOM_OFFSET * scalePlan;
    var bpTopEdge = bpBotEdge - bpRectH;
    var holeHorizSpacingScaled = HOLE_HORIZ_SPACING * scalePlan;
    var holeEdgeOffsetScaled = HOLE_EDGE_OFFSET * scalePlan;

    // Collect all hole x-positions across all backplates for the top dimension row
    var allHoleXs = [];
    for (var bpi2 = 0; bpi2 < backplatePositions.length; bpi2++) {
      var bpCxBack = feX + backplatePositions[bpi2] * scalePlan;
      var bpXBack = bpCxBack - bpRectW / 2;
      svg.appendChild(svgRect(bpXBack, bpTopEdge, bpRectW, bpRectH, STYLE.bodyStroke));

      // 4 mounting holes — 2 rows × 2 cols, spaced 185mm horizontally,
      // 42mm from top/bottom edge of the backplate.
      var holeY1 = bpTopEdge + holeEdgeOffsetScaled;
      var holeY2 = bpBotEdge - holeEdgeOffsetScaled;
      var holeXL = bpCxBack - holeHorizSpacingScaled / 2;
      var holeXR = bpCxBack + holeHorizSpacingScaled / 2;
      svg.appendChild(svgCircle(holeXL, holeY1, 0.6, { fill: '#1a1a1a' }));
      svg.appendChild(svgCircle(holeXR, holeY1, 0.6, { fill: '#1a1a1a' }));
      svg.appendChild(svgCircle(holeXL, holeY2, 0.6, { fill: '#1a1a1a' }));
      svg.appendChild(svgCircle(holeXR, holeY2, 0.6, { fill: '#1a1a1a' }));
      allHoleXs.push(holeXL, holeXR);

      // Per-backplate dimension callouts (LEFT side of each backplate)
      // 280 width below backplate
      drawDimensionLine(svg, bpXBack, bpBotEdge + 3, bpXBack + bpRectW, bpBotEdge + 3, formatMm(BACKPLATE_W));
      // 150 height on the LEFT of the backplate (not the right — text needs to be visible)
      drawDimensionLine(svg, bpXBack - 2, bpTopEdge, bpXBack - 2, bpBotEdge, formatMm(BACKPLATE_H));
      // 70 (backplate-to-bench-bottom) on the LEFT of EACH backplate
      drawDimensionLine(svg, bpXBack - 2, bpBotEdge, bpXBack - 2, bvY + bvH, formatMm(BACKPLATE_BOTTOM_OFFSET));
    }

    // ── Top dimension row: edge → hole → spacing → … → hole → edge ──
    // Plus overall length above the hole-position row.
    var topHoleDimY = bvY - 4;
    var topLengthDimY = bvY - 9;
    allHoleXs.sort(function(a, b) { return a - b; });
    var prevX = feX;
    for (var hxi = 0; hxi < allHoleXs.length; hxi++) {
      drawDimensionLine(svg, prevX, topHoleDimY, allHoleXs[hxi], topHoleDimY,
        formatMm((allHoleXs[hxi] - prevX) / scalePlan));
      prevX = allHoleXs[hxi];
    }
    drawDimensionLine(svg, prevX, topHoleDimY, feX + feW, topHoleDimY,
      formatMm((feX + feW - prevX) / scalePlan));
    // Overall length above
    drawDimensionLine(svg, feX, topLengthDimY, feX + feW, topLengthDimY, formatMm(len));

    // Bracket cutout dimensions (65 wide on top of left bracket, 296 height + 24 from top on outside LEFT)
    var brackBotL = brackTop + brackH;
    drawDimensionLine(svg, brackLeftX, brackBotL + 3, brackLeftX + brackW, brackBotL + 3, formatMm(BRACKET_W));
    // Stack 24 (top offset) + 296 (height) vertically on the OUTSIDE LEFT of the bracket
    var brackDimX = brackLeftX - 4;
    drawDimensionLine(svg, brackDimX, bvY, brackDimX, brackTop, formatMm(BRACKET_TOP_OFFSET));
    drawDimensionLine(svg, brackDimX, brackTop, brackDimX, brackBotL, formatMm(BRACKET_H));

    // Dryer backplate(s) in the back view — getDryerConfig returns null for
    // dAirblade400, so this no-ops for the D-Airblade itself.
    drawDryerBackplatesBackView(svg, design, feX, bvY, bvH, scalePlan, tapPositions, len);
    }

    // ===== SIDE CROSS-SECTION (right column, below info panel) =====
    var SECTION_PAD = 12;
    var sectionTitleY = infoPanelEndY + SECTION_PAD;
    var sx_origin = PAGE_W - PAGE_MARGIN - INFO_PANEL_W;

    svg.appendChild(svgText('Side view (cross-section)', sx_origin, sectionTitleY,
      Object.assign({}, STYLE.label, { 'font-weight': 'bold' })));

    // Standard orientation: width horizontal, height vertical
    var sAvailW = INFO_PANEL_W - 12;
    var sAvailH = PAGE_H - FOOTER_H - 12 - (sectionTitleY + 8);
    var sScale = Math.min(sAvailW / wid, sAvailH / hgt);

    var sBodyW = wid * sScale;
    var sBodyH = hgt * sScale;
    var sx = sx_origin + 6;
    var sy = sectionTitleY + 12;
    var t = MATERIAL_THICKNESS * sScale;

    // Outer rectangle (bench profile)
    svg.appendChild(svgRect(sx, sy, sBodyW, sBodyH, STYLE.bodyStrokeBold));

    var backWallX2  = sx + backInset * sScale;
    var frontWallX1 = sx + (backInset + basinWidth) * sScale;

    // Cavity outline traces (from front to back):
    //   - top-front corner of basin opening (frontWallX1, sy)
    //   - down 25mm front lip
    //   - diagonal slope to (wasteRightX, wasteTopY) at depth 100
    //   - the slope visually extends to within 6mm of the back wall (a faint
    //     dashed extension is drawn separately for that)
    //   - down 50mm at wasteRightX (right wall of waste)
    //   - across 94mm to back wall (waste floor)
    //   - up 150mm along the back wall to bench top
    //   - implicit close back along the bench top
    var frontLipY = sy + FRONT_LIP_DEPTH * sScale;
    var wasteRightX = backWallX2 + WASTE_WIDTH * sScale;
    var wasteTopY = sy + WASTE_TOP_DEPTH * sScale;     // depth 100 — slope meets waste
    var wasteBotY = sy + WASTE_BOT_DEPTH * sScale;     // depth 150 — waste floor
    var benchBotY = sy + sBodyH;

    var cavityPath =
      'M ' + frontWallX1 + ' ' + sy +
      ' L ' + frontWallX1 + ' ' + frontLipY +
      ' L ' + wasteRightX + ' ' + wasteTopY +              // diagonal slope
      ' L ' + wasteRightX + ' ' + wasteBotY +              // down 50 (waste right wall)
      ' L ' + backWallX2 + ' ' + wasteBotY +               // across 94 (waste floor)
      ' L ' + backWallX2 + ' ' + sy;                       // up 150 (back wall side)
    svg.appendChild(svgPath(cavityPath, STYLE.bodyStroke));

    // Slope extension — faint hidden line continuing the slope angle to within
    // 6mm of the 145/345 boundary, indicating the underlying ramp geometry.
    var slopePerX = (wasteTopY - frontLipY) / (frontWallX1 - wasteRightX);
    var slopeExtY = wasteTopY + (wasteRightX - (backWallX2 + 6 * sScale)) * slopePerX;
    svg.appendChild(svgLine(wasteRightX, wasteTopY,
      backWallX2 + 6 * sScale, slopeExtY, STYLE.hiddenLine));

    // 20mm material shell — back wall top edge and front wall top edge
    drawHatching(svg, { x: sx, y: sy, w: backInset * sScale, h: t }, { spacing: 0.55 });
    drawHatching(svg, { x: frontWallX1, y: sy, w: frontInset * sScale, h: t }, { spacing: 0.55 });
    // Front lip (25mm strip extending into the basin)
    drawHatching(svg, { x: frontWallX1 - t, y: sy, w: t, h: FRONT_LIP_DEPTH * sScale }, { spacing: 0.55 });

    // Backplate recess on the back face of the bench — drawn as a small
    // rectangle inside the back wall (20mm thick × 150mm tall, 70mm from
    // bottom). Shown whenever a backplate is shown in the BACK view: either the
    // D-Airblade structural backplate (backplatePositions), OR the Xlerator dryer
    // backplate (getDryerConfig present + basin ≥ 300mm tall — same rule as
    // drawDryerBackplatesBackView). Same plate geometry either way.
    var bpRecessW = BACKPLATE_THICK * sScale;
    var bpRecessH = BACKPLATE_H * sScale;
    var bpRecessTopY = benchBotY - BACKPLATE_BOTTOM_OFFSET * sScale - bpRecessH;
    var showBackplateSection = backplatePositions.length > 0 ||
      (!!getDryerConfig(design) && dryerHasBackplate(design));
    if (showBackplateSection) {
      svg.appendChild(svgRect(sx, bpRecessTopY, bpRecessW, bpRecessH, STYLE.bodyStroke));
    }

    // ── Dimensions ──
    // Width breakdown on top
    var topDimY = sy - 4;
    drawDimensionLine(svg, sx, topDimY, backWallX2, topDimY, formatMm(backInset));
    drawDimensionLine(svg, backWallX2, topDimY, frontWallX1, topDimY, formatMm(basinWidth));
    drawDimensionLine(svg, frontWallX1, topDimY, sx + sBodyW, topDimY, formatMm(frontInset));
    drawDimensionLine(svg, sx, topDimY - 4, sx + sBodyW, topDimY - 4, formatMm(wid));

    // Bench height on the LEFT side (moved per amendment)
    drawDimensionLine(svg, sx - 4, sy, sx - 4, sy + sBodyH, formatMm(hgt));

    // 25mm front lip depth (vertical, at front lip)
    drawDimensionLine(svg, frontWallX1 - 2, sy, frontWallX1 - 2, frontLipY, formatMm(FRONT_LIP_DEPTH));

    // 100mm and 50mm — vertical dimension lines on the right of the waste right
    // wall, with labels on the RIGHT of the dim line (rotated -90°) so the short
    // 50 label doesn't sit on top of its own line.
    function drawVDimRightLabel(parent, x, y1, y2, label) {
      var tick = 1.4;
      parent.appendChild(svgLine(x, y1, x, y2, STYLE.dimLine));
      parent.appendChild(svgLine(x - tick / 2, y1, x + tick / 2, y1, STYLE.dimLine));
      parent.appendChild(svgLine(x - tick / 2, y2, x + tick / 2, y2, STYLE.dimLine));
      var lbX = x + 1.5;
      var lbY = (y1 + y2) / 2;
      parent.appendChild(svgText(label, lbX, lbY,
        Object.assign({}, STYLE.dimText, {
          'text-anchor': 'middle',
          'dominant-baseline': 'middle',
          transform: 'rotate(-90 ' + lbX + ' ' + lbY + ')'
        })));
    }
    drawVDimRightLabel(svg, wasteRightX + 2, sy, wasteTopY, formatMm(WASTE_TOP_DEPTH));
    drawVDimRightLabel(svg, wasteRightX + 2, wasteTopY, wasteBotY, '50');

    // 150mm — waste back wall (full height to bench top)
    drawDimensionLine(svg, backWallX2 - 2, sy, backWallX2 - 2, wasteBotY, formatMm(WASTE_BOT_DEPTH));
    // 94mm waste floor width (horizontal callout below the waste floor)
    drawDimensionLine(svg, backWallX2, wasteBotY + 3, wasteRightX, wasteBotY + 3, formatMm(WASTE_WIDTH));

    // 20mm backplate depth — horizontal callout above the backplate recess
    // (whenever the recess is drawn — D-Airblade or Xlerator dryer backplate).
    if (showBackplateSection) {
      drawDimensionLine(svg, sx, bpRecessTopY - 3, sx + bpRecessW, bpRecessTopY - 3, formatMm(BACKPLATE_THICK));
    }

    return svg;
  }
  // Counter-top (mountType='top') side cross-section — basin sits PROUD of the
  // bench, not inset. Self-contained, NON-rotated: bench width runs horizontal
  // (back on the left), the bench slab is a thin hatched bar, and the basin bowl
  // sits ON TOP of it with the waste tapering down through the slab. Tap-deck
  // moulds (TP952/TP953) get a flat deck platform + tap hole on the tap side.
  // FIRST PASS — needs a browser eyeball.
  // Enlarged "drainage structure" detail for the TP-range waste flange
  // (Ø68 outer / Ø51 middle / Ø45 inner; 5 + 5 depth; 3 rim). Drawn under the
  // side view inside the info-panel column. cx = column centre, topY = title row.
  function drawDrainageStructure(svg, cx, topY, availW, mould) {
    var oD = mould.wasteTopDia || 68, mD = mould.wasteMidDia || 51, iD = mould.wasteBotDia || 45;
    var ds = Math.min((availW * 0.62) / oD, 0.36);      // enlarged scale (units/mm)
    var oR = oD / 2 * ds, mR = mD / 2 * ds, iR = iD / 2 * ds;
    var rim = (mould.drainRim != null) ? mould.drainRim : 3;  // final rim step — per model
    var d5 = 5 * ds, mt = rim * ds;                      // step depth + rim/material thickness

    svg.appendChild(svgText('Drainage structure', cx, topY,
      Object.assign({}, STYLE.label, { 'font-weight': 'bold', 'text-anchor': 'middle' })));

    var yRim  = topY + 7;               // flange rim (Ø68) — leaves room for the Ø68 dim
    var yMid  = yRim + d5;              // Ø51 ledge
    var yHole = yRim + 2 * d5;          // Ø45 hole level
    var bcy = (yRim + yHole + mt) / 2;
    var bubbleR = oR + 3;

    // Dashed detail bubble.
    svg.appendChild(svgEl('circle', Object.assign({ cx: cx, cy: bcy, r: bubbleR, fill: 'none', 'stroke-dasharray': '1.6 1.3' }, STYLE.dimLine)));

    // Flange profile (each side): HORIZONTAL from the bubble edge (at rim height)
    // → Ø68 rim → straight down 5 → ramp down 5 to the Ø51 ledge → horizontal to
    // the Ø45 hole → down 3.
    var dxTop = Math.sqrt(Math.max(0, bubbleR * bubbleR - (bcy - yRim) * (bcy - yRim)));
    [-1, 1].forEach(function (sx) {
      svg.appendChild(svgPath(
        'M ' + (cx + sx * dxTop) + ' ' + yRim +
        ' L ' + (cx + sx * oR) + ' ' + yRim +
        ' L ' + (cx + sx * oR) + ' ' + (yRim + d5) +
        ' L ' + (cx + sx * mR) + ' ' + yHole +
        ' L ' + (cx + sx * iR) + ' ' + yHole +
        ' L ' + (cx + sx * iR) + ' ' + (yHole + mt), STYLE.bodyStrokeBold));
    });

    // Dims: Ø68 above, Ø51 at the mid ledge, Ø45 below the hole; 5 + 5 vertical
    // (rim → mid → hole) inboard-left; 3 (rim thickness) on the right.
    drawDimensionLine(svg, cx - oR, yRim - 3, cx + oR, yRim - 3, 'Ø' + oD);
    drawDimensionLine(svg, cx - mR, yMid, cx + mR, yMid, '' + mD);
    drawDimensionLine(svg, cx - iR, yHole + mt + 3, cx + iR, yHole + mt + 3, 'Ø' + iD, { labelBelow: true });
    // 5 (straight drop) · 5 (ramp) · 3 — a vertical chain OUTSIDE the bubble, right.
    var vDimX = cx + bubbleR + 3;
    drawDimensionLine(svg, vDimX, yRim,  vDimX, yMid,       '5', { labelRight: true, labelOffset: 3 });
    drawDimensionLine(svg, vDimX, yMid,  vDimX, yHole,      '5', { labelRight: true, labelOffset: 3 });
    // Rim step — per model (e.g. 2.5 / 0.6). Its own column FURTHER right than the
    // 5·5 chain so the tiny segment's label doesn't overlap it.
    drawDimensionLine(svg, vDimX + 6, yHole, vDimX + 6, yHole + mt, String(rim), { labelRight: true, labelOffset: 3 });

    // Caption below the bubble.
    svg.appendChild(svgText('Enlarged view of the drainage structure', cx, bcy + bubbleR + 5,
      Object.assign({}, STYLE.dimText, { 'text-anchor': 'middle', 'font-style': 'italic' })));
  }

  // Aproned counter-top SIDE section — the basin is a rectangular vessel (apron)
  // sitting FLUSH on the bench: an outer square around a curved bowl. TP950–953.
  function drawMouldedApronSection(svg, o) {
    var mould = o.mould;
    var W = o.wid;                                   // bench width (front-to-back)
    var benchT = o.hgt;                              // bench slab thickness
    var apronH = mould.apronHeight || 150;          // outer box height (proud on bench)
    var bowlD  = mould.bowlDepth || 130;            // bowl depth from rim
    var wasteD = Math.max(2, (mould.bowlWasteDepth || (bowlD + 12)) - bowlD);
    var lipR   = mould.lipRadius || 0;              // rounded top lip (TP951/953)
    var apronWmm = o.basinWidth;                    // apron footprint width (AW)
    var bowlWmm  = mould.bowlWidth || apronWmm;     // bowl opening width (BW)
    var bowlWImm = mould.bowlWidthInner || bowlWmm; // inner bowl width (BWI, TP953)
    var deckMM   = mould.tapDeck || 0;              // tap-deck width, sits at the back
    var noBench = !!o.noBench;                       // basin-only (composition = none)
    var Wext   = noBench ? apronWmm : W;             // horizontal extent to scale/fit
    var totalH = noBench ? apronH : (apronH + benchT);
    var s = Math.min(o.availW / Wext, o.availH / totalH);
    var x0 = noBench ? (o.sx + Math.max(4, (o.availW - Wext * s) / 2)) : (o.sx + 4);
    var y0 = o.titleY + 12;
    var rimY = y0;                                   // apron top / rim
    var slabY = y0 + apronH * s;                     // bench top (or apron base, no-bench)
    var slabH = noBench ? 0 : benchT * s;
    var dimRightX = x0 + Wext * s;                   // right-hand dim column

    // Bench slab (full width) + hatching — omitted when there is no bench.
    if (!noBench) {
      svg.appendChild(svgRect(x0, slabY, W * s, slabH, STYLE.bodyStrokeBold));
      drawHatching(svg, { x: x0, y: slabY, w: W * s, h: slabH }, { spacing: 0.55 });
    }

    // Apron footprint — with a bench it starts at the back inset (E); no-bench it
    // fills the centred vessel area.
    var apronLeftX = noBench ? x0 : (x0 + o.backInset * s);
    var apronRightX = apronLeftX + apronWmm * s;
    var lipPx = Math.min(lipR * s, (apronRightX - apronLeftX) / 2, apronH * s / 2);

    // Bowl geometry. The lip sweeps from the OUTER rim to the INNER waterline:
    //   • no deck / no inner (TP950/951): rim = apron edge, waterline = bowl width.
    //   • deck / inner (TP952/953): rim = the bowl's own outer width (BW), flush to
    //     the FRONT with the deck at the back; waterline = BWI (TP953) else BW.
    var waterlineW = bowlWImm;
    var rimW = (deckMM > 0 || mould.bowlWidthInner) ? bowlWmm : apronWmm;
    var rimLeftX, rimRightX;
    if (deckMM > 0) { rimRightX = apronRightX - (mould.frontLip || 0) * s; rimLeftX = rimRightX - rimW * s; }
    else { var _cc = (apronLeftX + apronRightX) / 2; rimLeftX = _cc - rimW * s / 2; rimRightX = _cc + rimW * s / 2; }
    var bowlCx = (rimLeftX + rimRightX) / 2;
    var wlLeftX = bowlCx - waterlineW * s / 2, wlRightX = bowlCx + waterlineW * s / 2;
    var wcx = bowlCx;
    var wTopHalf = (mould.wasteTopDia / 2) * s;
    var wBotHalf = (mould.wasteBotDia / 2) * s;
    var bowlFloorY = rimY + bowlD * s;
    var wasteBotY  = rimY + (bowlD + wasteD) * s;

    // Outer apron — top line + left + right walls (the R-lip belongs to the BOWL
    // rim). Only the full base line is replaced by FEET at each side (see below).
    svg.appendChild(svgLine(apronLeftX, rimY, apronRightX, rimY, STYLE.bodyStrokeBold));
    svg.appendChild(svgLine(apronLeftX, rimY, apronLeftX, slabY, STYLE.bodyStrokeBold));
    svg.appendChild(svgLine(apronRightX, rimY, apronRightX, slabY, STYLE.bodyStrokeBold));
    var feetSV = mould.feet && mould.feet.sv;
    if (feetSV) {
      var fLpx = feetSV[0] * s, fRpx = feetSV[1] * s;
      svg.appendChild(svgLine(apronLeftX, slabY, apronLeftX + fLpx, slabY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(apronRightX - fRpx, slabY, apronRightX, slabY, STYLE.bodyStrokeBold));
      var fDy = slabY + 2.5;
      drawDimensionLine(svg, apronLeftX, fDy, apronLeftX + fLpx, fDy, formatMm(feetSV[0]), { labelBelow: true });
      drawDimensionLine(svg, apronRightX - fRpx, fDy, apronRightX, fDy, formatMm(feetSV[1]), { labelBelow: true });
    } else {
      svg.appendChild(svgLine(apronLeftX, slabY, apronRightX, slabY, STYLE.bodyStrokeBold));
    }

    // Apron top rim / deck segments outboard of the bowl's outer rim.
    svg.appendChild(svgLine(apronLeftX, rimY, rimLeftX, rimY, STYLE.bodyStroke));
    svg.appendChild(svgLine(rimRightX, rimY, apronRightX, rimY, STYLE.bodyStroke));

    // Bowl — rim rolls over an R-lip from the outer rim IN to the waterline (e.g.
    // TP951 421→378), then the wall curves to the waste. lipR = 0 → plain bowl.
    var Lp = Math.min(lipPx, (wlRightX - wlLeftX) / 2, bowlD * s / 2);
    if (Lp > 0) {
      svg.appendChild(svgPath(
        'M ' + rimLeftX + ' ' + rimY +
        ' Q ' + wlLeftX + ' ' + rimY + ' ' + wlLeftX + ' ' + (rimY + Lp) +
        ' Q ' + wlLeftX + ' ' + bowlFloorY + ' ' + (wcx - wTopHalf) + ' ' + bowlFloorY +
        ' L ' + (wcx + wTopHalf) + ' ' + bowlFloorY +
        ' Q ' + wlRightX + ' ' + bowlFloorY + ' ' + wlRightX + ' ' + (rimY + Lp) +
        ' Q ' + wlRightX + ' ' + rimY + ' ' + rimRightX + ' ' + rimY, STYLE.bodyStroke));
    } else {
      // Plain bowl: short flat rim from the outer rim in to the waterline, then curve.
      if (wlLeftX - rimLeftX > 0.1) {
        svg.appendChild(svgLine(rimLeftX, rimY, wlLeftX, rimY, STYLE.bodyStroke));
        svg.appendChild(svgLine(wlRightX, rimY, rimRightX, rimY, STYLE.bodyStroke));
      }
      svg.appendChild(svgPath(
        'M ' + wlLeftX + ' ' + rimY +
        ' Q ' + wlLeftX + ' ' + bowlFloorY + ' ' + (wcx - wTopHalf) + ' ' + bowlFloorY +
        ' L ' + (wcx + wTopHalf) + ' ' + bowlFloorY +
        ' Q ' + wlRightX + ' ' + bowlFloorY + ' ' + wlRightX + ' ' + rimY, STYLE.bodyStroke));
    }

    // Waste — tapers from the bowl floor down inside the apron skirt.
    svg.appendChild(svgPath(
      'M ' + (wcx - wTopHalf) + ' ' + bowlFloorY +
      ' L ' + (wcx - wBotHalf) + ' ' + wasteBotY +
      ' L ' + (wcx + wBotHalf) + ' ' + wasteBotY +
      ' L ' + (wcx + wTopHalf) + ' ' + bowlFloorY, STYLE.bodyStroke));

    // Dims (RIGHT): apron height, bowl&waste, bowl stacked; then bench thickness.
    // Bowl width (above) then apron width (below) along the bottom.
    // Apron height + bench thickness share the SAME column so they read as one
    // joined chain (basin height above bench height, tick where they meet).
    drawDimensionLine(svg, dimRightX + 4,  rimY,  dimRightX + 4,  slabY,         formatMm(Math.round(apronH)));
    if (!noBench) {
      drawDimensionLine(svg, dimRightX + 4,  slabY, dimRightX + 4,  slabY + slabH, formatMm(benchT));
    }
    drawDimensionLine(svg, dimRightX + 9,  rimY,  dimRightX + 9,  wasteBotY,     formatMm(Math.round(bowlD + wasteD)));
    drawDimensionLine(svg, dimRightX + 14, rimY,  dimRightX + 14, bowlFloorY,    formatMm(Math.round(bowlD)));
    // Bottom: (inner waterline, TP953) → bowl width → apron width — all centred on
    // the bowl. Bench width only when there's a bench.
    var dRow = slabY + slabH + 4;
    if (mould.bowlWidthInner) {
      drawDimensionLine(svg, wlLeftX, dRow, wlRightX, dRow, formatMm(Math.round(bowlWImm)));
      dRow += 4;
    }
    var bwHalf = bowlWmm * s / 2;
    drawDimensionLine(svg, bowlCx - bwHalf, dRow, bowlCx + bwHalf, dRow, formatMm(Math.round(bowlWmm)));
    dRow += 4;
    drawDimensionLine(svg, apronLeftX, dRow, apronRightX, dRow, formatMm(Math.round(apronWmm)));
    if (!noBench) {
      dRow += 4;
      drawDimensionLine(svg, x0, dRow, x0 + W * s, dRow, formatMm(Math.round(W)));
    }
    return dRow;
  }

  function drawMouldedCounterTopSection(svg, o) {
    var mould = o.mould;
    if (mould.apron) { return drawMouldedApronSection(svg, o); }
    var W = o.wid;                                   // bench width (front-to-back)
    var benchT = o.hgt;                              // bench slab thickness
    // Basin sits PROUD by its bowl depth (rim → bowl floor = bench top). The
    // waste (total − bowl, e.g. TP949 157−145 = 12) then drops through the slab.
    var proudH = mould.bowlDepth || 120;
    var wasteD = (mould.mouldHeight && mould.bowlDepth)
      ? Math.max(2, mould.mouldHeight - mould.bowlDepth)
      : (mould.wasteDepth || 12);
    var deckMM = mould.tapDeck || 0;
    var bowlWidthMM = Math.max(1, o.basinWidth - deckMM);
    var noBench = !!o.noBench;                        // basin-only (composition = none)
    var Wext   = noBench ? o.basinWidth : W;
    var totalH = noBench ? (proudH + wasteD) : (proudH + benchT);
    var s = Math.min(o.availW / Wext, o.availH / totalH);
    var x0 = noBench ? (o.sx + Math.max(4, (o.availW - Wext * s) / 2)) : (o.sx + 4);
    var y0 = o.titleY + 12;
    var rimY = y0;                                   // basin rim (top)
    var slabY = y0 + proudH * s;                     // bowl floor (bench top, or open no-bench)
    var slabH = noBench ? 0 : benchT * s;
    var dimRightX = x0 + Wext * s;

    // Bench slab (full width) + hatching — omitted when there is no bench.
    if (!noBench) {
      svg.appendChild(svgRect(x0, slabY, W * s, slabH, STYLE.bodyStrokeBold));
      drawHatching(svg, { x: x0, y: slabY, w: W * s, h: slabH }, { spacing: 0.55 });
    }

    // Layout across the width from the back (left): E | [deck | bowl] | G.
    var deckStartX = noBench ? x0 : (x0 + o.backInset * s);
    var bowlLeftX  = deckStartX + deckMM * s;
    var bowlRightX = bowlLeftX + bowlWidthMM * s;
    var wcx = (bowlLeftX + bowlRightX) / 2;
    var wTopHalf = (mould.wasteTopDia / 2) * s;
    var wBotHalf = (mould.wasteBotDia / 2) * s;

    // Bowl body — proud on top of the slab, open rim, curving to the waste. When
    // an inner rim is defined (TP949 450→434), the rim rolls off over an R-lip
    // (rimRadius) from the outer edge in to the inner opening, then the wall drops.
    var bowlCxN  = (bowlLeftX + bowlRightX) / 2;
    var innerWmm = mould.bowlInner || bowlWidthMM;
    var wlLn = bowlCxN - innerWmm * s / 2, wlRn = bowlCxN + innerWmm * s / 2;
    var LpN  = Math.min((mould.rimRadius || 0) * s, (slabY - rimY) * 0.6);
    if (LpN > 0 && innerWmm < bowlWidthMM) {
      svg.appendChild(svgPath(
        'M ' + bowlLeftX + ' ' + rimY +
        ' Q ' + wlLn + ' ' + rimY + ' ' + wlLn + ' ' + (rimY + LpN) +
        ' Q ' + wlLn + ' ' + slabY + ' ' + (wcx - wTopHalf) + ' ' + slabY +
        ' L ' + (wcx + wTopHalf) + ' ' + slabY +
        ' Q ' + wlRn + ' ' + slabY + ' ' + wlRn + ' ' + (rimY + LpN) +
        ' Q ' + wlRn + ' ' + rimY + ' ' + bowlRightX + ' ' + rimY, STYLE.bodyStroke));
    } else {
      svg.appendChild(svgPath(
        'M ' + bowlLeftX + ' ' + rimY +
        ' Q ' + bowlLeftX + ' ' + slabY + ' ' + (wcx - wTopHalf) + ' ' + slabY +
        ' L ' + (wcx + wTopHalf) + ' ' + slabY +
        ' Q ' + bowlRightX + ' ' + slabY + ' ' + bowlRightX + ' ' + rimY, STYLE.bodyStroke));
    }
    svg.appendChild(svgLine(bowlLeftX, rimY, bowlRightX, rimY, STYLE.bodyStroke));  // top rim line

    // R-rim callout (e.g. TP949 R61) at the front rim corner — the outer top edge
    // that seats into the bench when inset.
    if (mould.rimRadius) {
      svg.appendChild(svgLine(bowlRightX, rimY, bowlRightX - 3, rimY + 3, STYLE.dimLine));
      svg.appendChild(svgText('R' + mould.rimRadius, bowlRightX - 3.4, rimY + 4,
        Object.assign({}, STYLE.dimText, { 'text-anchor': 'end' })));
    }

    // Waste — tapers from Ø-top (at the slab) down through the slab to Ø-bottom.
    var wasteBotY = slabY + wasteD * s;
    svg.appendChild(svgPath(
      'M ' + (wcx - wTopHalf) + ' ' + slabY +
      ' L ' + (wcx - wBotHalf) + ' ' + wasteBotY +
      ' L ' + (wcx + wBotHalf) + ' ' + wasteBotY +
      ' L ' + (wcx + wTopHalf) + ' ' + slabY, STYLE.bodyStroke));

    // Tap deck (TP952/TP953) — flat platform at the rim on the tap (back) side.
    if (deckMM > 0) {
      svg.appendChild(svgLine(deckStartX, rimY, bowlLeftX, rimY, STYLE.bodyStroke));       // deck top
      svg.appendChild(svgLine(deckStartX, rimY, deckStartX, slabY, STYLE.bodyStroke));     // deck back drop
      var tapX = (deckStartX + bowlLeftX) / 2;                                             // tap hole on the deck
      svg.appendChild(svgCircle(tapX, rimY, 0.9, STYLE.bodyStroke));
    }

    // Dims (all on the RIGHT): total basin height (rim → bottom of waste) innermost,
    // then bowl height (rim → bowl floor) stacked above bench thickness alongside it;
    // bowl width (above) then bench width (below) along the bottom.
    drawDimensionLine(svg, dimRightX + 4, rimY, dimRightX + 4, wasteBotY, formatMm(Math.round(proudH + wasteD)));
    drawDimensionLine(svg, dimRightX + 9, rimY, dimRightX + 9, slabY, formatMm(Math.round(proudH)));
    if (!noBench) {
      drawDimensionLine(svg, dimRightX + 9, slabY, dimRightX + 9, slabY + slabH, formatMm(benchT));
    }
    drawDimensionLine(svg, bowlLeftX, slabY + slabH + 4, bowlRightX, slabY + slabH + 4, formatMm(Math.round(bowlWidthMM)));
    if (!noBench) {
      drawDimensionLine(svg, x0, slabY + slabH + 8, x0 + W * s, slabY + slabH + 8, formatMm(W));
    }
    return slabY + slabH + (noBench ? 5 : 8);
  }

  // Counter-top FRONT view (proud) — basin bowl(s) sit ON TOP of the bench slab,
  // sliced along the LENGTH axis. For a circular basin (TP949) this matches the
  // side section; for stadium/oblong moulds the bowl spans the (wider) length so
  // it differs from the side view, as expected. First pass — needs an eyeball.
  // Aproned counter-top FRONT view — rectangular vessel(s) sitting flush on the
  // bench, sliced along the LENGTH axis. An outer square around a curved bowl.
  function drawMouldedApronFrontView(svg, feX, feY, feW, feH, scalePlan, basinPositions, mould, noBench) {
    var apronH = mould.apronHeight || 150;
    var bowlD  = mould.bowlDepth || 130;
    var wasteD = Math.max(2, (mould.bowlWasteDepth || (bowlD + 12)) - bowlD);
    var lipR   = mould.lipRadius || 0;
    var apronLmm = mould.mouldLength;                  // apron footprint length (AL)
    var bowlLmm  = mould.bowlLength || apronLmm;       // bowl opening length (BL)
    var benchTopY = feY + apronH * scalePlan;          // bench pushed down by apron height
    var bottomY = benchTopY + feH;
    // Bench slab — omitted when there is no bench.
    if (!noBench) svg.appendChild(svgRect(feX, benchTopY, feW, feH, STYLE.bodyStrokeBold));
    var wTopHalf = (mould.wasteTopDia / 2) * scalePlan;
    var wBotHalf = (mould.wasteBotDia / 2) * scalePlan;
    var bowlFloorY = feY + bowlD * scalePlan;
    var wasteBotY  = feY + (bowlD + wasteD) * scalePlan;
    var lipPx = Math.min(lipR * scalePlan, apronH * scalePlan / 2);
    var bowlInsetPx = (apronLmm - bowlLmm) / 2 * scalePlan;   // bowl centred in the apron (length)
    for (var i = 0; i < basinPositions.length; i++) {
      var ax = basinPositions[i].x, aw = basinPositions[i].w; // apron footprint
      // Rim = apron edge (AL); waterline = bowl opening (BL), centred.
      var rimLeftX = ax, rimRightX = ax + aw;
      var wlLeftX = ax + bowlInsetPx, wlRightX = ax + aw - bowlInsetPx;
      var wcx = (wlLeftX + wlRightX) / 2;
      // Outer apron box — top line + left + right walls (R-lip is on the BOWL
      // rim). Only the full base line is replaced by FEET at each side (below).
      var apBaseY = feY + apronH * scalePlan;
      svg.appendChild(svgLine(ax, feY, ax + aw, feY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(ax, feY, ax, apBaseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(ax + aw, feY, ax + aw, apBaseY, STYLE.bodyStrokeBold));
      var feetFV = mould.feet && mould.feet.fv;
      if (feetFV) {
        var fvLpx = feetFV[0] * scalePlan, fvRpx = feetFV[1] * scalePlan;
        svg.appendChild(svgLine(ax, apBaseY, ax + fvLpx, apBaseY, STYLE.bodyStrokeBold));
        svg.appendChild(svgLine(ax + aw - fvRpx, apBaseY, ax + aw, apBaseY, STYLE.bodyStrokeBold));
        var fvDy = apBaseY + 2.5;
        drawDimensionLine(svg, ax, fvDy, ax + fvLpx, fvDy, formatMm(feetFV[0]), { labelBelow: true });
        drawDimensionLine(svg, ax + aw - fvRpx, fvDy, ax + aw, fvDy, formatMm(feetFV[1]), { labelBelow: true });
      } else {
        svg.appendChild(svgLine(ax, apBaseY, ax + aw, apBaseY, STYLE.bodyStrokeBold));
      }
      // Inner bowl — rim rolls over an R-lip from the apron edge IN to the bowl
      // (e.g. TP951 641→598); lipR = 0 → plain bowl with a short flat rim.
      var Lp = Math.min(lipPx, (wlRightX - wlLeftX) / 2, bowlD * scalePlan / 2);
      if (Lp > 0) {
        svg.appendChild(svgPath(
          'M ' + rimLeftX + ' ' + feY +
          ' Q ' + wlLeftX + ' ' + feY + ' ' + wlLeftX + ' ' + (feY + Lp) +
          ' Q ' + wlLeftX + ' ' + bowlFloorY + ' ' + (wcx - wTopHalf) + ' ' + bowlFloorY +
          ' L ' + (wcx + wTopHalf) + ' ' + bowlFloorY +
          ' Q ' + wlRightX + ' ' + bowlFloorY + ' ' + wlRightX + ' ' + (feY + Lp) +
          ' Q ' + wlRightX + ' ' + feY + ' ' + rimRightX + ' ' + feY, STYLE.bodyStroke));
      } else {
        if (wlLeftX - rimLeftX > 0.1) {
          svg.appendChild(svgLine(rimLeftX, feY, wlLeftX, feY, STYLE.bodyStroke));
          svg.appendChild(svgLine(wlRightX, feY, rimRightX, feY, STYLE.bodyStroke));
        }
        svg.appendChild(svgPath(
          'M ' + wlLeftX + ' ' + feY +
          ' Q ' + wlLeftX + ' ' + bowlFloorY + ' ' + (wcx - wTopHalf) + ' ' + bowlFloorY +
          ' L ' + (wcx + wTopHalf) + ' ' + bowlFloorY +
          ' Q ' + wlRightX + ' ' + bowlFloorY + ' ' + wlRightX + ' ' + feY, STYLE.bodyStroke));
      }
      // Waste tapering down inside the apron skirt.
      svg.appendChild(svgPath(
        'M ' + (wcx - wTopHalf) + ' ' + bowlFloorY +
        ' L ' + (wcx - wBotHalf) + ' ' + wasteBotY +
        ' L ' + (wcx + wBotHalf) + ' ' + wasteBotY +
        ' L ' + (wcx + wTopHalf) + ' ' + bowlFloorY, STYLE.bodyStroke));
    }
    // Dims — apron height (rim → bench) + bench height on the LEFT; bowl length
    // (above) then apron length (below) along the BOTTOM.
    drawDimensionLine(svg, feX - 4, feY, feX - 4, benchTopY, formatMm(Math.round(apronH)));
    if (!noBench) drawDimensionLine(svg, feX - 4, benchTopY, feX - 4, bottomY, formatMm(Math.round(feH / scalePlan)));
    if (basinPositions[0]) {
      var b0 = basinPositions[0];
      // ABOVE the basin (over the vessel, not below where the back view sits):
      // bowl length nearer the rim, apron length above it.
      drawDimensionLine(svg, b0.x + bowlInsetPx, feY - 4, b0.x + b0.w - bowlInsetPx, feY - 4, formatMm(Math.round(bowlLmm)));
      drawDimensionLine(svg, b0.x, feY - 8, b0.x + b0.w, feY - 8, formatMm(Math.round(apronLmm)));
    }
  }

  function drawMouldedCounterTopFrontView(svg, feX, feY, feW, feH, scalePlan, basinPositions, mould, noBench) {
    if (mould.apron) { drawMouldedApronFrontView(svg, feX, feY, feW, feH, scalePlan, basinPositions, mould, noBench); return; }
    var proudPx = mould.bowlDepth * scalePlan;         // basin proud height above bench
    var benchTopY = feY + proudPx;                     // bench pushed down; bowl above it
    var wasteD = (mould.mouldHeight && mould.bowlDepth)
      ? Math.max(2, mould.mouldHeight - mould.bowlDepth) : (mould.wasteDepth || 12);
    // Bench slab — omitted when there is no bench.
    if (!noBench) svg.appendChild(svgRect(feX, benchTopY, feW, feH, STYLE.bodyStrokeBold));
    var wTopHalf = (mould.wasteTopDia / 2) * scalePlan;
    var wBotHalf = (mould.wasteBotDia / 2) * scalePlan;
    for (var i = 0; i < basinPositions.length; i++) {
      var bpx = basinPositions[i].x, bpw = basinPositions[i].w;
      var wcx = bpx + bpw / 2;
      // Bowl proud: rim at feY, curving down to the waste opening. When an inner
      // rim is defined (TP949 450→434) the rim rolls off over an R-lip (rimRadius)
      // from the outer edge in to the inner opening, then the wall drops.
      var fInnerPx = (mould.bowlInner ? mould.bowlInner * scalePlan : bpw);
      var wlLf = wcx - fInnerPx / 2, wlRf = wcx + fInnerPx / 2;
      var LpF  = Math.min((mould.rimRadius || 0) * scalePlan, (benchTopY - feY) * 0.6);
      if (LpF > 0 && fInnerPx < bpw) {
        svg.appendChild(svgPath(
          'M ' + bpx + ' ' + feY +
          ' Q ' + wlLf + ' ' + feY + ' ' + wlLf + ' ' + (feY + LpF) +
          ' Q ' + wlLf + ' ' + benchTopY + ' ' + (wcx - wTopHalf) + ' ' + benchTopY +
          ' L ' + (wcx + wTopHalf) + ' ' + benchTopY +
          ' Q ' + wlRf + ' ' + benchTopY + ' ' + wlRf + ' ' + (feY + LpF) +
          ' Q ' + wlRf + ' ' + feY + ' ' + (bpx + bpw) + ' ' + feY, STYLE.bodyStroke));
      } else {
        svg.appendChild(svgPath(
          'M ' + bpx + ' ' + feY +
          ' Q ' + bpx + ' ' + benchTopY + ' ' + (wcx - wTopHalf) + ' ' + benchTopY +
          ' L ' + (wcx + wTopHalf) + ' ' + benchTopY +
          ' Q ' + (bpx + bpw) + ' ' + benchTopY + ' ' + (bpx + bpw) + ' ' + feY, STYLE.bodyStroke));
      }
      svg.appendChild(svgLine(bpx, feY, bpx + bpw, feY, STYLE.bodyStroke));  // top rim line
      // Waste tapering down through the slab.
      var wbY = benchTopY + wasteD * scalePlan;
      svg.appendChild(svgPath(
        'M ' + (wcx - wTopHalf) + ' ' + benchTopY +
        ' L ' + (wcx - wBotHalf) + ' ' + wbY +
        ' L ' + (wcx + wBotHalf) + ' ' + wbY +
        ' L ' + (wcx + wTopHalf) + ' ' + benchTopY, STYLE.bodyStroke));
    }

    // Dims — basin height (bowl depth, e.g. 145) + bench height on the LEFT;
    // basin length (first basin) + bench length along the BOTTOM.
    var bottomY = benchTopY + feH;
    var wasteBotY = benchTopY + wasteD * scalePlan;
    drawDimensionLine(svg, feX - 4, feY, feX - 4, benchTopY, formatMm(Math.round(mould.bowlDepth)));
    drawDimensionLine(svg, feX - 9, feY, feX - 9, wasteBotY, formatMm(Math.round(mould.bowlDepth + wasteD)));
    if (!noBench) drawDimensionLine(svg, feX - 4, benchTopY, feX - 4, bottomY, formatMm(Math.round(feH / scalePlan)));
    if (basinPositions[0]) {
      var b0 = basinPositions[0];
      drawDimensionLine(svg, b0.x, bottomY + 4, b0.x + b0.w, bottomY + 4, formatMm(Math.round(b0.w / scalePlan)));
    }
    // Bench length dim omitted here — it's already shown on the top + back views.
  }

  // Inset bowl FRONT view (TP957) — the same bowl as TP953 (roll-off lip from the
  // outer opening in to the inner waterline, curving to the waste) but RECESSED
  // into the bench (drawn inside the bench face rect). Length axis.
  function drawMouldedInsetBowlFrontView(svg, feX, feY, feW, feH, scalePlan, basinPositions, mould) {
    var bowlD  = mould.bowlDepth || 130;
    var wasteD = Math.max(2, (mould.bowlWasteDepth || (bowlD + 12)) - bowlD);
    var lipR   = mould.lipRadius || 0;
    var innerLmm = mould.bowlLength || (basinPositions[0] ? basinPositions[0].w / scalePlan : 0);
    var wTop = (mould.wasteTopDia / 2) * scalePlan, wBot = (mould.wasteBotDia / 2) * scalePlan;
    var floorY = feY + bowlD * scalePlan;
    var wasteBotY = feY + (bowlD + wasteD) * scalePlan;
    var Lp = Math.min(lipR * scalePlan, bowlD * scalePlan * 0.5);
    // Ceramic wall thickness (Grandios 20mm). When set, the bowl is drawn as TWO
    // profiles instead of one: the OUTER shell keeps the roll-off lip from the
    // outer opening in to the waterline and bottoms out `wall` deeper, where it
    // meets the waste taper; the INNER cavity starts straight at the waterline
    // (no lip roll) and follows the same curve to the waste opening. The gap
    // between them is the ceramic section.
    var wall = mould.wallThickness || 0;
    var shellFloorY = feY + (bowlD + wall) * scalePlan;
    // Half-width of the waste taper at the shell floor depth — the shell lands on
    // the Ø-top→Ø-bottom taper, not on a flat floor.
    var shellFrac = Math.min(1, wall / wasteD);
    var wShell = wTop + shellFrac * (wBot - wTop);
    for (var i = 0; i < basinPositions.length; i++) {
      var ax = basinPositions[i].x, aw = basinPositions[i].w;   // outer opening
      var cx = ax + aw / 2, ih = innerLmm * scalePlan / 2;
      var wlL = cx - ih, wlR = cx + ih;
      if (wall > 0) {
        // Outer shell — rim roll into a wall line `wall` outboard of the cavity
        // waterline (clamped to the outer opening), then the same curve to the
        // deeper shell floor. The roll spans the leftover rim shelf.
        var shL = Math.max(ax, wlL - wall * scalePlan);
        var shR = Math.min(ax + aw, wlR + wall * scalePlan);
        var shLp = Lp;   // full lip radius deep — see the side section for why
        // Rim edge — straight drop down the outer rim line before the roll starts.
        var shRimY = feY + (mould.rimEdge || 0) * scalePlan;
        svg.appendChild(svgPath(
          'M ' + ax + ' ' + feY +
          ' L ' + ax + ' ' + shRimY +
          ' Q ' + shL + ' ' + shRimY + ' ' + shL + ' ' + (shRimY + shLp) +
          ' Q ' + shL + ' ' + shellFloorY + ' ' + (cx - wShell) + ' ' + shellFloorY +
          ' M ' + (ax + aw) + ' ' + feY +
          ' L ' + (ax + aw) + ' ' + shRimY +
          ' Q ' + shR + ' ' + shRimY + ' ' + shR + ' ' + (shRimY + shLp) +
          ' Q ' + shR + ' ' + shellFloorY + ' ' + (cx + wShell) + ' ' + shellFloorY,
          STYLE.bodyStroke));
        // Inner cavity — starts AT the waterline, no lip roll.
        svg.appendChild(svgPath(
          'M ' + wlL + ' ' + feY +
          ' Q ' + wlL + ' ' + floorY + ' ' + (cx - wTop) + ' ' + floorY +
          ' L ' + (cx + wTop) + ' ' + floorY +
          ' Q ' + wlR + ' ' + floorY + ' ' + wlR + ' ' + feY, STYLE.bodyStroke));
      } else {
        svg.appendChild(svgPath(
          'M ' + ax + ' ' + feY +
          ' Q ' + wlL + ' ' + feY + ' ' + wlL + ' ' + (feY + Lp) +
          ' Q ' + wlL + ' ' + floorY + ' ' + (cx - wTop) + ' ' + floorY +
          ' L ' + (cx + wTop) + ' ' + floorY +
          ' Q ' + wlR + ' ' + floorY + ' ' + wlR + ' ' + (feY + Lp) +
          ' Q ' + wlR + ' ' + feY + ' ' + (ax + aw) + ' ' + feY, STYLE.bodyStroke));
      }
      svg.appendChild(svgPath(
        'M ' + (cx - wTop) + ' ' + floorY +
        ' L ' + (cx - wBot) + ' ' + wasteBotY +
        ' L ' + (cx + wBot) + ' ' + wasteBotY +
        ' L ' + (cx + wTop) + ' ' + floorY, STYLE.bodyStroke));
    }
    // Dims: bowl depth + bowl&waste on the LEFT; inner + outer opening ABOVE.
    drawDimensionLine(svg, feX - 4, feY, feX - 4, floorY, formatMm(Math.round(bowlD)));
    drawDimensionLine(svg, feX - 9, feY, feX - 9, wasteBotY, formatMm(Math.round(bowlD + wasteD)));
    if (basinPositions[0]) {
      var b0 = basinPositions[0], c0 = b0.x + b0.w / 2, ih0 = innerLmm * scalePlan / 2;
      drawDimensionLine(svg, c0 - ih0, feY - 4, c0 + ih0, feY - 4, formatMm(Math.round(innerLmm)));
      drawDimensionLine(svg, b0.x, feY - 8, b0.x + b0.w, feY - 8, formatMm(Math.round(b0.w / scalePlan)));
    }
  }

  // Inset bowl SIDE cross-section (TP957) — non-rotated, width axis. Bench slab
  // with the bowl recessed (roll-off lip outer→inner, curve to waste). Returns the
  // bottom Y so the drainage detail can sit below.
  function drawMouldedInsetBowlSection(svg, o) {
    var mould = o.mould;
    var W = o.wid, benchT = o.hgt;
    var bowlD  = mould.bowlDepth || 130;
    var wasteD = Math.max(2, (mould.bowlWasteDepth || (bowlD + 12)) - bowlD);
    var lipR   = mould.lipRadius || 0;
    var outerWmm = mould.mouldWidth;
    var innerWmm = mould.bowlWidthInner || outerWmm;
    // Scale to the DEEPER of the bench or the bowl+waste — the bowl hangs below a
    // thin bench, so it must not get clipped or overlapped by the dims below.
    var totalH = Math.max(benchT, bowlD + wasteD);
    var s = Math.min(o.availW / W, o.availH / totalH);
    var x0 = o.sx + 4, y0 = o.titleY + 12;
    var slabH = benchT * s;
    // Bench slab (full width) + hatching.
    svg.appendChild(svgRect(x0, y0, W * s, slabH, STYLE.bodyStrokeBold));
    drawHatching(svg, { x: x0, y: y0, w: W * s, h: slabH }, { spacing: 0.55 });
    // Bowl recessed from the bench top, centred across the mould footprint.
    var bLeft = x0 + o.backInset * s, bRight = bLeft + outerWmm * s;
    var cx = (bLeft + bRight) / 2, ih = innerWmm * s / 2;
    var wlL = cx - ih, wlR = cx + ih;
    var floorY = y0 + bowlD * s, wasteBotY = y0 + (bowlD + wasteD) * s;
    var wTop = (mould.wasteTopDia / 2) * s, wBot = (mould.wasteBotDia / 2) * s;
    var Lp = Math.min(lipR * s, bowlD * s * 0.5);
    svg.appendChild(svgPath(
      'M ' + bLeft + ' ' + y0 +
      ' Q ' + wlL + ' ' + y0 + ' ' + wlL + ' ' + (y0 + Lp) +
      ' Q ' + wlL + ' ' + floorY + ' ' + (cx - wTop) + ' ' + floorY +
      ' L ' + (cx + wTop) + ' ' + floorY +
      ' Q ' + wlR + ' ' + floorY + ' ' + wlR + ' ' + (y0 + Lp) +
      ' Q ' + wlR + ' ' + y0 + ' ' + bRight + ' ' + y0, STYLE.bodyStroke));
    svg.appendChild(svgPath(
      'M ' + (cx - wTop) + ' ' + floorY +
      ' L ' + (cx - wBot) + ' ' + wasteBotY +
      ' L ' + (cx + wBot) + ' ' + wasteBotY +
      ' L ' + (cx + wTop) + ' ' + floorY, STYLE.bodyStroke));
    // Dims: bowl depth + bowl&waste (RIGHT); bench height; bowl widths (bottom).
    drawDimensionLine(svg, x0 + W * s + 4, y0, x0 + W * s + 4, floorY, formatMm(Math.round(bowlD)));
    drawDimensionLine(svg, x0 + W * s + 9, y0, x0 + W * s + 9, wasteBotY, formatMm(Math.round(bowlD + wasteD)));
    drawDimensionLine(svg, x0 + W * s + 14, y0, x0 + W * s + 14, y0 + slabH, formatMm(benchT));
    // Bottom dims + (below them) the drainage detail anchor to the DEEPEST drawn
    // point — the bowl bottom when it hangs below a thin bench — so they don't ride
    // up with the bench height and overlap the bowl.
    var dRow = Math.max(y0 + slabH, wasteBotY) + 4;
    drawDimensionLine(svg, wlL, dRow, wlR, dRow, formatMm(Math.round(innerWmm)));
    dRow += 4;
    drawDimensionLine(svg, bLeft, dRow, bRight, dRow, formatMm(Math.round(outerWmm)));
    dRow += 4;
    drawDimensionLine(svg, x0, dRow, x0 + W * s, dRow, formatMm(Math.round(W)));
    return dRow;
  }

  function renderMouldedFormline(design) {
    // Resolve the mould (Kreno Formline by default; Smoothe per `mould` param).
    // Normalise legacy ca* keys BEFORE the Kreno fallback — an old link would
    // otherwise draw a 530 × 310 Kreno under a Smoothe heading (see
    // LEGACY_MOULD_KEYS). Written back onto the design so every downstream
    // reader, and anything this render re-emits, carries the sm* key.
    design.mould = normaliseMouldKey(design.mould);
    var mould = MOULD_SPECS[design.mould] || MOULD_SPECS.kreno;

    // Number of basins per model variant (count param wins; else friendly word).
    var BASIN_COUNT_BY_MODEL = { single: 1, double: 2, triple: 3, quadruple: 4, quintuple: 5, sextuple: 6 };
    var basinCount = parseInt(design.count, 10) || BASIN_COUNT_BY_MODEL[design.model] || 1;

    // Header title = brand + mould label (Kreno keeps the legacy Formline name
    // from MODEL_LABELS). Covers Smoothe / Grandios / Ferra.
    if (!design.modelName && mould.brand !== 'kreno') {
      var _brandLabel = { smoothe: 'Smoothe', grandios: 'Grandios', ferra: 'Ferra' }[mould.brand] || mould.brand;
      design.modelName = _brandLabel + ' ' + mould.label;
    }

    var svg = createSpecSheetSvg();
    renderHeader(svg, design);
    var infoPanelEndY = renderRightInfoPanel(svg, design);
    renderFooterContacts(svg);

    var entry = MODEL_REGISTRY[design.model] || MODEL_REGISTRY.single;
    var family = entry.family;                       // 'moulded'
    var constants = FAMILY_CONSTANTS[family];

    var len = parseFloat(design.length) || 1200;
    var wid = parseFloat(design.width)  || entry.widthDefault || 480;
    var hgt = parseFloat(design.height) || 200;
    var dInset = parseFloat(design.d) || 75;
    var gap = parseFloat(design.gap) || 150;

    // Basin mould opening — per-mould (Kreno 530×310; Smoothe per model).
    var basinLength = mould.mouldLength;
    var basinWidth  = mould.mouldWidth;

    // Width breakdown. Smoothe (derived G): E is the back inset, G follows
    // (G = wid − E − mouldWidth). Kreno (select G): G is 20/40, back inset is
    // the remainder.
    var frontInset, backInset;
    if (mould.gMode === 'derived') {
      backInset  = parseFloat(design.e) || 100;
      frontInset = Math.max(0, wid - backInset - basinWidth);
    } else {
      frontInset = parseFloat(design.g) === 40 ? 40 : 20;
      backInset  = Math.max(0, wid - frontInset - basinWidth);
    }

    // NO-BENCH (composition = none): a single counter-top basin sold WITHOUT a
    // bench. The "bench" collapses to the basin footprint (centred, no insets) so
    // the shared plan loop draws the basin alone; the bench outline, bench dims,
    // soap/dryer/tap chains and Back view are all suppressed below.
    var isNoBench = String(design.composition) === 'none';
    if (isNoBench) {
      basinCount = 1;
      len = basinLength; wid = basinWidth;
      dInset = 0; gap = 0; backInset = 0; frontInset = 0;
    }

    // ===== TOP PLAN VIEW =====
    // Top view lifted ~20px (5 units) so the tap / soap / dryer / waste dim
    // chains hanging below it clear the "Front view" title. planToFrontGap is
    // bumped by the same 5 below, so the front view block holds its position.
    var planArea = { x: DRAW_AREA.x + 14, y: DRAW_AREA.y + 7, w: DRAW_AREA.w - 18, h: isNoBench ? 85 : 50 };
    var scalePlan = Math.min(planArea.w / len, planArea.h / wid);

    var bx = planArea.x + (isNoBench ? Math.max(0, (planArea.w - len * scalePlan) / 2) : 0);
    var by = planArea.y;
    var bw = len * scalePlan;
    var bh = wid * scalePlan;

    drawViewLabel(svg, 'Top view', by - 12);
    // Bench top outline — rounded corners for Smoothe (front = the two front-edge
    // corners, i.e. bottom of the plan; all = all four). 'none'/Kreno = square.
    // No-bench: no bench outline (the basin footprint below is the only outline).
    if (!isNoBench) drawBenchTopOutline(svg, bx, by, bw, bh, design.corners || 'none');

    var basinY = by + backInset * scalePlan;
    var basinH = basinWidth * scalePlan;
    var basinPositions = []; // store for the front elevation

    var enableTaps = design.taps !== '0' && design.taps !== 0;

    // ── Centre / Left / Right (Single mould only) ──
    // The moulded builder sets basinPosition + basinEdge + basinLength on the
    // URL when the user picks Left/Right on the Single mould. basinEdge is
    // measured from the chosen side (Left → from bench-left to basin's right
    // edge; Right → from bench-right to basin's left edge). Translate that
    // into a single basinLeftOffsetMm from bench-left so the rest of the
    // plan-view code can be agnostic.
    var pos = (basinCount === 1) ? (design.basinPosition || 'center') : 'center';
    var basinStartFor = function (i) {
      if (pos === 'left' && design.basinEdge && design.basinLength) {
        var bl = parseFloat(design.basinLength);
        return Math.max(0, parseFloat(design.basinEdge) - bl);
      }
      if (pos === 'right' && design.basinEdge && design.basinLength) {
        return Math.max(0, len - parseFloat(design.basinEdge));
      }
      // Centre / multi-basin: original symmetric layout.
      return dInset + i * (basinLength + gap);
    };
    // Asymmetric end paddings. For Centre / multi-basin both are dInset; for
    // single Left, leftPad = D and rightPad = len − D − 530; for single Right
    // the two swap. Used by the length dim lines below.
    var leftPad, rightPad;
    if (basinCount === 1 && (pos === 'left' || pos === 'right')) {
      leftPad  = basinStartFor(0);
      rightPad = Math.max(0, len - leftPad - basinLength);
    } else {
      leftPad  = dInset;
      rightPad = dInset;
    }

    var tapBenchOffsets = []; // collect tap centre positions (mm from bench-left) for the dim chain
    var wasteCentresMm  = []; // waste centres (mm from bench-left) for the waste dim chain
    var wasteCentreY    = null; // waste centreline Y (SVG units) — same for every basin
    var wasteCentreYmm  = null; // …and the same in mm from the bench's BACK edge
    for (var bi = 0; bi < basinCount; bi++) {
      var basinStartMm = basinStartFor(bi);
      var basinX = bx + basinStartMm * scalePlan;
      var basinW = basinLength * scalePlan;
      var cutoutRpx = mould.cornerRadius ? mould.cornerRadius * scalePlan : null;
      // Aproned deck models (TP952/953): the apron footprint has a SQUARE back
      // (tap-deck) and a curved front. Ellipse-bowl models (TP953) trace the OUTER
      // bowl ellipse across the front; rounded models (TP952) round the front
      // corners by cornerRadius. Front = bottom of the plan.
      if (mould.apron && mould.tapDeck && mould.bowlShape === 'ellipse') {
        var exBw = mould.bowlWidth * scalePlan;                     // outer ellipse width
        var eyc = basinY + basinH - exBw / 2 - (mould.frontLip || 0) * scalePlan;  // ellipse centre
        var erx = basinW / 2, ery = exBw / 2;
        svg.appendChild(svgPath(
          'M ' + basinX + ' ' + basinY +
          ' L ' + (basinX + basinW) + ' ' + basinY +
          ' L ' + (basinX + basinW) + ' ' + eyc +
          ' A ' + erx + ' ' + ery + ' 0 0 1 ' + basinX + ' ' + eyc +
          ' Z', STYLE.bodyStroke));
      } else if (mould.apron && mould.tapDeck) {
        var apR = mould.cornerRadius ? mould.cornerRadius * scalePlan
                : (mould.shape === 'stadium' ? Math.min(basinW, basinH) / 2 : 0);
        apR = Math.min(apR, basinW / 2, basinH / 2);
        svg.appendChild(svgPath(
          'M ' + basinX + ' ' + basinY +
          ' L ' + (basinX + basinW) + ' ' + basinY +
          ' L ' + (basinX + basinW) + ' ' + (basinY + basinH - apR) +
          ' Q ' + (basinX + basinW) + ' ' + (basinY + basinH) + ' ' + (basinX + basinW - apR) + ' ' + (basinY + basinH) +
          ' L ' + (basinX + apR) + ' ' + (basinY + basinH) +
          ' Q ' + basinX + ' ' + (basinY + basinH) + ' ' + basinX + ' ' + (basinY + basinH - apR) +
          ' Z', STYLE.bodyStroke));
      } else {
        drawBasinCutout(svg, basinX, basinY, basinW, basinH, family, cutoutRpx, mould.shape);
      }
      basinPositions.push({ x: basinX, w: basinW, startMm: basinStartMm });

      // Aproned counter-tops (TP950–953): the cutout above is the APRON footprint;
      // nest the bowl opening (BL×BW) inside it — centred on the length, and
      // (deck models) toward the FRONT (minus any front lip) with the tap deck at
      // the back, else centred on the width. TP953 also gets an inner bowl ring.
      var apronBowlY = basinY;                        // back edge of the bowl (deck strip sits above it)
      // Waste: centred on the basin LENGTH, and set in from the basin's BACK edge
      // by the mould's own figure (CA08 160; everything else defaults to the bowl
      // centre — see getWasteFromBasinEdge).
      var wasteCx = basinX + basinW / 2;
      var wasteFromEdgeMm = getWasteFromBasinEdge(mould, basinWidth);
      var wasteCy = basinY + wasteFromEdgeMm * scalePlan;
      if (mould.apron && mould.bowlLength) {
        var bowlShp = mould.bowlShape || mould.shape;
        var blPx = (mould.bowlOuterLength || mould.bowlLength) * scalePlan;  // OUTER bowl length
        var bwPx = mould.bowlWidth * scalePlan;                             // OUTER bowl width
        var frontLipPx = (mould.frontLip || 0) * scalePlan;
        var bowlX = basinX + (basinW - blPx) / 2;
        apronBowlY = mould.tapDeck ? (basinY + basinH - bwPx - frontLipPx) : (basinY + (basinH - bwPx) / 2);
        drawBasinCutout(svg, bowlX, apronBowlY, blPx, bwPx, family, cutoutRpx, bowlShp);
        // Inner opening (TP953): bowlLength × bowlWidthInner, concentric.
        if (mould.bowlWidthInner) {
          var inLpx = mould.bowlLength * scalePlan;
          var inWpx = mould.bowlWidthInner * scalePlan;
          drawBasinCutout(svg, bowlX + (blPx - inLpx) / 2, apronBowlY + (bwPx - inWpx) / 2, inLpx, inWpx, family, cutoutRpx, bowlShp);
        }
        wasteCx = bowlX + blPx / 2;
        // Measured from the BOWL's back edge here (the apron/deck sits above it).
        wasteFromEdgeMm = getWasteFromBasinEdge(mould, mould.bowlWidth);
        wasteCy = apronBowlY + wasteFromEdgeMm * scalePlan;
      } else if (mould.bowlWidthInner && mould.bowlLength) {
        // Inset mould with an inner waterline (TP957): nest the inner opening
        // concentric within the bench cutout — same bowl as TP953, no apron.
        var inLpx0 = mould.bowlLength * scalePlan;
        var inWpx0 = mould.bowlWidthInner * scalePlan;
        drawBasinCutout(svg, basinX + (basinW - inLpx0) / 2, basinY + (basinH - inWpx0) / 2, inLpx0, inWpx0, family, cutoutRpx, mould.shape);
      }

      // Corner-radius callout (e.g. CA16 R70, Kreno R55) at the top-LEFT corner —
      // first basin only. For aproned moulds it marks the BOWL corner (the apron/
      // deck itself is square); otherwise the basin corner.
      var cornerLabelY = basinY - 1.6;
      if (bi === 0 && mould.cornerRadius) {
        var isAp = mould.apron && mould.bowlLength;
        var rcX = isAp ? bowlX : basinX;
        var rcY = isAp ? apronBowlY : basinY;
        var rcH = isAp ? bwPx : basinH;
        var rR = Math.min(mould.cornerRadius * scalePlan, rcH / 2);
        var arcX = rcX + rR - rR * 0.707;
        var arcY = rcY + rR - rR * 0.707;
        var cornerLabelShift = 5;
        svg.appendChild(svgLine(arcX, arcY, rcX + rR - cornerLabelShift, rcY - 1.2, STYLE.dimLine));
        svg.appendChild(svgText('R' + mould.cornerRadius, rcX + rR + 0.5 - cornerLabelShift, rcY - 1.6, STYLE.dimText));
      }

      // Tap hole — Ø35. Aproned deck models (TP952/953) place it ON the deck
      // (back strip inside the apron); everything else uses the back inset zone.
      if (enableTaps) {
        var tapsCx = wasteCx;
        // Datum: the back-inset row is measured UP FROM basinY, never down from
        // `by` — see tapRowDist()'s note. The old `by + backInset/2` form only
        // worked because the midpoint is symmetric about the strip.
        var tapsY = (mould.apron && mould.tapDeck)
          ? (basinY + apronBowlY) / 2
          : basinY - mouldedTapRowDist(design, mould, backInset) * scalePlan;
        drawTapHole(svg, tapsCx, tapsY, { showCallout: bi === 0, calloutY: cornerLabelY });
        tapBenchOffsets.push(basinStartMm + basinLength / 2);
      }

      // Waste rosette — Ø35/Ø70/Ø45 nested rings (drawWasteRosette handles moulded family)
      drawWasteRosette(svg, wasteCx, wasteCy, family, scalePlan, mould);
      // Record the waste centre for the two dim lines added below the plan: the
      // chained along-the-bench row, and the back-edge → centre setback.
      // Push the EXACT mm rather than converting wasteCx back out of pixels —
      // the round-trip loses precision and a centre landing on x.5 (e.g. CA08's
      // 399.5) rounded DOWN here while the tap chain, which carries exact mm,
      // rounded up, so the two rows disagreed by 1mm. wasteCx is always the
      // basin's length centre (the bowl is centred on the apron/deck moulds too).
      wasteCentresMm.push(basinStartMm + basinLength / 2);
      wasteCentreY = wasteCy;
      // Setback shown on the spec sheet = bench back edge → waste centre, i.e.
      // E + the mould's basin-edge figure. DERIVED from the placement datum (not
      // the other way round) so it tracks E automatically: widen the bench and
      // this figure grows while the waste stays put inside the bowl.
      // (apronBowlY − basinY accounts for the deck strip on TP952/953.)
      wasteCentreYmm = backInset + (apronBowlY - basinY) / scalePlan + wasteFromEdgeMm;
      // Waste hole Ø callouts (outer / mid / inner) — first basin, with a leader.
      if (bi === 0) {
        var wcx0 = wasteCx, wcy0 = wasteCy;
        var wasteDims = 'Ø' + mould.wasteTopDia +
          (mould.wasteMidDia ? ' / Ø' + mould.wasteMidDia : '') +
          ' / Ø' + mould.wasteBotDia;
        svg.appendChild(svgLine(wcx0, wcy0, wcx0 + 7, wcy0 - 5, STYLE.dimLine));
        svg.appendChild(svgText(wasteDims, wcx0 + 7.5, wcy0 - 5,
          Object.assign({}, STYLE.dimText, { 'text-anchor': 'start' })));
      }
    }

    // Chained tap-position dim row below the plan view — same pattern as the
    // Ramp Classic / Trough / D-Airblade renderers so all spec sheets look
    // consistent. Skips entirely when taps are disabled (no offsets collected).
    var mouldedTapDimY = by + bh + 3.5;
    if (!isNoBench && tapBenchOffsets.length) {
      drawTapDimChain(svg, bx, bw, scalePlan, mouldedTapDimY, tapBenchOffsets);
    }

    // Bench-relative accessory rows + insets — suppressed entirely for no-bench.
    if (!isNoBench) {
      // Dim rows stack below the plan in this order: tap → soap → dryer → waste.
      // Only rows that are actually drawn take a slot — soap + dryer are both
      // derived from the tap row, so a tap-less mould (TP949/950/951) drops the
      // waste row straight into the first slot instead of leaving gaps.
      var hasTapRow  = tapBenchOffsets.length > 0;
      var accRowsM   = hasTapRow
        ? 1 + (getSoapConfig(design) ? 1 : 0) + (getDryerConfig(design) ? 1 : 0)
        : 0;
      var totalRowsM = accRowsM + (wasteCentresMm.length ? 1 : 0);
      // The front elevation's top edge caps the stack. Tall benches (hgt > 200)
      // run a tighter plan→front gap AND have no spare page height below (they
      // already finish ~0.3 above the footer line), so the pitch shrinks to fit
      // rather than letting the last row's label print into the front view.
      // 4.0 = labelOffset (1.0) + 2.4 font + descender/clearance.
      var frontTopYM = planArea.y + planArea.h + (hgt > 200 ? 21 : 31) - 4;
      var availM     = frontTopYM - 4.0 - mouldedTapDimY;
      var rowPitchM  = 4.5;
      if (totalRowsM > 1) rowPitchM = Math.max(3.2, Math.min(4.5, availM / (totalRowsM - 1)));

      // Soap dispenser holes — sit in the back inset zone alongside the taps.
      drawSoapDispenserPlan(svg, design, bx, by, bw, bh, scalePlan, basinY, tapBenchOffsets, len, mouldedTapDimY, rowPitchM);
      // Dryer holes — backplate behind each.
      drawDryerHolePlan(svg, design, bx, by, bw, bh, scalePlan, basinY, tapBenchOffsets, len,
        mouldedTapDimY + rowPitchM * (getSoapConfig(design) ? 2 : 1));
      // distFromBasin dims for tap / soap / dryer rows — right-side stack, dedup'd.
      // Tap-deck moulds (TP952/TP953) sit on the basin's apron deck, not in the
      // back-inset band, so their tap row has no offset to dimension here (null
      // suppresses it, same as wall-mounted taps on the ramp D-Airblade).
      drawBackInsetOffsetDims(svg, design, bx, bw, scalePlan, basinY, tapBenchOffsets, len,
        (mould.apron && mould.tapDeck) ? null : mouldedTapRowDist(design, mould, backInset));

      // Waste-position dim chain — edge → waste → … → edge, below the accessory
      // rows. Mirrors renderRampClassic's waste row.
      if (wasteCentresMm.length) {
        drawTapDimChain(svg, bx, bw, scalePlan, mouldedTapDimY + rowPitchM * accRowsM, wasteCentresMm);
      }
    }

    // Waste setback — bench back edge → waste centreline. Placed just outboard of
    // the bench's RIGHT edge: the width chain owns the left side, and the
    // distFromBasin witnesses sit just INSIDE the right edge, so this is the only
    // clear lane. Label flipped right (labelOffset 3) to clear its own dim line.
    if (wasteCentreY != null) {
      var wsX = bx + bw + 3;
      // Label comes from the mm value carried out of the loop, not from measuring
      // the drawn pixels back out (see wasteCentresMm for why that misrounds).
      drawDimensionLine(svg, wsX, by, wsX, wasteCentreY, formatMm(wasteCentreYmm),
        { labelRight: true, labelOffset: 3 });
    }

    if (isNoBench) {
      // Basin footprint dims: apron length (AL) above; on the LEFT, apron width
      // (AW) outboard + bowl inner width (BWI) nearer the basin, centred on the bowl.
      drawDimensionLine(svg, bx, by - 4, bx + bw, by - 4, formatMm(len));
      if (mould.bowlWidth) {
        drawDimensionLine(svg, bx - 9, by, bx - 9, by + bh, formatMm(wid));       // AW (moved left)
        var nbBWI   = mould.bowlWidthInner || mould.bowlWidth;                    // 378
        var nbBWIpx = nbBWI * scalePlan;
        var nbCy    = (typeof wasteCy !== 'undefined') ? wasteCy : (by + bh / 2); // bowl centre
        drawDimensionLine(svg, bx - 4, nbCy - nbBWIpx / 2, bx - 4, nbCy + nbBWIpx / 2, formatMm(Math.round(nbBWI)));
      } else {
        drawDimensionLine(svg, bx - 4, by, bx - 4, by + bh, formatMm(wid));       // AW only (TP949)
      }
    } else {
      // Length dimensions (above bench): overall + breakdown leftPad | basin | gap | basin | ... | rightPad
      drawDimensionLine(svg, bx, by - 8, bx + bw, by - 8, formatMm(len));
      drawDimensionLine(svg, bx, by - 3.5, bx + leftPad * scalePlan, by - 3.5, formatMm(Math.round(leftPad)));
      for (var bdi = 0; bdi < basinCount; bdi++) {
        var bsStart = basinStartFor(bdi);
        var bsEnd = bsStart + basinLength;
        drawDimensionLine(svg, bx + bsStart * scalePlan, by - 3.5, bx + bsEnd * scalePlan, by - 3.5, formatMm(basinLength));
        if (bdi < basinCount - 1) {
          drawDimensionLine(svg, bx + bsEnd * scalePlan, by - 3.5, bx + (bsEnd + gap) * scalePlan, by - 3.5, formatMm(gap));
        }
      }
      drawDimensionLine(svg, bx + (len - rightPad) * scalePlan, by - 3.5, bx + bw, by - 3.5, formatMm(Math.round(rightPad)));

      // Width dimensions (left of bench)
      drawDimensionLine(svg, bx - 4, by, bx - 4, by + backInset * scalePlan, formatMm(backInset));
      drawDimensionLine(svg, bx - 4, by + backInset * scalePlan, bx - 4, by + (backInset + basinWidth) * scalePlan, formatMm(basinWidth));
      drawDimensionLine(svg, bx - 4, by + (backInset + basinWidth) * scalePlan, bx - 4, by + bh, formatMm(frontInset));
      drawDimensionLine(svg, bx - 9, by, bx - 9, by + bh, formatMm(wid));
    }

    // ===== FRONT ELEVATION =====
    // Wider plan→front gap on standard-height benches (see renderRampClassic).
    // Moulded default is 200mm so this evaluates to the wider 26mm gap.
    var planToFrontGap = hgt > 200 ? 21 : 31;
    var feY = planArea.y + planArea.h + planToFrontGap;
    var feX = bx;
    var feW = bw;
    var feH = hgt * scalePlan;
    // Length dim removed (already on the top + back views); lift the diagram
    // ~15px (4 units) into the freed space. The view title now sits vertically
    // to the LEFT of the diagram (drawViewLabelLeft) instead of above it.
    feY -= 4;
    if (isNoBench) feH = 0;                            // no bench slab below the vessel

    // For Smoothe the bowl + waste hang BELOW the (often thin/flat) bench, so the
    // front view occupies more vertical space than the bench height alone.
    var isSmoothe = mould.brand === 'smoothe';
    // Moulds with an overflow (Smoothe + Kreno) get the full bowl + waste +
    // overflow front view and the cross-section overflow channel.
    var showBowl = isSmoothe || mould.overflow === true;
    // Overflow is a SEPARATE question from showBowl — CA13 draws the full bowl but
    // has no overflow slot or channel. See hasOverflow().
    var showOverflow = hasOverflow(mould);
    // Inset bowls with an inner waterline (Grandios SN0##, Ferra TP957) draw a
    // curved bowl cross-section on the front face too, and a deep ceramic bowl
    // (SN023 = 200 to the waste) hangs below a 160 bench — so they need the same
    // "reserve the bowl drop" layout allowance as Smoothe.
    var insetBowlFront = !!(mould.bowlWidthInner && !mould.apron);
    var feMouldDrop = (mould.bowlDepth + mould.wasteDepth) * scalePlan;
    var feLayoutH = (showBowl || insetBowlFront) ? Math.max(feH, feMouldDrop) : feH;

    drawViewLabelLeft(svg, 'Front view', feX, feY, feLayoutH);
    if (String(design.mountType) === 'top') {
      // Counter-top: proud front cross-section (basin(s) sit on top of the bench).
      drawMouldedCounterTopFrontView(svg, feX, feY, feW, feH, scalePlan, basinPositions, mould, isNoBench);
    } else {
    svg.appendChild(svgRect(feX, feY, feW, feH, STYLE.bodyStrokeBold));

    if (insetBowlFront) {
      // Inset bowl (Ferra TP957, Grandios SN0##) — bowl recessed into the bench face.
      drawMouldedInsetBowlFrontView(svg, feX, feY, feW, feH, scalePlan, basinPositions, mould);
    } else if (showBowl) {
      // Bowl front view — show the basin bowl, the (tapered or straight) waste
      // and the overflow slot, plus bench-height + basin-depth dims.
      var feBowlDrop = mould.bowlDepth * scalePlan;
      var ov    = mould.overflowSlot || SMOOTHE_OVERFLOW;  // per-mould overflow slot (CA16 = 55×6)
      var ofW   = ov.width  * scalePlan;
      var ofH   = ov.height * scalePlan;
      var ofTop = feY + ov.fromTop * scalePlan;
      for (var bfi = 0; bfi < basinCount; bfi++) {
        var feBP = basinPositions[bfi];
        var bpx = feBP.x, bpw = feBP.w;
        // Floor-corner radius. 'square' bowls (CA08) take the mould's own small
        // figure so the bowl reads as a box with barely-eased bottom corners;
        // the curved profiles keep the proportional radius (~75mm on a 495×125
        // bowl, which is what made CA08 look rounded before).
        var rr = isSquareBowl(mould)
          ? Math.min(bowlFloorRadiusMm(mould) * scalePlan, bpw / 2, feBowlDrop)
          : Math.min(bpw * 0.18, feBowlDrop * 0.6);
        var feFloorY = feY + feBowlDrop;
        var wcx = bpx + bpw / 2;
        var wTopHalf = (mould.wasteTopDia / 2) * scalePlan;
        var wBotHalf = (mould.wasteBotDia / 2) * scalePlan;
        var bowlPath;
        if (isDishBowl(mould)) {
          // Curved dish bowl (CA12) — one cubic per half, mirrored, sized to match the
          // mould's A-A section. DISH_* are fractions of the opening width / bowl depth:
          // the first control pulls almost straight down just inside the rim (crisp rim
          // radius, steep wall), the second sits ON the floor line a fifth of the way
          // across (so the wall has fully flattened by then and the floor reads broad).
          // A short level pad either side of the waste keeps the waste taper meeting a
          // flat floor rather than a point.
          var dRimX = dishDial(mould, 'rimX', DISH_RIM_X),
              dRimY = dishDial(mould, 'rimY', DISH_RIM_Y),
              dEase = dishDial(mould, 'easeX', DISH_EASE_X);
          var feRad = rimAngleRad(mould);
          var dFlat = Math.max(wTopHalf, bpw * 0.05);
          var dL = wcx - dFlat, dR = wcx + dFlat;
          // Roll-off lip (CA13): the top face turns down through `rimRoll` before the
          // wall drops, so the rim reads as a soft convex roll instead of a hard
          // edge. Drawn as a quadratic whose control sits at the rim CORNER — the
          // curve leaves the top face horizontally and arrives vertical, which is
          // exactly a fillet. The dish cubic then starts from the roll's end point,
          // with its rim control shifted in by the same amount so the two join
          // tangentially. rimRoll 0 (every other mould) collapses this to nothing.
          // NOTE ON THE ARITHMETIC: every one of these is written so that a roll of 0
          // reduces to EXACTLY the pre-roll expression, bit for bit. Deriving them the
          // obvious way instead (wallW = wallTopR - wallTopL, wallDrop = feFloorY -
          // wallTopY) is equal in exact arithmetic but not in floating point, and the
          // last-digit drift showed up as churn in the path of every roll-free dish
          // mould. Keep the `roll ? … : <original>` shape when editing.
          var roll = Math.min(rimRollMm(mould) * scalePlan, bpw * 0.25, feBowlDrop * 0.6);
          var wallTopL = roll ? bpx + roll : bpx;              // wall starts after the roll
          var wallW    = roll ? bpw - 2 * roll : bpw;
          var wallTopR = roll ? wallTopL + wallW : bpx + bpw;
          var wallTopY = roll ? feY + roll : feY;
          var wallDrop = roll ? feBowlDrop - roll : feBowlDrop;
          var rollIn  = roll ? (' Q ' + wallTopL + ' ' + feY + ' ' + wallTopL + ' ' + wallTopY) : '';
          var rollOut = roll ? (' Q ' + wallTopR + ' ' + feY + ' ' + (bpx + bpw) + ' ' + feY) : '';
          // First control: either straight off the stated rim ANGLE, or the legacy
          // rimX/rimY fractions. `dL - wallTopL` is the room along the face before the
          // floor pad begins, which is the correct bound for the angled step.
          var feStep = feRad
            ? rimAngleStep(feRad, dishDial(mould, 'rimRun', 0.55),
                           Math.max(0, dL - wallTopL), wallDrop)
            : null;
          var c1x = feStep ? (wallTopL + feStep.along) : (wallTopL + wallW * dRimX);
          var c1y = feStep ? (wallTopY + feStep.down)  : (wallTopY + wallDrop * dRimY);
          var c1xR = feStep ? (wallTopR - feStep.along) : (wallTopL + wallW * (1 - dRimX));
          bowlPath =
            'M ' + bpx + ' ' + feY + rollIn +
            ' C ' + c1x + ' ' + c1y +
              ' '  + (wallTopL + wallW * dEase) + ' ' + feFloorY +
              ' '  + dL + ' ' + feFloorY +
            ' L ' + dR + ' ' + feFloorY +
            ' C ' + (wallTopL + wallW * (1 - dEase)) + ' ' + feFloorY +
              ' '  + c1xR + ' ' + c1y +
              ' '  + wallTopR + ' ' + wallTopY +
            rollOut;
        } else if (mould.shallowBowl) {
          // Shallow bowl (Kreno) — no vertical walls. A gentle curve runs from
          // the top rim almost straight down to the waste edge, mirroring the
          // side-view section. Control points sit near the rim→waste straight
          // line (nudged 30% toward the lower corner) for a slight curve only.
          var ff = 0.3;
          var lEndX = wcx - wTopHalf, rEndX = wcx + wTopHalf;
          var lMx = (bpx + lEndX) / 2, lMy = (feY + feFloorY) / 2;
          var lCx = lMx + ff * (bpx - lMx), lCy = lMy + ff * (feFloorY - lMy);
          var rMx = (rEndX + bpx + bpw) / 2, rMy = (feFloorY + feY) / 2;
          var rCx = rMx + ff * (bpx + bpw - rMx), rCy = rMy + ff * (feFloorY - rMy);
          bowlPath =
            'M ' + bpx + ' ' + feY +
            ' Q ' + lCx + ' ' + lCy + ' ' + lEndX + ' ' + feFloorY +
            ' L ' + rEndX + ' ' + feFloorY +
            ' Q ' + rCx + ' ' + rCy + ' ' + (bpx + bpw) + ' ' + feY;
        } else {
          // Deeper Smoothe bowl — U-shape from the bench top to the cavity floor.
          bowlPath =
            'M ' + bpx + ' ' + feY +
            ' L ' + bpx + ' ' + (feY + feBowlDrop - rr) +
            ' Q ' + bpx + ' ' + (feY + feBowlDrop) + ' ' + (bpx + rr) + ' ' + (feY + feBowlDrop) +
            ' L ' + (bpx + bpw - rr) + ' ' + (feY + feBowlDrop) +
            ' Q ' + (bpx + bpw) + ' ' + (feY + feBowlDrop) + ' ' + (bpx + bpw) + ' ' + (feY + feBowlDrop - rr) +
            ' L ' + (bpx + bpw) + ' ' + feY;
        }
        svg.appendChild(svgPath(bowlPath, STYLE.hiddenLine));
        // Tapered waste (hidden) at the basin centre, down to the mould depth.
        var wpath =
          'M ' + (wcx - wTopHalf) + ' ' + feFloorY +
          ' L ' + (wcx - wBotHalf) + ' ' + (feY + feMouldDrop) +
          ' L ' + (wcx + wBotHalf) + ' ' + (feY + feMouldDrop) +
          ' L ' + (wcx + wTopHalf) + ' ' + feFloorY;
        svg.appendChild(svgPath(wpath, STYLE.hiddenLine));
        // Overflow slot (solid — a real opening on the front face), centred,
        // 25mm down from the bench top, 40mm × 6mm. Skipped entirely on a mould
        // that declares `overflow: false` (CA13).
        if (showOverflow) svg.appendChild(svgRect(wcx - ofW / 2, ofTop, ofW, ofH, STYLE.bodyStroke));
        // Overflow dims on the first basin only (keeps the view uncluttered).
        if (showOverflow && bfi === 0) {
          // 25 (from bench top) — vertical dim, label left of the line.
          drawDimensionLine(svg, wcx - ofW / 2 - 3, feY, wcx - ofW / 2 - 3, ofTop, formatMm(ov.fromTop));
          // 6 (overflow height) — vertical dim, label to the RIGHT of the line.
          drawDimensionLine(svg, wcx + ofW / 2 + 2, ofTop, wcx + ofW / 2 + 2, ofTop + ofH, formatMm(ov.height), { labelRight: true });
          // overflow width — horizontal dim just BELOW the slot, inside the
          // basin, with the label below the dim line.
          drawDimensionLine(svg, wcx - ofW / 2, ofTop + ofH + 1.5, wcx + ofW / 2, ofTop + ofH + 1.5, formatMm(ov.width), { labelBelow: true });
        }
      }
      // Bench height + basin height (top of bench → bottom of waste) dims, left.
      drawDimensionLine(svg, feX - 4, feY, feX - 4, feY + feH, formatMm(hgt));
      drawDimensionLine(svg, feX - 9, feY, feX - 9, feY + feMouldDrop, formatMm(mould.bowlDepth + mould.wasteDepth));
    } else {
      // Kreno (Formline) — hidden-line basin cutouts (unchanged).
      var feBasinTop = feY + feH * 0.1;
      var feBasinH = feH * 0.6;
      for (var bki = 0; bki < basinCount; bki++) {
        var kBP = basinPositions[bki];
        svg.appendChild(svgRect(kBP.x, feBasinTop, kBP.w, feBasinH, STYLE.hiddenLine));
      }
      drawDimensionLine(svg, feX - 4, feY, feX - 4, feY + feH, formatMm(hgt));
    }
    } // end inset front view (mountType !== 'top')

    // Back view — omitted for no-bench (no bench to mount to).
    if (!isNoBench) {
    // ===== BACK ELEVATION =====
    // Moulded benches (Kreno + Smoothe) now carry the same rear-face mounting
    // detail as the ramp basins:
    //   • Bolt holes — a row of fixing holes along the length, reusing the ramp
    //     spacing rule (bracketPositionsForLength). Drawn on ALL lengths of
    //     raised bench, centred vertically on the bench height.
    //   • Corner bracket cutouts — a 65mm-wide notch 20mm in from each side edge
    //     and 20mm down from the top, open to the base (same as the ramp). Only
    //     on benches ≥1700mm long that have a ≥100mm D inset (bench-edge →
    //     basin-edge clearance, per the top view) so the notch clears the basin.
    //   • The flat slab gets NONE of this — plain rear face. The cutoff is the
    //     mould's own `flatSlabMax` (default 12; SM08 is 15) — keep it in step with
    //     isFlatSlabHeight() in `sections/moulded-basin-builder.liquid`, or the
    //     builder and the drawing will disagree about whether brackets exist.
    var frontToBackGap = hgt > 200 ? 16 : 20;
    var bvY = feY + feLayoutH + frontToBackGap;
    var bvH = hgt * scalePlan;
    drawViewLabelLeft(svg, 'Back view', feX, bvY, bvH);

    var _flatSlabMax    = (typeof mould.flatSlabMax === 'number') ? mould.flatSlabMax : 12;
    var mouldedBrackets = hgt > _flatSlabMax;                    // raised bench
    var mouldedCutouts  = mouldedBrackets && len >= 1700 && dInset >= 100;
    var bvBaseY = bvY + bvH;

    if (!mouldedBrackets) {
      // 12mm flat slab — plain rear face (unchanged).
      svg.appendChild(svgRect(feX, bvY, feW, bvH, STYLE.bodyStrokeBold));
    } else if (mouldedCutouts) {
      // Raised bench, long enough for corner bracket cutouts.
      var cornerW       = 65 * scalePlan;
      var cornerOffX_mm = 20;            // from each side edge
      var cornerOffY_mm = 20;            // from the top edge
      var cornerOffX    = cornerOffX_mm * scalePlan;
      var cornerOffY    = cornerOffY_mm * scalePlan;
      var lcX = feX + cornerOffX;
      var rcX = feX + feW - cornerOffX - cornerW;
      var cornerTopY = bvY + cornerOffY;

      // Outer border — top/left/right solid; base broken where the notches sit.
      svg.appendChild(svgLine(feX, bvY, feX + feW, bvY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(feX, bvY, feX, bvBaseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(feX + feW, bvY, feX + feW, bvBaseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(feX,           bvBaseY, lcX,       bvBaseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(lcX + cornerW, bvBaseY, rcX,       bvBaseY, STYLE.bodyStrokeBold));
      svg.appendChild(svgLine(rcX + cornerW, bvBaseY, feX + feW, bvBaseY, STYLE.bodyStrokeBold));

      // Corner notches (3 sides, open to base) — left + right.
      svg.appendChild(svgLine(lcX, cornerTopY, lcX + cornerW, cornerTopY, STYLE.bodyStroke));
      svg.appendChild(svgLine(lcX, cornerTopY, lcX, bvBaseY,              STYLE.bodyStroke));
      svg.appendChild(svgLine(lcX + cornerW, cornerTopY, lcX + cornerW, bvBaseY, STYLE.bodyStroke));
      svg.appendChild(svgLine(rcX, cornerTopY, rcX + cornerW, cornerTopY, STYLE.bodyStroke));
      svg.appendChild(svgLine(rcX, cornerTopY, rcX, bvBaseY,              STYLE.bodyStroke));
      svg.appendChild(svgLine(rcX + cornerW, cornerTopY, rcX + cornerW, bvBaseY, STYLE.bodyStroke));

      // Cutout dims above: 20 · 65 (left), 65 · 20 (right).
      var bvTopDimY = bvY - 4;
      drawDimensionLine(svg, feX, bvTopDimY, lcX, bvTopDimY, formatMm(cornerOffX_mm));
      drawDimensionLine(svg, lcX, bvTopDimY, lcX + cornerW, bvTopDimY, '65');
      drawDimensionLine(svg, rcX, bvTopDimY, rcX + cornerW, bvTopDimY, '65');
      drawDimensionLine(svg, rcX + cornerW, bvTopDimY, feX + feW, bvTopDimY, formatMm(cornerOffX_mm));
      // 20mm from top (left dim).
      drawDimensionLine(svg, feX - 4, bvY, feX - 4, cornerTopY, formatMm(cornerOffY_mm));
    } else {
      // Raised bench, but too short for corner cutouts — plain box.
      svg.appendChild(svgRect(feX, bvY, feW, bvH, STYLE.bodyStrokeBold));
    }

    // Bolt holes — reuse the ramp spacing rule, centred vertically on the bench.
    if (mouldedBrackets) {
      var bvBoltPositions = bracketPositionsForLength(len);
      if (bvBoltPositions && bvBoltPositions.length) {
        var bvBoltYmm = hgt / 2;
        var bvBoltY   = bvY + bvBoltYmm * scalePlan;
        for (var mbi = 0; mbi < bvBoltPositions.length; mbi++) {
          svg.appendChild(svgCircle(feX + bvBoltPositions[mbi] * scalePlan, bvBoltY, 0.7, { fill: '#1a1a1a' }));
        }
        // Bolt-hole height from top (left dim, outboard of the cutout dim).
        drawDimensionLine(svg, feX - 9, bvY, feX - 9, bvBoltY, formatMm(bvBoltYmm));
        // Spacing chain below the back view: edge → hole → … → edge.
        var bvSpDimY = bvBaseY + 4;
        var bvPrev = 0;
        var bvRun = feX;
        for (var bsp = 0; bsp < bvBoltPositions.length; bsp++) {
          var segMm = bvBoltPositions[bsp] - bvPrev;
          var segNextX = bvRun + segMm * scalePlan;
          if (segMm >= 20) drawDimensionLine(svg, bvRun, bvSpDimY, segNextX, bvSpDimY, formatMm(segMm));
          bvRun = segNextX;
          bvPrev = bvBoltPositions[bsp];
        }
        var bvLastSeg = len - bvPrev;
        if (bvLastSeg >= 20) drawDimensionLine(svg, bvRun, bvSpDimY, bvRun + bvLastSeg * scalePlan, bvSpDimY, formatMm(bvLastSeg));
      }
    }

    // Dryer backplate(s) in the back view — same plate as the D-Airblade.
    // cornerEndMm = 85 (20mm edge + 65mm corner) when the corner cutouts are
    // present so the dryer top dim chain fills the MIDDLE of the corner dim row;
    // 0 otherwise (full-width chain, no corner dims).
    drawDryerBackplatesBackView(svg, design, feX, bvY, bvH, scalePlan, tapBenchOffsets, len, mouldedCutouts ? 85 : 0);
    // Length dim sits below the bolt-hole spacing chain (when present).
    var bvLenDimY = mouldedBrackets && bracketPositionsForLength(len) ? bvBaseY + 8 : bvBaseY + 4;
    drawDimensionLine(svg, feX, bvLenDimY, feX + feW, bvLenDimY, formatMm(len));
    // Full bench-height dim on the left — drop to feX−4 only when no cutout dim
    // already occupies that column (the cutout uses feX−4 for its 20-from-top).
    if (!mouldedCutouts) {
      drawDimensionLine(svg, feX - 4, bvY, feX - 4, bvY + bvH, formatMm(hgt));
    }
    } // end back view (skipped for no-bench)

    // ===== SIDE CROSS-SECTION (right column, below info panel) =====
    // Rotated orientation per the user reference: bench WIDTH runs vertical
    // (with the back/basin/front breakdown on the right), bench HEIGHT runs
    // horizontal (200mm across the top). Basin opens at the LEFT (bench top)
    // and curves inward, reaching a 70mm-diameter waste opening at 138mm
    // depth. The waste then tapers down to 45mm diameter at 175mm depth.
    var SECTION_PAD = 12;
    var sectionTitleY = infoPanelEndY + SECTION_PAD;
    var sx_origin = PAGE_W - PAGE_MARGIN - INFO_PANEL_W;

    svg.appendChild(svgText('Side view (cross-section)', sx_origin, sectionTitleY,
      Object.assign({}, STYLE.label, { 'font-weight': 'bold' })));

    var sAvailW = INFO_PANEL_W - 14;
    var sAvailH = PAGE_H - FOOTER_H - 12 - (sectionTitleY + 8);
    var sScale = Math.min(sAvailW / hgt, sAvailH / wid);

    var sBodyW = hgt * sScale;       // horizontal in image = bench height
    var sBodyH = wid * sScale;       // vertical in image = bench width
    var sx = sx_origin + 4;
    var sy = sectionTitleY + 12;
    var t = (mould.wallThickness || MATERIAL_THICKNESS) * sScale;

    // Counter-top basin (mountType='top') — basin sits PROUD of the bench. Draw
    // the dedicated proud section and finish (skips the inset cavity below).
    if (String(design.mountType) === 'top') {
      var sideBottomY = drawMouldedCounterTopSection(svg, {
        mould: mould, wid: wid, hgt: hgt, backInset: backInset,
        basinWidth: basinWidth, sx: sx_origin, titleY: sectionTitleY,
        availW: sAvailW, availH: sAvailH, noBench: isNoBench
      });
      // TP range: enlarged drainage-structure detail beneath the side view.
      if (mould.wasteMidDia) {
        drawDrainageStructure(svg, sx_origin + 4 + sAvailW / 2,
          (sideBottomY || sectionTitleY + 60) + 10, sAvailW, mould);
      }
      return svg;
    }

    // Inset bowl with an inner waterline (TP957) — non-rotated bowl cross-section
    // (bench slab + recessed bowl) instead of the generic rotated cavity. Gated on
    // the mould declaring an `inset` MOUNT, not just an inner waterline: the
    // Grandios ceramics also carry bowlLength/bowlWidthInner (for the top + front
    // views) but keep the generic ROTATED cavity section, which the client signed
    // off on. Don't loosen this back to `bowlWidthInner && !apron`.
    if (mould.bowlWidthInner && !mould.apron && (mould.mounts || []).indexOf('inset') !== -1) {
      var insBottomY = drawMouldedInsetBowlSection(svg, {
        mould: mould, wid: wid, hgt: hgt, backInset: backInset,
        basinWidth: basinWidth, sx: sx_origin, titleY: sectionTitleY,
        availW: sAvailW, availH: sAvailH
      });
      if (mould.wasteMidDia) {
        drawDrainageStructure(svg, sx_origin + 4 + sAvailW / 2,
          (insBottomY || sectionTitleY + 60) + 10, sAvailW, mould);
      }
      return svg;
    }

    // Bench profile drawn as individual line segments rather than a single
    // rect so the right side (bench bottom in rotated orientation) can be
    // broken — the basin/waste opens through the bench bottom under the
    // basin, so the right side is NOT enclosed in the middle.
    var cavityTopY = sy + backInset * sScale;
    var cavityBotY = sy + (backInset + basinWidth) * sScale;
    // Waste centreline. Set in from the basin's BACK edge by the mould's own
    // figure (CA08 160) rather than assumed to be the bowl centre, so this
    // section agrees with the top view. On a mould with no figure declared it
    // resolves to the bowl centre — (cavityTopY + cavityBotY) / 2, which is what
    // this used to be hard-coded to — so those sections are unchanged. Everything below —
    // the bowl's back and front walls, the overflow channel, the Ø callouts —
    // hangs off this, so an off-centre waste correctly yields ASYMMETRIC bowl
    // walls (shorter run on the back side, longer on the front).
    var wasteCenterY = cavityTopY + getWasteFromBasinEdge(mould, basinWidth) * sScale;
    // Top edge (back of bench), bottom edge (front of bench), left edge
    // (bench top — solid). Right edge only drawn in the front-inset segment
    // (from cavityBotY down to the front edge); the back-inset segment is
    // omitted because the basin form passes through unenclosed.
    svg.appendChild(svgLine(sx, sy, sx + sBodyW, sy, STYLE.bodyStrokeBold));
    svg.appendChild(svgLine(sx, sy + sBodyH, sx + sBodyW, sy + sBodyH, STYLE.bodyStrokeBold));
    svg.appendChild(svgLine(sx, sy, sx, sy + sBodyH, STYLE.bodyStrokeBold));
    svg.appendChild(svgLine(sx + sBodyW, cavityBotY, sx + sBodyW, sy + sBodyH, STYLE.bodyStrokeBold));

    // Waste geometry — per-mould. Kreno: Ø70 @ 138mm tapering to Ø45 @ 175mm.
    // Smoothe: Ø70 at the model's bowl depth, tapering to Ø45 +40mm below.
    var WASTE_TOP_DEPTH = mould.bowlDepth;                    // depth to the Ø-top waste opening
    var WASTE_BOT_DEPTH = mould.bowlDepth + mould.wasteDepth; // depth to the Ø-bottom waste
    var WASTE_TOP_DIA = mould.wasteTopDia;
    var WASTE_BOT_DIA = mould.wasteBotDia;

    var wasteTopX = sx + WASTE_TOP_DEPTH * sScale;
    var wasteBotX = sx + WASTE_BOT_DEPTH * sScale;
    var wasteTopBackY  = wasteCenterY - (WASTE_TOP_DIA / 2) * sScale;
    var wasteTopFrontY = wasteCenterY + (WASTE_TOP_DIA / 2) * sScale;
    var wasteBotBackY  = wasteCenterY - (WASTE_BOT_DIA / 2) * sScale;
    var wasteBotFrontY = wasteCenterY + (WASTE_BOT_DIA / 2) * sScale;

    // Cavity outline: curved basin walls dropping from the top opening down
    // to the waste, then tapered waste through to depth 175.
    // Quadratic control point sits at the corner (wasteTopX, cavityTopY /
    // cavityBotY) so the curve runs along the top first and then drops sharply
    // down — a deeper, more bowl-shaped curve.
    // Floor-corner radius for the 'square' profile (0 for the curved profiles).
    // Clamped so it can't exceed the bowl depth or either floor run — the runs
    // differ because the waste is off-centre, which is correct.
    var sqR = isSquareBowl(mould)
      ? Math.max(0, Math.min(
          bowlFloorRadiusMm(mould) * sScale,
          wasteTopX - sx,
          wasteTopBackY - cavityTopY,
          cavityBotY - wasteTopFrontY
        ))
      : 0;

    // Inner waterline (Grandios SN0## — bowlWidthInner 338 inside the 390 rim).
    // Centred in the basin footprint, matching the concentric inner opening the
    // top view draws. The rim then rolls off over `lipRadius` from the outer edge
    // in to this line before the wall curves down to the waste — same profile as
    // drawMouldedInsetBowlFrontView, mapped into this rotated section (x = depth,
    // y = across the bench width).
    var svWlBackY = cavityTopY, svWlFrontY = cavityBotY, svLipPx = 0;
    if (mould.bowlWidthInner) {
      var svWlInset = (basinWidth - mould.bowlWidthInner) / 2 * sScale;
      svWlBackY  = cavityTopY + svWlInset;
      svWlFrontY = cavityBotY - svWlInset;
      svLipPx = Math.min((mould.lipRadius || 0) * sScale, (wasteTopX - sx) * 0.5);
    }

    var cavityPath;
    var svBackCubic = null;   // set when the wall is an angled cubic (see below)
    if (mould.bowlWidthInner) {
      // Inset ceramic bowl — corner-control curve from the inner waterline down to
      // the waste opening, mirrored on the front side, with the tapered waste
      // between. When the mould declares a wall thickness the rim roll belongs to
      // the OUTER shell (drawn separately below) and this path starts straight at
      // the waterline; with no wall thickness the roll stays on this single path.
      var svCavityStart = mould.wallThickness
        ? 'M ' + sx + ' ' + svWlBackY
        : 'M ' + sx + ' ' + cavityTopY +
          ' Q ' + sx + ' ' + svWlBackY + ' ' + (sx + svLipPx) + ' ' + svWlBackY;
      var svCavityEnd = mould.wallThickness
        ? ' Q ' + wasteTopX + ' ' + svWlFrontY + ' ' + sx + ' ' + svWlFrontY
        : ' Q ' + wasteTopX + ' ' + svWlFrontY + ' ' + (sx + svLipPx) + ' ' + svWlFrontY +
          ' Q ' + sx + ' ' + svWlFrontY + ' ' + sx + ' ' + cavityBotY;
      cavityPath = svCavityStart +
        ' Q ' + wasteTopX + ' ' + svWlBackY + ' ' + wasteTopX + ' ' + wasteTopBackY +
        ' L ' + wasteBotX + ' ' + wasteBotBackY +
        ' L ' + wasteBotX + ' ' + wasteBotFrontY +
        ' L ' + wasteTopX + ' ' + wasteTopFrontY +
        svCavityEnd;
    } else if (isSquareBowl(mould)) {
      // Near-square bowl (CA08) — walls run straight down the depth axis from the
      // rim, then turn through a small radius into a flat floor that meets the
      // waste. In this rotated section: x = depth (sx = bench top), y = across the
      // bench width (cavityTopY = basin BACK edge, cavityBotY = basin FRONT edge).
      // The floor is the vertical run at x = wasteTopX, so the two floor corners
      // are (wasteTopX, cavityTopY) and (wasteTopX, cavityBotY).
      cavityPath =
        'M ' + sx + ' ' + cavityTopY +
        ' L ' + (wasteTopX - sqR) + ' ' + cavityTopY +
        ' Q ' + wasteTopX + ' ' + cavityTopY + ' ' + wasteTopX + ' ' + (cavityTopY + sqR) +
        ' L ' + wasteTopX + ' ' + wasteTopBackY +
        ' L ' + wasteBotX + ' ' + wasteBotBackY +
        ' L ' + wasteBotX + ' ' + wasteBotFrontY +
        ' L ' + wasteTopX + ' ' + wasteTopFrontY +
        ' L ' + wasteTopX + ' ' + (cavityBotY - sqR) +
        ' Q ' + wasteTopX + ' ' + cavityBotY + ' ' + (wasteTopX - sqR) + ' ' + cavityBotY +
        ' L ' + sx + ' ' + cavityBotY;
    } else if (mould.shallowBowl) {
      // Shallow bowl (Kreno) — the wall runs almost straight from the rim to the
      // waste opening with only a slight inward curve. Control points sit near
      // the straight-line midpoint (nudged 30% toward the corner) instead of at
      // the corner itself, so the bowl reads gently curved rather than deep.
      var f = 0.3;
      var cbMx = (sx + wasteTopX) / 2, cbMy = (cavityTopY + wasteTopBackY) / 2;
      var cbCx = cbMx + f * (wasteTopX - cbMx), cbCy = cbMy + f * (cavityTopY - cbMy);
      var cfMx = (sx + wasteTopX) / 2, cfMy = (cavityBotY + wasteTopFrontY) / 2;
      var cfCx = cfMx + f * (wasteTopX - cfMx), cfCy = cfMy + f * (cavityBotY - cfMy);
      cavityPath =
        'M ' + sx + ' ' + cavityTopY +
        ' Q ' + cbCx + ' ' + cbCy + ' ' + wasteTopX + ' ' + wasteTopBackY +
        ' L ' + wasteBotX + ' ' + wasteBotBackY +
        ' L ' + wasteBotX + ' ' + wasteBotFrontY +
        ' L ' + wasteTopX + ' ' + wasteTopFrontY +
        ' Q ' + cfCx + ' ' + cfCy + ' ' + sx + ' ' + cavityBotY;
    } else if (rimRollSideMm(mould) || rimAngleSideRad(mould)) {
      // Roll-off lip (CA13). Same corner-control bowl curve as the default Smoothe
      // section below, but the rim turns down through `rimRoll` first. In this
      // ROTATED section x = depth and y = across the bench width, so the roll runs
      // `rimRoll` deep (x) and `rimRoll` inboard (y) — mirrored on the front side.
      // The walls stay ASYMMETRIC around an off-centre waste, because each side's
      // curve still ends on its own side of the waste opening.
      // Roll is OPTIONAL here (rollSide: 0 on CA15/CA16). With no roll the wall starts
      // at the rim itself: svRoll 0 collapses svRollX/svRbY/svRfY back onto the rim
      // lines and the two roll quadratics drop out of the path entirely.
      var svRollMm = rimRollSideMm(mould);
      var svRoll = svRollMm ? Math.min(svRollMm * sScale,
                            (wasteTopX - sx) * 0.5,
                            (wasteCenterY - cavityTopY) * 0.5,
                            (cavityBotY - wasteCenterY) * 0.5) : 0;
      var svRollX = svRoll ? sx + svRoll : sx;         // depth the roll has reached
      var svRbY = svRoll ? cavityTopY + svRoll : cavityTopY;   // back rim
      var svRfY = svRoll ? cavityBotY - svRoll : cavityBotY;   // front rim
      // Control points sit so each quadratic is a true FILLET: it leaves the bench
      // top face tangentially (along the width, y) and arrives tangential to the
      // wall (down the depth, x). Getting these the other way round draws a hump
      // at the rim instead of a roll — the wall then also joins the following
      // corner-control curve tangentially, since both are travelling +x there.
      // How deep the wall runs before it starts turning toward the waste. 1 = the
      // full bowl depth (the original corner control — a deep, steep bowl); lower
      // reads shallower and flatter. Written so `side` 1 reduces to EXACTLY
      // wasteTopX, keeping every mould that doesn't set the dial byte-identical.
      var svSide = dishDial(mould, 'side', 1);
      var svCtlX = (svSide === 1) ? wasteTopX : (svRollX + (wasteTopX - svRollX) * svSide);
      var svRad = rimAngleSideRad(mould);
      var svWall;
      // null unless the angled-cubic path below runs; the overflow channel keys off it.
      svBackCubic = null;   // back-side wall, roll end -> waste opening; mirrored for the front
      if (svRad) {
        // ROTATED axes: "along the face" is +y (across the bench width) and "down the
        // wall" is +x (depth). So the angled step swaps components relative to the
        // front view — this is the same mirroring that made the rim fillet tricky.
        // Cubic, so the wall can hold the angle and THEN ease onto the waste.
        var svStepB = rimAngleStep(svRad, dishDial(mould, 'rimRun', 0.55),
                                   Math.max(0, wasteTopBackY - svRbY),
                                   Math.max(0, wasteTopX - svRollX));
        var svStepF = rimAngleStep(svRad, dishDial(mould, 'rimRun', 0.55),
                                   Math.max(0, svRfY - wasteTopFrontY),
                                   Math.max(0, wasteTopX - svRollX));
        // Keep the back wall's control points: the overflow channel draws its outer
        // skin and its dashed centreline as PARALLEL OFFSETS of this exact curve, so
        // the channel follows whatever angle the bowl is set to instead of the old
        // corner-control shape. Without this the channel visibly disagreed with the
        // wall as soon as the wall stopped being a corner-control quadratic.
        // Last control aimed along the waste taper so the wall flows into it.
        var svCtlLen = Math.max(0, wasteTopX - svCtlX);
        var svC2B = tangentCtl(wasteTopX, wasteTopBackY,  wasteBotX, wasteBotBackY,  svCtlLen);
        var svC2F = tangentCtl(wasteTopX, wasteTopFrontY, wasteBotX, wasteBotFrontY, svCtlLen);
        svBackCubic = [ [svRollX, svRbY],
                        [svRollX + svStepB.down, svRbY + svStepB.along],
                        svC2B,
                        [wasteTopX, wasteTopBackY] ];
        svWall = {
          back: ' C ' + (svRollX + svStepB.down) + ' ' + (svRbY + svStepB.along) +
                ' '   + svC2B[0] + ' ' + svC2B[1] +
                ' '   + wasteTopX + ' ' + wasteTopBackY,
          front: ' C ' + svC2F[0] + ' ' + svC2F[1] +
                 ' '   + (svRollX + svStepF.down) + ' ' + (svRfY - svStepF.along) +
                 ' '   + svRollX + ' ' + (svRoll ? svRfY : cavityBotY)
        };
      } else {
        svWall = {
          back:  ' Q ' + svCtlX + ' ' + svRbY + ' ' + wasteTopX + ' ' + wasteTopBackY,
          front: ' Q ' + svCtlX + ' ' + svRfY + ' ' + svRollX + ' ' + (svRoll ? svRfY : cavityBotY)
        };
      }
      var svRollIn  = svRoll ? (' Q ' + sx + ' ' + svRbY + ' ' + svRollX + ' ' + svRbY) : '';
      var svRollOut = svRoll ? (' Q ' + sx + ' ' + svRfY + ' ' + sx + ' ' + cavityBotY) : '';
      cavityPath =
        'M ' + sx + ' ' + cavityTopY +
        svRollIn +
        svWall.back +
        ' L ' + wasteBotX + ' ' + wasteBotBackY +
        ' L ' + wasteBotX + ' ' + wasteBotFrontY +
        ' L ' + wasteTopX + ' ' + wasteTopFrontY +
        svWall.front +
        svRollOut;
    } else {
      // Deeper Smoothe bowl — control at the corner, so the curve runs along the
      // top then drops sharply (a deep, bowl-shaped wall).
      cavityPath =
        'M ' + sx + ' ' + cavityTopY +
        ' Q ' + wasteTopX + ' ' + cavityTopY + ' ' + wasteTopX + ' ' + wasteTopBackY +
        ' L ' + wasteBotX + ' ' + wasteBotBackY +
        ' L ' + wasteBotX + ' ' + wasteBotFrontY +
        ' L ' + wasteTopX + ' ' + wasteTopFrontY +
        ' Q ' + wasteTopX + ' ' + cavityBotY + ' ' + sx + ' ' + cavityBotY;
    }
    svg.appendChild(svgPath(cavityPath, STYLE.bodyStroke));

    // Outer ceramic shell (Grandios wallThickness 20) — a second profile outside
    // the cavity: rim roll from the outer rim in to the waterline, then the same
    // corner-control curve to a floor `wall` deeper, landing on the waste taper
    // rather than on a flat floor. The band between this and cavityPath reads as
    // the ceramic section.
    if (mould.bowlWidthInner && mould.wallThickness) {
      var svWall = mould.wallThickness;
      var svShellX = sx + (WASTE_TOP_DEPTH + svWall) * sScale;
      var svShellFrac = Math.min(1, svWall / mould.wasteDepth);
      var svShellBackY  = wasteTopBackY  + svShellFrac * (wasteBotBackY - wasteTopBackY);
      var svShellFrontY = wasteTopFrontY + svShellFrac * (wasteBotFrontY - wasteTopFrontY);
      // The shell wall runs `wall` OUTBOARD of the cavity waterline (near the rim
      // the wall is near-vertical, so the offset reads across the width), clamped
      // to the outer rim so a narrow rim can't push the shell outside the cutout.
      var svShellBackWlY  = Math.max(cavityTopY, svWlBackY  - svWall * sScale);
      var svShellFrontWlY = Math.min(cavityBotY, svWlFrontY + svWall * sScale);
      // Roll depth is the FULL lip radius, not the width of the rim shelf. The
      // shelf is only ~6mm on SN023, so clamping to it made the rim kick away in a
      // 6×6 tick; running the roll the full lipRadius deep gives the long, soft
      // roll-off the drawings show. `lipRadius` is the knob for how soft it reads.
      var svShellLipPx = svLipPx;
      // Rim edge — the ceramic rim has real thickness, so the shell drops straight
      // down the outer rim line before it rolls inward. Without this the shell
      // curls away from the rim immediately and the roll reads as a tick, because
      // the rim shelf only leaves ~6mm of width to roll across.
      var svRimEdgeX = sx + (mould.rimEdge || 0) * sScale;
      svg.appendChild(svgPath(
        'M ' + sx + ' ' + cavityTopY +
        ' L ' + svRimEdgeX + ' ' + cavityTopY +
        ' Q ' + svRimEdgeX + ' ' + svShellBackWlY + ' ' + (svRimEdgeX + svShellLipPx) + ' ' + svShellBackWlY +
        ' Q ' + svShellX + ' ' + svShellBackWlY + ' ' + svShellX + ' ' + svShellBackY +
        ' M ' + sx + ' ' + cavityBotY +
        ' L ' + svRimEdgeX + ' ' + cavityBotY +
        ' Q ' + svRimEdgeX + ' ' + svShellFrontWlY + ' ' + (svRimEdgeX + svShellLipPx) + ' ' + svShellFrontWlY +
        ' Q ' + svShellX + ' ' + svShellFrontWlY + ' ' + svShellX + ' ' + svShellFrontY,
        STYLE.bodyStroke));
    }

    // R15 callout on the front (90 / G) rim — this is the OUTSIDE edge radius, so
    // the label sits INSIDE the bowl with a short leader to the outer rim corner.
    if (mould.shallowBowl && mould.rimRadius) {
      svg.appendChild(svgLine(sx, cavityBotY, sx + 3, cavityBotY - 3.2, STYLE.dimLine));
      svg.appendChild(svgText('R' + mould.rimRadius, sx + 3.4, cavityBotY - 3.2,
        Object.assign({}, STYLE.dimText, { 'text-anchor': 'start' })));
    }

    // 20mm material shell — strips along the back wall, front wall, bench bottom
    drawHatching(svg, { x: sx, y: sy, w: sBodyW, h: t }, { spacing: 0.55 }); // bench top behind basin
    drawHatching(svg, { x: sx, y: sy + sBodyH - t, w: sBodyW, h: t }, { spacing: 0.55 }); // bench bottom

    // Smoothe overflow channel — a second (inner) wall on the tap/back side of
    // the bowl, set 28mm inside the back wall. The gap between the outer bowl
    // wall and this inner skin is the overflow space. (Back = top in this
    // rotated section.) Drawn after the cavity outline so it sits on top.
    if (showOverflow) {
      var ofWall = SMOOTHE_TAP_WALL * sScale;
      // The overflow channel sits BEHIND the bowl's tap-side wall (in the back
      // inset). Its outer wall (this new curve) starts 28mm back from the 100
      // line — i.e. 72mm from the bench back edge — and curves down to meet the
      // waste wall 28mm below the basin floor (into the waste). The existing
      // bowl wall (cavityPath) is the channel's other side: it starts on the
      // 100 line and meets the top of the waste at the bowl depth.
      var ofTopY = cavityTopY - ofWall;                       // 72 from back edge
      var ofFrac = Math.min(1, SMOOTHE_TAP_WALL / mould.wasteDepth);
      var ofEndX = wasteTopX + ofFrac * (wasteBotX - wasteTopX);   // depth = bowlDepth + 28
      var ofEndY = wasteTopBackY + ofFrac * (wasteBotBackY - wasteTopBackY); // on the waste wall
      var ofPathEndX = wasteTopX + 16 * sScale;           // 16mm into the waste
      var ofMidEndY  = (wasteTopBackY + ofEndY) / 2;      // centred between the two wall ends

      // 28 channel-width callout — out in the right-hand dim column, on the same
      // x-axis as the Ø45 / Ø70 waste dims (wasteBotX + 4), so it's clear of the
      // diagram lines rather than sitting over the top of them.
      drawDimensionLine(svg, wasteBotX + 4, ofTopY, wasteBotX + 4, cavityTopY, formatMm(SMOOTHE_TAP_WALL));

      var innerBackPath, overflowPath;
      if (mould.shallowBowl) {
        // Shallow bowl (Kreno) — both channel lines run PARALLEL to the inner
        // basin wall (same angle), offset toward the back edge: the inner skin
        // (outline) by the full channel width, the dashed water path by half
        // (the channel centreline). Both then dive into the waste. No vertical
        // kink. cbCx/cbCy are the cavity back-wall control point set above.
        var ofHalf = ofWall / 2;
        innerBackPath =
          'M ' + sx + ' ' + ofTopY +
          ' Q ' + cbCx + ' ' + (cbCy - ofWall) + ' ' + wasteTopX + ' ' + (wasteTopBackY - ofWall) +
          ' L ' + ofEndX + ' ' + ofEndY;
        // Dashed water path on the channel centreline (cavity wall offset by
        // half), but STARTING at the overflow hole — 25mm from the top. Solve
        // the centreline quadratic for the parameter t where depth = 25mm, then
        // split it (de Casteljau) so the dashed sub-curve stays parallel to the
        // basin wall from that point down into the waste.
        var clp0x = sx,        clp0y = cavityTopY - ofHalf;
        var clpcx = cbCx,      clpcy = cbCy - ofHalf;
        var clp1x = wasteTopX, clp1y = wasteTopBackY - ofHalf;
        var holeX = sx + 25 * sScale;
        var qA = clp0x - 2 * clpcx + clp1x, qB = 2 * (clpcx - clp0x), qC = clp0x - holeX;
        var tH;
        if (Math.abs(qA) < 1e-6) {
          tH = qB !== 0 ? -qC / qB : 0;
        } else {
          var disc = Math.sqrt(Math.max(0, qB * qB - 4 * qA * qC));
          var tA = (-qB + disc) / (2 * qA), tB = (-qB - disc) / (2 * qA);
          tH = (tA >= 0 && tA <= 1) ? tA : tB;
        }
        tH = Math.max(0, Math.min(1, tH));
        var s0x = clp0x + (clpcx - clp0x) * tH, s0y = clp0y + (clpcy - clp0y) * tH;
        var s1x = clpcx + (clp1x - clpcx) * tH, s1y = clpcy + (clp1y - clpcy) * tH;
        var startX = s0x + (s1x - s0x) * tH, startY = s0y + (s1y - s0y) * tH; // point at the hole
        overflowPath =
          'M ' + startX + ' ' + startY +
          ' Q ' + s1x + ' ' + s1y + ' ' + clp1x + ' ' + clp1y +
          ' L ' + ofPathEndX + ' ' + ofMidEndY;
      } else if (isSquareBowl(mould)) {
        // Square bowl (CA08) — the channel runs PARALLEL to the straight back
        // wall, 28mm behind it, and turns the same tight corner into the waste.
        // The deep-bowl path below sweeps one wide quadratic across the whole
        // corner, which over a square bowl reads as a curved bowl again.
        var sqMidY  = (cavityTopY + ofTopY) / 2;            // channel centreline
        var sqHoleX = sx + 25 * sScale;                     // hole depth (25 from top)
        innerBackPath =
          'M ' + sx + ' ' + ofTopY +
          ' L ' + (ofEndX - sqR) + ' ' + ofTopY +
          ' Q ' + ofEndX + ' ' + ofTopY + ' ' + ofEndX + ' ' + (ofTopY + sqR) +
          ' L ' + ofEndX + ' ' + ofEndY;
        // Dashed water path: up from the overflow hole onto the channel
        // centreline, along it, then the matching corner down into the waste.
        overflowPath =
          'M ' + sqHoleX + ' ' + cavityTopY +
          ' L ' + sqHoleX + ' ' + sqMidY +
          ' L ' + Math.max(sqHoleX, ofPathEndX - sqR / 2) + ' ' + sqMidY +
          ' Q ' + ofPathEndX + ' ' + sqMidY + ' ' + ofPathEndX + ' ' + ofMidEndY;
      } else if (svBackCubic) {
        // Angled/dish wall (CA15, CA16) — BOTH channel lines are parallel offsets of
        // the bowl's back wall, so the channel matches the wall at whatever angle it
        // is set to: the outer skin by the FULL 28mm channel width, the dashed water
        // path by HALF of it, i.e. running up the middle of the channel. Same
        // treatment Kreno gets; the old corner-control branch below is what made the
        // channel disagree with the wall once the wall became an angled cubic.
        // Offset the wall in y for the parallel 28mm gap, then re-aim each line's
        // last control at ITS OWN end target (the waste-taper point the channel dives
        // to), so the outer skin and the dashed centreline flow into the waste instead
        // of turning a corner. Same kink fix as the bowl wall above.
        // Three different rules, one per control, each for a reason:
        //   P0  pure −y shift  — the channel wall starts ON the bench top face, `dy`
        //                        behind the bowl rim. This is what the 28 dim measures.
        //   C1  along the start NORMAL — keeps the gap ≈ dy as the wall flattens,
        //                        instead of letting a y-shift close it up.
        //   C2  aimed at the channel's own end target — arrives tangential to the dive
        //                        into the waste, so there is no kink at the bottom.
        var ofNrm = cubicStartNormal(svBackCubic);
        function ofOffset(dy) {
          var c = cubicOffsetY(svBackCubic, dy);
          c[1] = [ svBackCubic[1][0] + ofNrm[0] * dy, svBackCubic[1][1] + ofNrm[1] * dy ];
          var len = Math.sqrt(Math.pow(c[3][0] - c[2][0], 2) + Math.pow(c[3][1] - c[2][1], 2));
          c[2] = tangentCtl(c[3][0], c[3][1], ofEndX, ofEndY, len);
          return c;
        }
        var ofOuter = ofOffset(ofWall);
        var ofMid   = ofOffset(ofWall / 2);
        innerBackPath = cubicD(ofOuter, true) + ' L ' + ofEndX + ' ' + ofEndY;
        // Start the dashed path AT the overflow hole (its own fromTop, 25 by default)
        // rather than at the rim, then follow the centreline down into the waste.
        var ofSlot  = mould.overflowSlot || SMOOTHE_OVERFLOW;
        var ofHoleX2 = sx + ofSlot.fromTop * sScale;
        var ofMidCut = cubicSplitAfter(ofMid, cubicTAtX(ofMid, ofHoleX2));
        overflowPath = cubicD(ofMidCut, true) + ' L ' + ofPathEndX + ' ' + ofMidEndY;
      } else {
        // Deep Smoothe bowl — the channel hugs the corner (flat then drop), and
        // the dashed water path rises vertically from the overflow hole to the
        // channel midline before curving into the waste.
        var midlineY = (cavityTopY + ofTopY) / 2;
        var ofHoleX  = sx + 25 * sScale;                    // hole depth (25 from top)
        innerBackPath =
          'M ' + sx + ' ' + ofTopY +
          ' Q ' + ofEndX + ' ' + ofTopY +
          ' ' + ofEndX + ' ' + ofEndY;
        overflowPath =
          'M ' + ofHoleX + ' ' + cavityTopY +
          ' L ' + ofHoleX + ' ' + midlineY +
          ' Q ' + ofPathEndX + ' ' + midlineY + ' ' + ofPathEndX + ' ' + ofMidEndY;
      }
      svg.appendChild(svgPath(innerBackPath, STYLE.bodyStroke));
      svg.appendChild(svgPath(overflowPath, Object.assign({}, STYLE.bodyStroke, { 'stroke-dasharray': '1.2 0.9' })));
    }

    // ── Dimensions ──
    // Vertical breakdown on the LEFT side: [bowl inner] / back / basin / front / total
    var dimX = sx - 5;
    // Inner bowl width (Grandios 338) — innermost column, so the back/basin/front
    // chain and the overall width shift one column further out.
    if (mould.bowlWidthInner) {
      drawDimensionLine(svg, dimX, svWlBackY, dimX, svWlFrontY, formatMm(mould.bowlWidthInner));
      dimX -= 5;
    }
    drawDimensionLine(svg, dimX, sy, dimX, cavityTopY, formatMm(backInset));
    drawDimensionLine(svg, dimX, cavityTopY, dimX, cavityBotY, formatMm(basinWidth));
    drawDimensionLine(svg, dimX, cavityBotY, dimX, sy + sBodyH, formatMm(frontInset));
    drawDimensionLine(svg, dimX - 5, sy, dimX - 5, sy + sBodyH, formatMm(wid));

    // Bench height (200) on top
    drawDimensionLine(svg, sx, sy - 4, sx + sBodyW, sy - 4, formatMm(hgt));

    // 138 callout — INSIDE the bowl, aligned with the top edge of the waste.
    drawDimensionLine(svg, sx, wasteTopBackY, wasteTopX, wasteTopBackY, formatMm(WASTE_TOP_DEPTH));
    // 175 callout — INSIDE the bowl, on the waste centreline (depth to waste bottom).
    drawDimensionLine(svg, sx, wasteCenterY, wasteBotX, wasteCenterY, formatMm(WASTE_BOT_DEPTH));

    // Ø70 and Ø45 width dim lines on the RIGHT of the basin — proper vertical
    // dim lines (replacing the inline text labels). 45 closer to feature, 70
    // further out (standard nested dimension convention).
    var dim45MX = wasteBotX + 4;
    var dim70MX = wasteBotX + 9;
    drawDimensionLine(svg, dim45MX, wasteBotBackY, dim45MX, wasteBotFrontY, 'Ø' + WASTE_BOT_DIA);
    // Single-outlet moulds (Kreno Ø45) have equal top/bottom dia — skip the
    // redundant second callout that would otherwise overprint the first.
    if (WASTE_TOP_DIA !== WASTE_BOT_DIA) {
      drawDimensionLine(svg, dim70MX, wasteTopBackY, dim70MX, wasteTopFrontY, 'Ø' + WASTE_TOP_DIA);
    }

    return svg;
  }

  function renderStub(design, label) {
    var svg = createSpecSheetSvg();
    renderHeader(svg, design);
    renderRightInfoPanel(svg, design);
    renderFooterContacts(svg);
    svg.appendChild(svgText(
      label + ' renderer — coming in next implementation session',
      PAGE_W / 2, PAGE_H / 2,
      Object.assign({}, STYLE.label, { 'text-anchor': 'middle', 'font-size': 4, fill: '#999' })
    ));
    return svg;
  }

  // ─────────────────────────────────────────────────────────────
  // Watermark — Phase 6
  // Tiles The-ramp-basin-collection-logo.png diagonally across
  // the full page at low opacity. Inserted as the first child of
  // the SVG so it renders behind all drawing content.
  // ─────────────────────────────────────────────────────────────

  function renderWatermark(svg) {
    var assets = window.SPEC_SHEET_ASSETS || {};
    if (!assets.rampLogo) return;

    // <defs> containing a repeating pattern
    var defs = document.createElementNS(SVG_NS, 'defs');
    var pat  = document.createElementNS(SVG_NS, 'pattern');
    pat.setAttribute('id',               'wm-tile');
    pat.setAttribute('x',                '0');
    pat.setAttribute('y',                '0');
    pat.setAttribute('width',            '95');
    pat.setAttribute('height',           '48');
    pat.setAttribute('patternUnits',     'userSpaceOnUse');
    pat.setAttribute('patternTransform', 'rotate(-30 ' + (PAGE_W / 2) + ' ' + (PAGE_H / 2) + ')');

    var wmImg = document.createElementNS(SVG_NS, 'image');
    wmImg.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', assets.rampLogo);
    wmImg.setAttribute('href',                 assets.rampLogo);
    wmImg.setAttribute('x',                    '5');
    wmImg.setAttribute('y',                    '9');
    wmImg.setAttribute('width',                '85');
    wmImg.setAttribute('height',               '30');
    wmImg.setAttribute('opacity',              '0.07');
    wmImg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    pat.appendChild(wmImg);
    defs.appendChild(pat);

    // Full-page rect filled with the tiling pattern
    var wmRect = document.createElementNS(SVG_NS, 'rect');
    wmRect.setAttribute('x',      '0');
    wmRect.setAttribute('y',      '0');
    wmRect.setAttribute('width',  String(PAGE_W));
    wmRect.setAttribute('height', String(PAGE_H));
    wmRect.setAttribute('fill',   'url(#wm-tile)');

    // Prepend both so they sit behind every other element
    var first = svg.firstChild;
    if (first) {
      svg.insertBefore(wmRect, first);
      svg.insertBefore(defs,   wmRect);
    } else {
      svg.appendChild(defs);
      svg.appendChild(wmRect);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Top-level dispatcher
  // ─────────────────────────────────────────────────────────────

  function renderSpecSheet(design) {
    if (!design || !design.builder) {
      throw new Error('renderSpecSheet: design.builder is required');
    }

    var svg;
    if (design.builder === 'benchtop') {
      svg = renderBenchtop(design);
    } else if (design.builder === 'moulded') {
      svg = renderMouldedFormline(design);
    } else if (design.builder === 'rampBasin') {
      var entry = MODEL_REGISTRY[design.model];
      if (!entry) throw new Error('Unknown ramp basin model: ' + design.model);
      // Ramp Classic with 2+ basins uses the multi-basin path (the generalised
      // former Double renderer). Legacy doubleC100 routes there too.
      var rampCount = parseInt(design.count, 10);
      if (design.model === 'classicC100' && rampCount >= 2) {
        svg = renderRampMulti(design);
      } else {
        switch (entry.render) {
          case 'rampClassic':   svg = renderRampClassic(design);   break;
          case 'rampDouble':    svg = renderRampMulti(design);     break;
          case 'rampTrough':    svg = renderRampTrough(design);    break;
          case 'rampDAirblade': svg = renderRampDAirblade(design); break;
          default: throw new Error('Unknown render path: ' + entry.render);
        }
      }
    } else {
      throw new Error('Unknown builder: ' + design.builder);
    }

    // Phase 6 — watermark behind all drawing content
    renderWatermark(svg);

    return svg;
  }

  // ─────────────────────────────────────────────────────────────
  // URL serialisation (Phase 3 — colocated with renderer)
  // ─────────────────────────────────────────────────────────────

  var NUMERIC_KEYS = [
    'length', 'width', 'height',
    'face1', 'face2', 'gap', 'count',
    'd', 'e', 'f', 'g', 'h', 'w',
    'basinEdge', 'basinLength', 'taps',
    // tapEdgeInset  — basin edge → first/last tap, along the bench.
    // tapDistFromBasin — basin back edge → tap centreline, into the E strip.
    //   Absent means "auto-centred" (E / 2); see tapLineDist() / tapRowDist().
    'tapEdgeInset', 'tapDistFromBasin',
    // Waste / backplate placement — wastePosition + backplateSide stay strings
    // ('left'/'right'); the per-element override lists (backplateOffsets /
    // wasteOffsets) are comma-separated strings parsed manually. Only the scalar
    // magnitudes are numeric.
    'wasteOffset', 'wasteGap', 'backplateOffset',
    // Soap dispenser params — soapDispenser stays string ('1'/'0') and
    // soapRatio stays string ('1-1' / '1-2' / '2-3'); only the geometry
    // fields are numeric.
    'soapDiameter', 'soapDistFromTap', 'soapDistFromBasin',
    // Dryer hole params — dryerHole stays string ('1'/'0') and dryerRatio stays
    // string; only the geometry fields are numeric. dryerBackplateSide stays a
    // string ('left'/'right') and dryerBackplateOffsets is a comma-separated
    // override list parsed manually via parseOffsetList.
    'dryerDiameter', 'dryerDistFromTap', 'dryerDistFromBasin', 'dryerBackplateOffset'
  ];

  function buildSpecSheetUrl(design, baseUrl) {
    var base = baseUrl || '/pages/spec-sheet';
    var params = new URLSearchParams();
    Object.keys(design).forEach(function (k) {
      var v = design[k];
      if (v === null || v === undefined || v === '') return;
      params.set(k, String(v));
    });
    return base + '?' + params.toString();
  }

  function parseDesignFromUrl(input) {
    var search;
    if (!input || input === window) {
      search = window.location.search;
    } else if (typeof input === 'string') {
      var q = input.indexOf('?');
      search = q >= 0 ? input.substring(q) : input;
    } else {
      search = String(input);
    }
    var params = new URLSearchParams(search);
    var design = {};
    params.forEach(function (value, key) {
      if (NUMERIC_KEYS.indexOf(key) >= 0) {
        var n = parseFloat(value);
        design[key] = isNaN(n) ? value : n;
      } else {
        design[key] = value;
      }
    });
    return design;
  }

  // ─────────────────────────────────────────────────────────────
  // Staff preview override
  //
  // Lets staff make the "View Spec Sheet" button appear on any storefront
  // page without logging in as a tagged customer. Visit any page once
  // with ?previewSpecBtn=1 — the flag is persisted in localStorage so the
  // button stays on for that browser until the staff member visits with
  // ?previewSpecBtn=0 to turn it off.
  //
  // Consumed by the three builders (which JS-inject the button when this
  // is true but the Liquid gate didn't render it) and by page.quote.liquid
  // (which ORs it into window.IS_STAFF so the per-design link renders).
  // ─────────────────────────────────────────────────────────────
  function isStaffPreview() {
    try {
      var params = new URLSearchParams(window.location.search);
      var p = params.get('previewSpecBtn');
      if (p === '1') { localStorage.setItem('specBtnPreview', '1'); return true; }
      if (p === '0') { localStorage.removeItem('specBtnPreview'); return false; }
      return localStorage.getItem('specBtnPreview') === '1';
    } catch (e) { return false; }
  }

  // ─────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────

  window.SpecSheet = {
    render: function (design, container) {
      if (!design.id)   design.id   = designId(design);
      if (!design.date) design.date = todayString();

      var svg = renderSpecSheet(design);
      if (container) {
        container.innerHTML = '';
        var wrap = document.createElement('div');
        wrap.className = 'spec-sheet-page-1';
        wrap.appendChild(svg);
        container.appendChild(wrap);
        renderInstallGuidePage(container);
      }
      return svg;
    },
    buildUrl: buildSpecSheetUrl,
    parseUrl: parseDesignFromUrl,
    designId: designId,
    estimateNetWeight: estimateNetWeight,
    isStaffPreview: isStaffPreview,
    FAMILY_CONSTANTS: FAMILY_CONSTANTS,
    MODEL_REGISTRY: MODEL_REGISTRY,
    MODEL_LABELS: MODEL_LABELS
  };
})();